/**
 * Lichte fetch-wrapper. Vite dev-server proxyt /api → :8000.
 * In productie serveert nginx /api → backend container.
 */

const TOKEN_KEY = "merkatura.access_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown, message: string) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

export async function api<T = unknown>(
  path: string,
  init: Omit<RequestInit, "body"> & { body?: BodyInit | object } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  let body = init.body as BodyInit | undefined;
  if (body && typeof body === "object" && !(body instanceof FormData) && !(body instanceof URLSearchParams)) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(body);
  }

  const res = await fetch(path, { ...init, headers, body });

  if (res.status === 401) {
    setToken(null);
    if (!path.endsWith("/auth/login")) {
      window.location.href = "/login";
    }
  }

  // 204 No Content heeft per HTTP-spec geen body — niet proberen te parsen
  if (res.status === 204) {
    return undefined as T;
  }
  const ct = res.headers.get("content-type") || "";
  const payload = ct.includes("application/json") ? await res.json() : await res.text();

  if (!res.ok) {
    // Node backend gebruikt `error` (Express); oude FastAPI gebruikte `detail`.
    // Pak de eerste die een string is, anders statusText als fallback.
    const obj = typeof payload === "object" && payload !== null ? (payload as Record<string, unknown>) : null;
    const msg =
      (obj && typeof obj.error === "string" && obj.error) ||
      (obj && typeof obj.detail === "string" && obj.detail) ||
      res.statusText ||
      `HTTP ${res.status}`;
    throw new ApiError(res.status, payload, msg);
  }
  return payload as T;
}

/**
 * Download een CSV-stream als blob met token-auth. Vertaalt veelvoorkomende
 * statusses naar NL-meldingen i.p.v. "HTTP 401".
 */
export async function downloadCsvBlob(url: string, filename: string): Promise<void> {
  const token = getToken();
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    let msg = res.statusText || `HTTP ${res.status}`;
    if (res.status === 401) msg = "Sessie verlopen — log opnieuw in.";
    else if (res.status === 403) msg = "Geen rechten voor deze export.";
    else {
      try {
        const txt = await res.text();
        try {
          const obj = JSON.parse(txt);
          if (typeof obj?.error === "string") msg = obj.error;
          else if (typeof obj?.detail === "string") msg = obj.detail;
        } catch {
          if (txt) msg = txt;
        }
      } catch {
        // negeer
      }
    }
    throw new ApiError(res.status, null, msg);
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}

export interface GranularPerm {
  view: boolean;
  edit: boolean;
  amounts: boolean;
}

export interface AuthUser {
  id: number;
  email: string;
  display_name: string | null;
  role: string;
  is_owner?: boolean;
  permissions: string[];
  granular?: Record<string, GranularPerm>;
}

export async function login(email: string, password: string) {
  return api<{
    access_token: string;
    token_type: string;
    user: AuthUser;
  }>("/api/auth/login", { method: "POST", body: { email, password } });
}

export async function fetchMe() {
  return api<AuthUser>("/api/auth/me");
}

// ---------------------------------------------------------------------------
// Warehouse (Fase 4.2)
// ---------------------------------------------------------------------------

export type MutationType = "correction" | "damage" | "count" | "transfer";

export interface StockRow {
  sku: string;
  product_name: string | null;
  qty_vvb: number;
  qty_mda: number;
  qty_onderweg: number;
  qty_initial_totaal: number;
  qty_inkomend: number;
  sold_since_snapshot: number;
  qty_actueel: number;
  snapshot_date: string | null;
  landed_cost_eur: string | null;
  waarde_actueel: string | null;
}

export interface StockMutation {
  id: number;
  mutation_type: MutationType;
  qty_delta: number;
  warehouse: string | null;
  reason: string | null;
  user_id: number | null;
  user_name: string | null;
  auto_task_id: number | null;
  created_at: string;
}

export interface IncomingRow {
  id: number;
  sku: string;
  product_name: string | null;
  qty: number;
  received_qty: number | null;
  lever_datum: string | null;
  trailer_id: string | null;
  trailer_label: string | null;
  trailer_status: string | null;
  source: string;
  received_at: string | null;
  received_by_id: number | null;
  received_by_name: string | null;
  created_at: string | null;
}

export interface StockDetail extends Omit<StockRow, "landed_cost_eur" | "waarde_actueel"> {
  mutations: StockMutation[];
  incoming: IncomingRow[];
}

export interface MutationLogEntry extends StockMutation {
  sku: string;
  product_name: string | null;
}

export async function fetchStock(q?: string, onlyStock = false) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (onlyStock) params.set("only_stock", "true");
  const qs = params.toString();
  return api<StockRow[]>(`/api/warehouse/stock${qs ? "?" + qs : ""}`);
}

export async function fetchStockDetail(sku: string) {
  return api<StockDetail>(`/api/warehouse/stock/${encodeURIComponent(sku)}`);
}

export async function createMutation(body: {
  sku: string;
  mutation_type: MutationType;
  qty_delta: number;
  warehouse?: string | null;
  reason?: string | null;
}) {
  return api<{ id: number; created_at: string; auto_task_id: number | null }>(
    "/api/warehouse/mutations", { method: "POST", body }
  );
}

export async function fetchMutationLog(params: { sku?: string; type?: MutationType; limit?: number } = {}) {
  const qs = new URLSearchParams();
  if (params.sku) qs.set("sku", params.sku);
  if (params.type) qs.set("type", params.type);
  if (params.limit) qs.set("limit", String(params.limit));
  const s = qs.toString();
  return api<MutationLogEntry[]>(`/api/warehouse/mutations${s ? "?" + s : ""}`);
}

export async function fetchIncoming(onlyPending = true) {
  return api<IncomingRow[]>(`/api/warehouse/incoming?only_pending=${onlyPending}`);
}

export async function receiveIncoming(id: number, received_qty: number, variance_reason?: string) {
  return api<{ ok: boolean; variance: number }>(
    `/api/warehouse/incoming/${id}/receive`,
    { method: "POST", body: { received_qty, variance_reason: variance_reason ?? null } }
  );
}

// ---------------------------------------------------------------------------
// Orders (Fase 4.1)
// ---------------------------------------------------------------------------

export interface OrderListItem {
  id: number;
  external_id: string | null;
  sales_channel_name: string | null;
  status: string;
  workflow_state: string | null;
  customer_country: string | null;
  customer_email_hash: string | null;
  order_date: string | null;
  shipping_paid: string | null;
  item_count: number;
  total_amount: string;
  total_margin: string;
  has_returns: boolean;
  has_notes?: boolean;
  first_item_sku: string | null;
  first_item_title: string | null;
  first_item_qty: number | null;
  tracking_number: string | null;
  tracking_url: string | null;
  carrier: string | null;
}

export interface OrderItem {
  id: number;
  product_sku: string | null;
  product_title: string | null;
  quantity_ordered: number;
  unit_price: string | null;
  revenue_incl_vat: string | null;
  vat_pct: number | null;
  landed_cost: string | null;
  landed_cost_source: string | null;
  commission_used: string | null;
  shipping_total: string | null;
  payment_fee: string | null;
  expected_return_loss: string | null;
  gross_margin: string | null;
  /** Warehouse-bucket waarop landed_cost is bepaald (sinds migratie 0171).
   *  DEN_BOSCH/COS/NOD = klopt qua auto/handmatig. FBA/LVB/UNKNOWN = fallback.
   *  Null = nog niet ge-resolved. */
  warehouse_bucket: WarehouseBucket | "FBA" | "LVB" | "UNKNOWN" | null;
  warehouse_bucket_source: string | null;
  /** Serienummer(s) van de gepickte units voor deze regel — leeg voor
   *  non-MDA items of orders van vóór de picklijst-label-flow.
   *  Bij qty>1 in MDA-flow zijn er meerdere SN's (1 per unit). */
  serial_numbers: string[];
}

export interface OrderNote {
  id: number;
  note: string;
  user_id: number | null;
  user_name: string | null;
  created_at: string;
}

export interface OrderStatusLogEntry {
  id: number;
  workflow_state: string;
  note: string | null;
  user_id: number | null;
  user_name: string | null;
  created_at: string;
}

export interface OrderDetail {
  id: number;
  external_id: string | null;
  sales_channel_name: string | null;
  status: string;
  workflow_state: string | null;
  customer_country: string | null;
  order_date: string | null;
  shipping_paid: string | null;
  raw: Record<string, unknown>;
  items: OrderItem[];
  totals: Record<string, string>;
  notes: OrderNote[];
  status_log: OrderStatusLogEntry[];
}

export type OrderBucket = "te-verzenden" | "verzonden" | "geleverd" | "returns" | "cancelled";

export interface OrdersFilter {
  status?: string;
  workflow_state?: string;
  kanaal?: string;
  date_from?: string;
  date_to?: string;
  q?: string;
  limit?: number;
  /** Inbox-mode: alleen orders die nog aandacht nodig hebben (status != CANCELED,
   *  workflow_state != closed/refunded). Backend-side filter. */
  only_open?: boolean;
  /** Status-tab bucket (server-side gefilterd). */
  bucket?: OrderBucket;
}

export async function fetchOrders(filter: OrdersFilter = {}) {
  const params = new URLSearchParams();
  if (filter.status) params.set("status", filter.status);
  if (filter.workflow_state) params.set("workflow_state", filter.workflow_state);
  if (filter.kanaal) params.set("kanaal", filter.kanaal);
  if (filter.date_from) params.set("date_from", filter.date_from);
  if (filter.date_to) params.set("date_to", filter.date_to);
  if (filter.q) params.set("q", filter.q);
  if (filter.limit) params.set("limit", String(filter.limit));
  if (filter.only_open) params.set("only_open", "true");
  if (filter.bucket) params.set("bucket", filter.bucket);
  const qs = params.toString();
  return api<OrderListItem[]>(`/api/orders${qs ? "?" + qs : ""}`);
}

export async function bulkUpdateOrdersWorkflow(order_ids: number[], workflow_state: string, note?: string) {
  return api<{ ok: true; updated: number; workflow_state: string }>(
    "/api/orders/bulk-workflow",
    { method: "POST", body: { order_ids, workflow_state, note } },
  );
}

export async function bulkAddOrderNote(order_ids: number[], note: string) {
  return api<{ ok: true; inserted: number }>(
    "/api/orders/bulk-note",
    { method: "POST", body: { order_ids, note } },
  );
}

export async function bulkExportOrdersCsv(order_ids: number[]): Promise<Blob> {
  const token = getToken();
  const res = await fetch("/api/orders/bulk-export.csv", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ order_ids }),
  });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  return res.blob();
}

export async function fetchOrder(id: number) {
  return api<OrderDetail>(`/api/orders/${id}`);
}

export async function updateOrderWorkflow(id: number, workflow_state: string, note?: string) {
  return api<{ ok: boolean; workflow_state: string }>(`/api/orders/${id}`, {
    method: "PUT", body: { workflow_state, ...(note ? { note } : {}) },
  });
}

export async function addOrderNote(id: number, note: string) {
  return api<OrderNote>(`/api/orders/${id}/notes`, { method: "POST", body: { note } });
}

export async function fetchOrderWorkflowStates() {
  return api<Array<{ workflow_state: string; n: number }>>(`/api/orders/distinct/workflow-states`);
}

// ---------------------------------------------------------------------------
// Taken (Fase 3)
// ---------------------------------------------------------------------------

export type TaskType = "restitution" | "stock_check" | "customer_callback"
  | "supplier_action" | "content_change" | "reconciliation" | "general";
export type TaskParentType = "order" | "sku" | "supplier";
export type TaskStatus = "open" | "done";

export interface TaskRow {
  id: number;
  task_type: TaskType;
  title: string;
  parent_type: TaskParentType | null;
  parent_id: string | null;
  assignee_id: number | null;
  assignee_name: string | null;
  creator_id: number;
  creator_name: string | null;
  status: TaskStatus;
  fields: Record<string, unknown>;
  created_at: string;
  completed_at: string | null;
  completed_by_id: number | null;
  completed_by_name: string | null;
}

export interface TaskNote {
  id: number;
  note: string;
  user_id: number | null;
  user_name: string | null;
  created_at: string;
}

export interface TaskDetail extends TaskRow {
  notes: TaskNote[];
}

export interface TaskFilter {
  status?: TaskStatus | "";
  type?: TaskType | "";
  parent_type?: TaskParentType | "";
  assignee_id?: number;
  q?: string;
}

export async function fetchTasks(filter: TaskFilter = {}) {
  const params = new URLSearchParams();
  if (filter.status) params.set("status", filter.status);
  if (filter.type) params.set("type", filter.type);
  if (filter.parent_type) params.set("parent_type", filter.parent_type);
  if (filter.assignee_id) params.set("assignee_id", String(filter.assignee_id));
  if (filter.q) params.set("q", filter.q);
  const qs = params.toString();
  return api<TaskRow[]>(`/api/tasks${qs ? "?" + qs : ""}`);
}

export async function fetchTask(id: number) {
  return api<TaskDetail>(`/api/tasks/${id}`);
}

export interface TaskSummary { open_assigned: number; open_created: number }

export async function fetchTaskSummary() {
  return api<TaskSummary>("/api/tasks/my-summary");
}

export async function createTask(body: {
  task_type: TaskType;
  title: string;
  parent_type?: TaskParentType | null;
  parent_id?: string | null;
  assignee_id?: number | null;
  fields?: Record<string, unknown>;
  on_behalf_of_id?: number | null;
}) {
  return api<{ id: number }>("/api/tasks", { method: "POST", body });
}

export async function updateTask(id: number, body: Partial<{
  title: string;
  assignee_id: number | null;
  fields: Record<string, unknown>;
}>) {
  return api<{ ok: boolean }>(`/api/tasks/${id}`, { method: "PUT", body });
}

export async function completeTask(id: number, reopen = false) {
  return api<{ ok: boolean; status: TaskStatus }>(`/api/tasks/${id}/complete`, {
    method: "POST", body: { reopen },
  });
}

export async function deleteTask(id: number) {
  return api<{ ok: boolean }>(`/api/tasks/${id}`, { method: "DELETE" });
}

export async function addTaskNote(id: number, note: string) {
  return api<TaskNote>(`/api/tasks/${id}/notes`, { method: "POST", body: { note } });
}

// ---------------------------------------------------------------------------
// Dashboard layout (widget personalisatie — Fase 2)
// ---------------------------------------------------------------------------

export interface LayoutItem {
  widget_id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  config?: Record<string, unknown> | null;
}

export interface DashboardLayoutResponse {
  user_id: number;
  items: LayoutItem[];
}

export async function fetchDashboardLayout() {
  return api<DashboardLayoutResponse>("/api/dashboard/layout");
}

export async function saveDashboardLayout(items: LayoutItem[]) {
  return api<{ ok: boolean; count: number }>("/api/dashboard/layout", {
    method: "PUT",
    body: { items },
  });
}

// ---------------------------------------------------------------------------
// Project voortgang
// ---------------------------------------------------------------------------

export type ProgressStatus = "pending" | "in_progress" | "done" | "paused" | "cancelled";

export interface ProgressItem {
  item_key: string;
  phase_key: string;
  phase_label: string;
  section_key: string | null;
  section_label: string | null;
  item_label: string;
  status: ProgressStatus;
  sort_order: number;
  notes: string | null;
  updated_at: string;
  updated_by_id: number | null;
  updated_by_name: string | null;
}

export interface ProgressSection {
  key: string;
  label: string | null;
  items: ProgressItem[];
}

export interface ProgressPhase {
  key: string;
  label: string;
  total: number;
  done: number;
  in_progress: number;
  sections: ProgressSection[];
}

export interface ProgressResponse {
  overall: { total: number; done: number; in_progress: number };
  phases: ProgressPhase[];
}

export interface ProgressLogEntry {
  id: number;
  item_key: string;
  item_label: string | null;
  old_status: ProgressStatus | null;
  new_status: ProgressStatus;
  note: string | null;
  changed_by_name: string | null;
  changed_at: string;
}

export async function fetchProgress() {
  return api<ProgressResponse>("/api/progress");
}

export async function updateProgressItem(
  itemKey: string,
  status: ProgressStatus,
  note?: string,
) {
  return api<{ ok: boolean; old_status?: ProgressStatus; new_status?: ProgressStatus }>(
    `/api/progress/${encodeURIComponent(itemKey)}`,
    { method: "PUT", body: { status, ...(note ? { note } : {}) } },
  );
}

export async function fetchProgressLog(limit = 50) {
  return api<{ entries: ProgressLogEntry[] }>(`/api/progress/log?limit=${limit}`);
}

export interface HeatmapCell {
  weekday: number;  // 1=ma .. 7=zo
  hour: number;     // 0-23
  omzet: number;
  orders: number;
}

export interface HeatmapResponse {
  periode_start: string;
  periode_end: string;
  kanaal: string | null;
  cells: HeatmapCell[];
}

export async function fetchSalesHeatmap(start: string, end: string, kanaal?: string) {
  const q = new URLSearchParams({ periode_start: start, periode_end: end });
  if (kanaal) q.set("kanaal", kanaal);
  return api<HeatmapResponse>(`/api/dashboard/sales-heatmap?${q}`);
}

export interface KpiResponse {
  periode_start: string;
  periode_end: string;
  kanaal: string | null;
  bruto_omzet: string;
  netto_omzet: string;
  aantal_orders: number;
  marge_eur: string;
  marge_pct: number;
  netto_resultaat: string;
  comparison?: {
    periode_start: string;
    periode_end: string;
    bruto_omzet: string;
    netto_omzet: string;
    aantal_orders: number;
    marge_eur: string;
    marge_pct: number;
    netto_resultaat: string;
    delta_pct: {
      bruto_omzet: number | null;
      netto_omzet: number | null;
      aantal_orders: number | null;
      marge_eur: number | null;
      marge_pct: number | null;
      netto_resultaat: number | null;
    };
  };
  sparkline?: Array<{
    day: string;
    omzet: number;
    orders: number;
    marge_eur: number;
  }>;
}

export interface ChannelRow {
  kanaal: string;
  omzet: string;
  orders: number;
  marge_eur: string;
  marge_pct: number;
}

export async function fetchKpi(
  start: string,
  end: string,
  kanaal?: string,
  opts?: { comparePrevious?: boolean; sparkline?: boolean; orderType?: "b2c" | "b2b" | "all" },
) {
  const q = new URLSearchParams({ periode_start: start, periode_end: end });
  if (kanaal) q.set("kanaal", kanaal);
  if (opts?.comparePrevious) q.set("compare_previous", "true");
  if (opts?.sparkline) q.set("sparkline", "true");
  if (opts?.orderType && opts.orderType !== "all") q.set("order_type", opts.orderType);
  return api<KpiResponse>(`/api/dashboard/kpi?${q}`);
}

export async function fetchPerChannel(start: string, end: string) {
  const q = new URLSearchParams({ periode_start: start, periode_end: end });
  return api<ChannelRow[]>(`/api/dashboard/per-channel?${q}`);
}

export interface ChannelDetailRow {
  kanaal: string | null;
  orders: number;
  units: number;
  bruto_omzet: string;
  netto: string;
  cogs: string;
  commissie: string;
  shipping: string;
  payment_fee: string;
  retour: string;
  brutomarge: string;
  brutomarge_pct: number | null;
  marketing: string;
  netto_resultaat_excl_overhead: string;
  poas: number | null;
}

export interface B2bOrderInput {
  sku: string;
  quantity: number;
  exw_price_per_unit: number;
  transport_cost: number;
  company_name: string;
  country: string;
  vat_number?: string | null;
  is_intracommunautair: boolean;
  notes?: string | null;
}

export interface B2bOrderResult {
  ok: true;
  order_id: number;
  store_id: string;
  vat_rate_pct: number;
  totals: {
    price_incl_vat: string;
    shipping_incl_vat: string;
    total_incl_vat: string;
  };
}

export async function createB2bOrder(body: B2bOrderInput) {
  return api<B2bOrderResult>("/api/orders/b2b", { method: "POST", body });
}

export async function fetchPerChannelDetail(start: string, end: string, orderType?: "b2c" | "b2b" | "all") {
  const q = new URLSearchParams({ periode_start: start, periode_end: end });
  if (orderType && orderType !== "all") q.set("order_type", orderType);
  return api<{ periode: { start: string; end: string }; kanalen: ChannelDetailRow[] }>(
    `/api/dashboard/per-channel-detail?${q}`,
  );
}

export interface PnlLineItem {
  label: string;
  amount: string;
  pct_of_revenue: number | null;
  kind: "revenue" | "cost" | "subtotal" | "result";
}

export interface PnlBreakdown {
  bruto_omzet: string;
  items: PnlLineItem[];
}

export async function fetchPnlBreakdown(start: string, end: string, kanaal?: string, orderType?: "b2c" | "b2b" | "all") {
  const q = new URLSearchParams({ periode_start: start, periode_end: end });
  if (kanaal) q.set("kanaal", kanaal);
  if (orderType && orderType !== "all") q.set("order_type", orderType);
  return api<PnlBreakdown>(`/api/dashboard/pnl-breakdown?${q}`);
}

export interface SkuRow {
  sku: string;
  product_name: string | null;
  units: number;
  bruto_omzet: string;
  marge_eur: string;
  marge_pct: number | null;
}

export interface CosSummaryRow {
  sku: string | null;
  product_name: string | null;
  orders_count: number;
  qty_total: number;
  revenue_excl_vat: string;
  landed_cost: string;
  gross_margin: string;
}
export interface CosSummaryTotals {
  orders_count: number;
  skus_count: number;
  qty_total: number;
  revenue_excl_vat: string;
  landed_cost: string;
  gross_margin: string;
  margin_pct: number | null;
}
export interface CosSummaryResponse {
  periode_start: string;
  periode_end: string;
  kanaal: string | null;
  rows: CosSummaryRow[];
  totals: CosSummaryTotals;
}

export async function fetchCosSummary(start: string, end: string, kanaal?: string) {
  const q = new URLSearchParams({ periode_start: start, periode_end: end });
  if (kanaal) q.set("kanaal", kanaal);
  return api<CosSummaryResponse>(`/api/dashboard/cos-summary?${q}`);
}

export async function fetchTopSkus(
  start: string,
  end: string,
  metric: "revenue" | "margin",
  limit = 20,
  kanaal?: string,
) {
  const q = new URLSearchParams({
    periode_start: start,
    periode_end: end,
    metric,
    limit: String(limit),
  });
  if (kanaal) q.set("kanaal", kanaal);
  return api<SkuRow[]>(`/api/dashboard/top-skus?${q}`);
}

export async function fetchHiddenGems(
  start: string,
  end: string,
  type: "winners" | "losers",
  limit = 20,
  kanaal?: string,
) {
  const q = new URLSearchParams({
    periode_start: start,
    periode_end: end,
    type,
    limit: String(limit),
  });
  if (kanaal) q.set("kanaal", kanaal);
  return api<SkuRow[]>(`/api/dashboard/hidden-gems?${q}`);
}

export interface HourBucket {
  hour: number;
  omzet: string;
  orders: number;
}

export interface WeekdayBucket {
  weekday: number;
  label: string;
  omzet: string;
  orders: number;
}

export interface MonthBucket {
  month: string;
  label: string;
  omzet: string;
  orders: number;
  marge_eur: string;
  growth_pct: number | null;
}

export async function fetchSalesByHour(start: string, end: string, kanaal?: string) {
  const q = new URLSearchParams({ periode_start: start, periode_end: end });
  if (kanaal) q.set("kanaal", kanaal);
  return api<HourBucket[]>(`/api/dashboard/sales-by-hour?${q}`);
}

export async function fetchSalesByWeekday(start: string, end: string, kanaal?: string) {
  const q = new URLSearchParams({ periode_start: start, periode_end: end });
  if (kanaal) q.set("kanaal", kanaal);
  return api<WeekdayBucket[]>(`/api/dashboard/sales-by-weekday?${q}`);
}

export async function fetchGrowthByMonth(months = 12, kanaal?: string) {
  const q = new URLSearchParams({ months: String(months) });
  if (kanaal) q.set("kanaal", kanaal);
  return api<MonthBucket[]>(`/api/dashboard/growth-by-month?${q}`);
}

// ===========================================================================
// Inkoop — Parameters
// ===========================================================================

export interface ParameterRow {
  key: string;
  value_num: string | null;
  value_text: string | null;
  label: string;
  unit: string | null;
  notes: string | null;
  sort_order: number;
  updated_at: string;
}

export async function fetchParameters() {
  return api<ParameterRow[]>("/api/inkoop/parameters");
}

export async function updateParameter(
  key: string,
  body: { value_num?: number | null; value_text?: string | null; notes?: string | null },
) {
  return api<ParameterRow>(`/api/inkoop/parameters/${encodeURIComponent(key)}`, {
    method: "PUT",
    body,
  });
}

// ===========================================================================
// Inkoop — Trailers
// ===========================================================================

export type TrailerStatus =
  | "planning"
  | "besteld"
  | "onderweg"
  | "geleverd"
  | "geannuleerd";

export interface TrailerRow {
  id: number;
  label: string;
  budget_inkoop: string | null;
  eta_datum: string | null;
  status: TrailerStatus;
  backorder_eur: string | null;
  backorder_m3: string | null;
  notes: string | null;
  sort_order: number;
}

export interface TrailerCreate {
  label: string;
  budget_inkoop?: number | null;
  eta_datum?: string | null;
  status?: TrailerStatus;
  backorder_eur?: number | null;
  backorder_m3?: number | null;
  notes?: string | null;
  sort_order?: number;
}

export async function fetchTrailers() {
  return api<TrailerRow[]>("/api/inkoop/trailers");
}

export async function createTrailer(body: TrailerCreate) {
  return api<TrailerRow>("/api/inkoop/trailers", { method: "POST", body });
}

export async function updateTrailer(id: number, body: Partial<TrailerCreate>) {
  return api<TrailerRow>(`/api/inkoop/trailers/${id}`, { method: "PUT", body });
}

export async function deleteTrailer(id: number) {
  return api<void>(`/api/inkoop/trailers/${id}`, { method: "DELETE" });
}

// ===========================================================================
// Inkoop — Landed cost
// ===========================================================================

export type WarehouseBucket = "DEN_BOSCH" | "COS" | "NOD";

export interface LandedCostRow {
  sku: string;
  product_name: string | null;
  warehouse_bucket: WarehouseBucket;
  valid_from: string;
  valid_to: string | null;
  purchase_eur: string;
  shipping_eur: string | null;
  landed_cost_eur: string;
  is_current: boolean;
}

export async function fetchLandedCost(
  opts: { q?: string; onlyCurrent?: boolean; bucket?: WarehouseBucket } = {},
) {
  const q = new URLSearchParams();
  if (opts.q) q.set("q", opts.q);
  if (opts.onlyCurrent === false) q.set("only_current", "false");
  if (opts.bucket) q.set("bucket", opts.bucket);
  return api<LandedCostRow[]>(`/api/inkoop/landed-cost?${q}`);
}

export async function fetchLandedCostHistory(sku: string, bucket: WarehouseBucket = "DEN_BOSCH") {
  const q = new URLSearchParams({ bucket });
  return api<LandedCostRow[]>(
    `/api/inkoop/landed-cost/${encodeURIComponent(sku)}/history?${q}`,
  );
}

export async function createLandedCost(body: {
  sku: string;
  warehouse_bucket?: WarehouseBucket;
  valid_from: string;
  purchase_eur: number;
  shipping_eur?: number;
}) {
  return api<LandedCostRow>("/api/inkoop/landed-cost", { method: "POST", body });
}

export async function updateLandedCost(
  sku: string,
  valid_from: string,
  body: { purchase_eur?: number; shipping_eur?: number; bucket?: WarehouseBucket },
) {
  return api<LandedCostRow>(
    `/api/inkoop/landed-cost/${encodeURIComponent(sku)}/${valid_from}`,
    { method: "PUT", body },
  );
}

export async function deleteLandedCost(
  sku: string,
  valid_from: string,
  bucket: WarehouseBucket = "DEN_BOSCH",
) {
  const q = new URLSearchParams({ bucket });
  return api<void>(
    `/api/inkoop/landed-cost/${encodeURIComponent(sku)}/${valid_from}?${q}`,
    { method: "DELETE" },
  );
}

export interface LandedCostBulkRow {
  sku: string;
  warehouse_bucket?: WarehouseBucket;
  valid_from: string;
  purchase_eur: number | string;
  shipping_eur?: number | string | null;
}

export async function bulkLandedCost(
  rows: LandedCostBulkRow[],
  bucket: WarehouseBucket = "DEN_BOSCH",
) {
  return api<{ upserted: number }>("/api/inkoop/landed-cost/bulk", {
    method: "POST",
    body: { rows, bucket },
  });
}

// Per-order-item warehouse-bucket override (COS/DB/NOD flip)
export interface WarehouseBucketPatchResult {
  ok: boolean;
  order_item_id?: number;
  order_id?: number;
  items_updated?: number;
  warehouse_bucket: WarehouseBucket | null;
  reason: string | null;
  re_resolved: { bucket: number; landed_cost: number };
}

export async function patchOrderItemWarehouseBucket(
  orderItemId: number,
  bucket: WarehouseBucket | null,
  reason?: string,
) {
  return api<WarehouseBucketPatchResult>(
    `/api/overrides/${orderItemId}/warehouse-bucket`,
    { method: "PATCH", body: { warehouse_bucket: bucket, reason: reason ?? null } },
  );
}

export async function patchOrderWarehouseBucket(
  orderId: number,
  bucket: WarehouseBucket | null,
  reason?: string,
) {
  return api<WarehouseBucketPatchResult>(
    `/api/overrides/order/${orderId}/warehouse-bucket`,
    { method: "PATCH", body: { warehouse_bucket: bucket, reason: reason ?? null } },
  );
}

// ===========================================================================
// Inkoop — Voorraad
// ===========================================================================

export interface VoorraadRow {
  sku: string;
  product_name: string | null;
  qty_actueel: number;
  qty_inkomend: number;
  qty_totaal: number;
  landed_cost_eur: string | null;
  waarde_actueel: string | null;
}

export async function fetchVoorraad(opts: { q?: string; onlyWithStock?: boolean } = {}) {
  const q = new URLSearchParams();
  if (opts.q) q.set("q", opts.q);
  if (opts.onlyWithStock) q.set("only_with_stock", "true");
  return api<VoorraadRow[]>(`/api/inkoop/voorraad?${q}`);
}

// ===========================================================================
// Inkoop — Velocity overrides
// ===========================================================================

export interface VelocityOverrideRow {
  sku: string;
  velocity_per_day: string;
  reason: string | null;
  updated_at: string;
}

export async function fetchVelocityOverrides() {
  return api<VelocityOverrideRow[]>("/api/inkoop/velocity-overrides");
}

export async function upsertVelocityOverride(
  sku: string,
  body: { velocity_per_day: number; reason?: string | null },
) {
  return api<VelocityOverrideRow>(
    `/api/inkoop/velocity-overrides/${encodeURIComponent(sku)}`,
    { method: "PUT", body },
  );
}

export async function deleteVelocityOverride(sku: string) {
  return api<void>(`/api/inkoop/velocity-overrides/${encodeURIComponent(sku)}`, {
    method: "DELETE",
  });
}

// ===========================================================================
// Inkoop — Plan data (velocity + marge + voorraad-dagen per SKU)
// ===========================================================================

export interface PlanDataRow {
  sku: string;
  product_name: string | null;
  velocity_per_day: string;
  velocity_source: "override" | "weighted" | "no_sales";
  qty_actueel: number;
  qty_inkomend: number;
  voorraad_dagen: string | null;
  landed_cost_eur: string | null;
  avg_sale_price_eur: string | null;
  margin_eur: string | null;
  margin_pct: string | null;
  m3_per_stuk: string | null;
}

export async function fetchPlanData(opts: { q?: string; limit?: number } = {}) {
  const q = new URLSearchParams();
  if (opts.q) q.set("q", opts.q);
  if (opts.limit) q.set("limit", String(opts.limit));
  return api<PlanDataRow[]>(`/api/inkoop/plan-data?${q}`);
}

// ===========================================================================
// Inkoop — Plan (trailers met plan_items)
// ===========================================================================

export interface PlanItemRow {
  id: number;
  trailer_id: number;
  sku: string;
  product_name: string | null;
  qty: number;
  unit_price_eur: string | null;
  sort_order: number;
  notes: string | null;
  line_total_eur: string | null;
}

export interface TrailerWithItemsRow {
  id: number;
  label: string;
  budget_inkoop: string | null;
  eta_datum: string | null;
  status: TrailerStatus;
  backorder_eur: string | null;
  backorder_m3: string | null;
  notes: string | null;
  sort_order: number;
  items: PlanItemRow[];
  totaal_eur: string;
  totaal_qty: number;
  aantal_skus: number;
}

export async function fetchPlan() {
  return api<TrailerWithItemsRow[]>("/api/inkoop/plan");
}

export async function addPlanItem(
  trailerId: number,
  body: { sku: string; qty?: number; unit_price_eur?: number | null; sort_order?: number; notes?: string | null },
) {
  return api<PlanItemRow>(`/api/inkoop/trailers/${trailerId}/items`, {
    method: "POST",
    body,
  });
}

export async function updatePlanItem(
  trailerId: number,
  itemId: number,
  body: { qty?: number; unit_price_eur?: number | null; sort_order?: number; notes?: string | null },
) {
  return api<PlanItemRow>(`/api/inkoop/trailers/${trailerId}/items/${itemId}`, {
    method: "PUT",
    body,
  });
}

export async function deletePlanItem(trailerId: number, itemId: number) {
  return api<void>(`/api/inkoop/trailers/${trailerId}/items/${itemId}`, {
    method: "DELETE",
  });
}

// ===========================================================================
// Inkoop — Cashflow
// ===========================================================================

export interface CashflowWeek {
  week_start: string;
  week_label: string;
  is_forecast: boolean;
  inkomsten_eur: string;
  uitgaven_trailers_eur: string;
  uitgaven_aflossing_eur: string;
  netto_eur: string;
  saldo_eind_eur: string;
}

export interface CashflowResponse {
  opening_cash: string;
  min_cash_buffer: string;
  aflossing_per_maand: string;
  weeks: CashflowWeek[];
  laagste_saldo: string;
  laagste_saldo_week: string;
  eind_saldo: string;
  buffer_gehaald: boolean;
}

export async function fetchCashflow(weeks: number = 17) {
  return api<CashflowResponse>(`/api/inkoop/cashflow?weeks=${weeks}`);
}

// ===========================================================================
// Inkoop — CSV export
// ===========================================================================

export function trailerCsvUrl(trailerId: number): string {
  return `/api/inkoop/trailers/${trailerId}/csv`;
}

export async function downloadTrailerCsv(trailerId: number, label: string) {
  const token = getToken();
  const res = await fetch(trailerCsvUrl(trailerId), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new ApiError(res.status, await res.text(), res.statusText);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `trailer_${label.replace(/\s+/g, "_")}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ===========================================================================
// Order overrides
// ===========================================================================

export interface OverrideRow {
  order_item_id: number;
  order_store_id: string | null;
  order_date: string;
  sku: string | null;
  product_name: string | null;
  sales_channel_name: string | null;
  quantity_ordered: number;
  revenue_incl_vat: string | null;
  cogs: string | null;
  commission_used: string | null;
  shipping_total: string | null;
  payment_fee: string | null;
  expected_return_loss: string | null;
  gross_margin: string | null;
  margin_pct: string | null;
  landed_cost_per_unit_override: string | null;
  commission_override: string | null;
  shipping_override: string | null;
  payment_fee_override: string | null;
  expected_return_loss_override: string | null;
  compensation_eur: string | null;
  discount_eur_incl_vat: string | null;
  carrier_compensation_eur: string | null;
  notes: string | null;
  updated_at: string | null;
  warehouse_bucket: string | null;
  warehouse_bucket_source: string | null;
  is_excluded_from_results: boolean;
  is_exchange?: boolean;
}

export interface OverrideUpsert {
  landed_cost_per_unit_override?: number | null;
  commission_override?: number | null;
  shipping_override?: number | null;
  payment_fee_override?: number | null;
  expected_return_loss_override?: number | null;
  compensation_eur?: number | null;
  discount_eur_incl_vat?: number | null;
  carrier_compensation_eur?: number | null;
  notes?: string | null;
  excluded_from_results?: boolean;
  is_exchange?: boolean;
}

export async function fetchOverrides(opts: {
  periode_start: string;
  periode_end: string;
  kanaal?: string;
  q?: string;
  only_with_override?: boolean;
  bucket?: "check_override" | "zero_costs" | "negative_margin" | "qty_gt_1" | "webshop" | "from_siu";
  limit?: number;
  offset?: number;
}) {
  const q = new URLSearchParams({
    periode_start: opts.periode_start,
    periode_end: opts.periode_end,
  });
  if (opts.kanaal) q.set("kanaal", opts.kanaal);
  if (opts.q) q.set("q", opts.q);
  if (opts.only_with_override) q.set("only_with_override", "true");
  if (opts.bucket) q.set("bucket", opts.bucket);
  if (opts.limit) q.set("limit", String(opts.limit));
  if (opts.offset) q.set("offset", String(opts.offset));
  return api<OverrideRow[]>(`/api/overrides?${q}`);
}

export async function upsertOverride(orderItemId: number, body: OverrideUpsert) {
  return api<OverrideRow>(`/api/overrides/${orderItemId}`, {
    method: "PUT",
    body,
  });
}

// ===========================================================================
// Costs
// ===========================================================================

export interface CostRow {
  id: number;
  month: string;
  category: string;
  sub_category: string;
  amount_eur: string | null;
  notes: string | null;
  updated_at: string;
}

export async function fetchCosts(opts: { year?: number; month?: number } = {}) {
  const q = new URLSearchParams();
  if (opts.year) q.set("year", String(opts.year));
  if (opts.month) q.set("month", String(opts.month));
  return api<CostRow[]>(`/api/costs?${q}`);
}

export async function fetchCostCategories() {
  return api<{ category: string; sub_category: string }[]>("/api/costs/categories");
}

export async function createCost(body: {
  month: string;
  category: string;
  sub_category: string;
  amount_eur?: number | null;
  notes?: string | null;
}) {
  return api<CostRow>("/api/costs", { method: "POST", body });
}

export async function updateCost(
  id: number,
  body: { amount_eur?: number | null; notes?: string | null; category?: string; sub_category?: string },
) {
  return api<CostRow>(`/api/costs/${id}`, { method: "PUT", body });
}

export async function deleteCost(id: number) {
  return api<void>(`/api/costs/${id}`, { method: "DELETE" });
}

// ===========================================================================
// Marketing
// ===========================================================================

export interface MarketingRow {
  id: number;
  period_start: string;
  period_end: string;
  platform: string;
  sales_channel: string;
  amount_eur: string;
  notes: string | null;
  updated_at: string;
}

export async function fetchMarketing(opts: {
  periode_start?: string;
  periode_end?: string;
  platform?: string;
  sales_channel?: string;
} = {}) {
  const q = new URLSearchParams();
  if (opts.periode_start) q.set("periode_start", opts.periode_start);
  if (opts.periode_end) q.set("periode_end", opts.periode_end);
  if (opts.platform) q.set("platform", opts.platform);
  if (opts.sales_channel) q.set("sales_channel", opts.sales_channel);
  return api<MarketingRow[]>(`/api/marketing?${q}`);
}

export async function createMarketing(body: {
  period_start: string;
  period_end: string;
  platform: string;
  sales_channel: string;
  amount_eur: number;
  notes?: string | null;
}) {
  return api<MarketingRow>("/api/marketing", { method: "POST", body });
}

export async function updateMarketing(
  id: number,
  body: Partial<{
    period_start: string;
    period_end: string;
    platform: string;
    sales_channel: string;
    amount_eur: number;
    notes: string | null;
  }>,
) {
  return api<MarketingRow>(`/api/marketing/${id}`, { method: "PUT", body });
}

export async function deleteMarketing(id: number) {
  return api<void>(`/api/marketing/${id}`, { method: "DELETE" });
}

// ===========================================================================
// Returns
// ===========================================================================

export interface ReturnRow {
  source: string;
  kanaal: string;
  datum: string | null;
  ordernummer: string | null;
  sku: string | null;
  titel: string | null;
  aantal: number | null;
  reden: string | null;
  detail: string | null;
  klant_opmerking: string | null;
  fulfilment: string | null;
  handled: boolean;
  handled_at: string | null;
  laatste_status: string | null;
  track_trace: string | null;
  transporter: string | null;
  ontvangen_op: string | null;
  gemapt: boolean;
  uniq_id: string;
  return_id: string | null;
  notes: string | null;
}

export interface ReturnsStats {
  total_returns: number;
  handled: number;
  received: number;
  pending: number;
  by_channel: Record<string, number>;
  return_rate_pct: number | null;
}

export async function fetchReturns(opts: {
  periode_start?: string;
  periode_end?: string;
  kanaal?: string;
  status?: "pending" | "in_transit" | "received" | "handled";
  q?: string;
  limit?: number;
  offset?: number;
} = {}) {
  const q = new URLSearchParams();
  if (opts.periode_start) q.set("periode_start", opts.periode_start);
  if (opts.periode_end) q.set("periode_end", opts.periode_end);
  if (opts.kanaal) q.set("kanaal", opts.kanaal);
  if (opts.status) q.set("status", opts.status);
  if (opts.q) q.set("q", opts.q);
  if (opts.limit) q.set("limit", String(opts.limit));
  if (opts.offset) q.set("offset", String(opts.offset));
  return api<ReturnRow[]>(`/api/returns?${q}`);
}

export async function fetchReturnsStats(opts: {
  periode_start?: string;
  periode_end?: string;
  kanaal?: string;
} = {}) {
  const q = new URLSearchParams();
  if (opts.periode_start) q.set("periode_start", opts.periode_start);
  if (opts.periode_end) q.set("periode_end", opts.periode_end);
  if (opts.kanaal) q.set("kanaal", opts.kanaal);
  return api<ReturnsStats>(`/api/returns/stats?${q}`);
}

export async function lookupReturn(code: string) {
  const q = new URLSearchParams({ code });
  return api<ReturnRow>(`/api/returns/lookup?${q}`);
}

export async function markReturnReceived(source: string, uniq_id: string) {
  return api<ReturnRow>(
    `/api/returns/${encodeURIComponent(source)}/${encodeURIComponent(uniq_id)}/mark-received`,
    { method: "POST" },
  );
}

export async function markReturnHandled(
  source: string,
  uniq_id: string,
  notes?: string | null,
) {
  return api<ReturnRow>(
    `/api/returns/${encodeURIComponent(source)}/${encodeURIComponent(uniq_id)}/mark-handled`,
    { method: "POST", body: { notes: notes ?? null } },
  );
}

// ===========================================================================
// Finance / Cashflow
// ===========================================================================

export interface CashflowCell {
  week_start: string;
  amount: string;
  is_override: boolean;
  locked: boolean;
  notes: string | null;
}

export interface CashflowRow {
  post_key: string;
  label: string;
  group: "inkomsten" | "uitgaven" | "custom" | "subtotal";
  editable: boolean;
  is_subtotal: boolean;
  /** Positie-bepaalde sectie — custom rijen "inkomst" boven Totaal inkomsten,
   *  "uitgaven" tussen de twee subtotalen, "informatief" daarna. Voor
   *  built-ins gelijk aan hun vaste sectie. */
  effective_section: "inkomsten" | "uitgaven" | "informatief";
  /** Globale rij-volgorde — bepaalt rendervolgorde én classificatie. */
  sort_order: number;
  /** Subtotaal-rijen + netto zijn niet sleepbaar. */
  draggable: boolean;
  cells: CashflowCell[];
  total: string;
}

export interface ScenarioRow {
  id: number;
  name: string;
  description: string | null;
  forecast_multiplier: string;
  sort_order: number;
  is_default: boolean;
}

export interface CashflowMatrix {
  weeks_count: number;
  week_starts: string[];
  week_labels: string[];
  opening_cash: string;
  min_cash_buffer: string;
  scenario: ScenarioRow;
  rows: CashflowRow[];
  saldo_per_week: string[];
  laagste_saldo: string;
  laagste_saldo_week: string;
  eind_saldo: string;
  buffer_gehaald: boolean;
}

export async function fetchCashflowMatrix(weeks: number = 17, scenarioId: number = 1) {
  return api<CashflowMatrix>(`/api/finance/cashflow?weeks=${weeks}&scenario_id=${scenarioId}`);
}

export async function fetchScenarios() {
  return api<ScenarioRow[]>("/api/finance/scenarios");
}

export async function setCashflowCell(
  postKey: string,
  weekStart: string,
  amount: number | null,
  opts: { notes?: string | null; locked?: boolean | null; scenarioId?: number } = {},
) {
  const sid = opts.scenarioId ?? 1;
  return api<void>(
    `/api/finance/cell/${encodeURIComponent(postKey)}/${weekStart}?scenario_id=${sid}`,
    {
      method: "PUT",
      body: { amount, notes: opts.notes ?? null, locked: opts.locked ?? null },
    },
  );
}

export async function downloadCashflowCsv(weeks: number, scenarioId: number, scenarioName: string) {
  const token = getToken();
  const res = await fetch(`/api/finance/cashflow.csv?weeks=${weeks}&scenario_id=${scenarioId}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new ApiError(res.status, await res.text(), res.statusText);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `cashflow_${scenarioName.replace(/\s+/g, "_")}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export interface CustomLineRow {
  id: number;
  label: string;
  sort_order: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export async function fetchCustomLines() {
  return api<CustomLineRow[]>("/api/finance/custom-lines");
}

export async function createCustomLine(body: { label: string; sort_order?: number; notes?: string | null }) {
  return api<CustomLineRow>("/api/finance/custom-lines", { method: "POST", body });
}

export async function updateCustomLine(id: number, body: { label?: string; sort_order?: number; notes?: string | null }) {
  return api<CustomLineRow>(`/api/finance/custom-lines/${id}`, { method: "PUT", body });
}

export async function deleteCustomLine(id: number) {
  return api<void>(`/api/finance/custom-lines/${id}`, { method: "DELETE" });
}

/** Verplaats een rij in de cashflow-tabel.
 *  after_post_key = null  → vooraan
 *  after_post_key = "X"   → direct na X */
export async function reorderCashflowRow(postKey: string, afterPostKey: string | null) {
  return api<{ ok: boolean }>("/api/finance/cashflow/reorder", {
    method: "POST",
    body: { post_key: postKey, after_post_key: afterPostKey },
  });
}

// ===========================================================================
// BTW (vat_rates)
// ===========================================================================

export interface VatRateRow {
  id: number;
  country_code: string;
  rate_pct: string;
  valid_from: string;
  valid_to: string | null;
  notes: string | null;
  source: string | null;
  created_at: string;
  updated_at: string;
}

export async function fetchBtw(opts: { country?: string; only_current?: boolean } = {}) {
  const qs = new URLSearchParams();
  if (opts.country) qs.set("country", opts.country);
  if (opts.only_current) qs.set("only_current", "true");
  return api<VatRateRow[]>(`/api/btw${qs.toString() ? `?${qs}` : ""}`);
}

export async function createBtw(body: {
  country_code: string;
  rate_pct: number | string;
  valid_from: string;
  valid_to?: string | null;
  notes?: string | null;
  source?: string | null;
}) {
  return api<VatRateRow>("/api/btw", { method: "POST", body });
}

export async function updateBtw(
  id: number,
  body: Partial<{
    country_code: string;
    rate_pct: number | string;
    valid_from: string;
    valid_to: string | null;
    notes: string | null;
    source: string | null;
  }>,
) {
  return api<VatRateRow>(`/api/btw/${id}`, { method: "PUT", body });
}

export async function deleteBtw(id: number) {
  return api<void>(`/api/btw/${id}`, { method: "DELETE" });
}

// ===========================================================================
// Shipping outbound + defaults
// ===========================================================================

export interface ShippingOutboundRow {
  sku: string;
  method: string;
  country: string;
  amount_eur: string;
  notes: string | null;
  updated_at: string;
}

export interface ShippingUpsertBody {
  sku: string;
  method: string;
  country: string;
  amount_eur: number | string;
  notes?: string | null;
}

export async function fetchShippingOutbound(opts: {
  q?: string;
  country?: string;
  method?: string;
  limit?: number;
} = {}) {
  const qs = new URLSearchParams();
  if (opts.q) qs.set("q", opts.q);
  if (opts.country) qs.set("country", opts.country);
  if (opts.method) qs.set("method", opts.method);
  if (opts.limit) qs.set("limit", String(opts.limit));
  return api<ShippingOutboundRow[]>(
    `/api/shipping/outbound${qs.toString() ? `?${qs}` : ""}`,
  );
}

export async function upsertShippingOutbound(body: ShippingUpsertBody) {
  return api<ShippingOutboundRow>("/api/shipping/outbound", { method: "PUT", body });
}

export async function bulkShippingOutbound(rows: ShippingUpsertBody[]) {
  return api<{ upserted: number }>("/api/shipping/outbound/bulk", {
    method: "POST",
    body: { rows },
  });
}

export async function deleteShippingOutbound(body: { sku: string; method: string; country: string }) {
  return api<void>("/api/shipping/outbound", { method: "DELETE", body });
}

export interface ShippingDefaultRow {
  zone: string;
  amount_eur: string;
  notes: string | null;
}

export async function fetchShippingDefaults() {
  return api<ShippingDefaultRow[]>("/api/shipping/defaults");
}

export async function upsertShippingDefault(zone: string, body: { amount_eur: number | string; notes?: string | null }) {
  return api<ShippingDefaultRow>(`/api/shipping/defaults/${zone}`, { method: "PUT", body });
}

// ===========================================================================
// Users + Roles (admin)
// ===========================================================================

export interface AdminUserRow {
  id: number;
  email: string;
  display_name: string | null;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  role: string;
  is_owner?: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
  password_changed_at?: string | null;
  created_by_id?: number | null;
}

export interface AdminUserDetail extends AdminUserRow {
  created_by_name: string | null;
  modules: Array<{ module: string; views: number; edits: number; total: number }>;
}

export interface AdminRoleRow {
  id: number;
  name: string;
  description: string | null;
  permissions: string[];
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdminUsersFilter {
  q?: string;
  status?: "all" | "active" | "inactive";
  role?: "all" | "owner" | "regular";
}

export async function fetchAdminUsers(filter: AdminUsersFilter = {}) {
  const params = new URLSearchParams();
  if (filter.q) params.set("q", filter.q);
  if (filter.status && filter.status !== "all") params.set("status", filter.status);
  if (filter.role && filter.role !== "all") params.set("role", filter.role);
  const qs = params.toString();
  return api<AdminUserRow[]>(`/api/admin/users${qs ? "?" + qs : ""}`);
}

export async function fetchAdminUserDetail(id: number) {
  return api<AdminUserDetail>(`/api/admin/users/${id}`);
}

export async function createAdminUser(body: {
  email: string;
  password?: string;
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  role: string;
  is_active?: boolean;
  is_owner?: boolean;
  copy_permissions_from_user_id?: number | null;
}) {
  return api<AdminUserRow & { generated_password: string | null }>("/api/admin/users", { method: "POST", body });
}

export async function updateAdminUser(
  id: number,
  body: Partial<{
    email: string;
    password: string;
    display_name: string | null;
    first_name: string | null;
    last_name: string | null;
    phone: string | null;
    role: string;
    is_active: boolean;
  }>,
) {
  return api<AdminUserRow>(`/api/admin/users/${id}`, { method: "PUT", body });
}

export async function deactivateAdminUser(id: number) {
  return api<AdminUserRow>(`/api/admin/users/${id}`, { method: "DELETE" });
}

export async function bulkActivateUsers(ids: number[], active: boolean) {
  return api<{ ok: boolean; updated: number }>(`/api/admin/users/bulk-activate`, {
    method: "POST", body: { ids, active },
  });
}

export async function promoteUserToOwner(id: number) {
  return api<{ ok: boolean }>(`/api/admin/users/${id}/promote-owner`, { method: "POST", body: {} });
}

export async function demoteOwner(id: number) {
  return api<{ ok: boolean }>(`/api/admin/users/${id}/demote-owner`, { method: "POST", body: {} });
}

export async function resetUserPassword(id: number) {
  return api<{ ok: boolean; new_password: string }>(`/api/admin/users/${id}/reset-password`, { method: "POST", body: {} });
}

export async function fetchAdminRoles() {
  return api<AdminRoleRow[]>("/api/admin/roles");
}

// --- Audit log ---

export interface AuditLogEntry {
  id: number;
  user_id: number | null;
  actor_name: string | null;
  actor_email: string | null;
  action: string;
  target_table: string | null;
  target_id: string | null;
  diff: unknown;
  ip_address: string | null;
  created_at: string;
}

export interface AuditLogFilter {
  q?: string;
  user_id?: number;
  action?: string;
  periode?: "today" | "7d" | "30d" | "all";
  limit?: number;
}

// --- Permissions (user × catalogus) ---

export interface PermissionItemWithState {
  key: string;
  module: string;
  label: string;
  description: string | null;
  has_amounts: boolean;
  is_amounts: boolean;
  parent_key: string | null;
  sort_order: number;
  can_view: boolean;
  can_edit: boolean;
  can_see_amounts: boolean;
}

export interface PermissionModule {
  module: string;
  items: PermissionItemWithState[];
}

export interface UserPermissionsResponse {
  user_id: number;
  is_owner: boolean;
  modules: PermissionModule[];
  total_items: number;
  total_granted_view: number;
  total_granted_edit: number;
}

export async function fetchUserPermissions(userId: number) {
  return api<UserPermissionsResponse>(`/api/admin/users/${userId}/permissions`);
}

export async function updateUserPermission(
  userId: number,
  key: string,
  patch: Partial<{ can_view: boolean; can_edit: boolean; can_see_amounts: boolean }>,
) {
  return api<{ ok: boolean; can_view: boolean; can_edit: boolean; can_see_amounts: boolean }>(
    `/api/admin/users/${userId}/permissions/${encodeURIComponent(key)}`,
    { method: "PUT", body: patch },
  );
}

export type BulkPermissionAction =
  | "all_on" | "all_off" | "reset"
  | "module_on" | "module_off"
  | "marketplaces_view_on" | "marketplaces_manage_on" | "marketplaces_off";

export async function bulkUpdateUserPermissions(userId: number, action: BulkPermissionAction, module?: string) {
  return api<{ ok: boolean; affected: number }>(
    `/api/admin/users/${userId}/permissions/bulk`,
    { method: "POST", body: { action, ...(module ? { module } : {}) } },
  );
}

export async function copyUserPermissions(targetUserId: number, sourceUserId: number) {
  return api<{ ok: boolean; copied: number }>(
    `/api/admin/users/${targetUserId}/permissions/copy-from/${sourceUserId}`,
    { method: "POST", body: {} },
  );
}

// --- API keys ---

export interface ApiKeyRow {
  id: number;
  name: string;
  key_prefix: string;
  scope: "read" | "write";
  ip_whitelist: string[] | null;
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
  created_by_id: number;
  created_by_name: string | null;
  revoked_at: string | null;
  is_revoked: boolean;
  is_expired: boolean;
}

export async function fetchApiKeys() {
  return api<ApiKeyRow[]>("/api/admin/api-keys");
}

export async function createApiKey(body: {
  name: string;
  scope: "read" | "write";
  expires_at?: string | null;
  ip_whitelist?: string[] | null;
}) {
  return api<{ id: number; name: string; key_prefix: string; scope: string; plain_key: string }>(
    "/api/admin/api-keys",
    { method: "POST", body },
  );
}

export async function revokeApiKey(id: number) {
  return api<{ ok: boolean }>(`/api/admin/api-keys/${id}/revoke`, { method: "POST", body: {} });
}

export async function rotateApiKey(id: number) {
  return api<{ id: number; key_prefix: string; plain_key: string }>(
    `/api/admin/api-keys/${id}/rotate`, { method: "POST", body: {} },
  );
}

// --- Company settings ---

export interface CompanySettings {
  id: number;
  company_name: string;
  kvk_number: string | null;
  vat_number: string | null;
  address_street: string | null;
  address_zip: string | null;
  address_city: string | null;
  address_country: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  logo_url: string | null;
  default_currency: string;
  default_language: string;
  default_timezone: string;
  default_country: string;
  session_hours_max: number;
  password_min_length: number;
  require_2fa_owners: boolean;
  require_2fa_others: boolean;
  updated_at: string;
  updated_by_id: number | null;
}

export async function fetchCompanySettings() {
  return api<CompanySettings>("/api/admin/settings");
}

export async function updateCompanySettings(patch: Partial<Omit<CompanySettings, "id" | "updated_at" | "updated_by_id">>) {
  return api<CompanySettings>("/api/admin/settings", { method: "PUT", body: patch });
}

export async function fetchAuditLog(filter: AuditLogFilter = {}) {
  const params = new URLSearchParams();
  if (filter.q) params.set("q", filter.q);
  if (filter.user_id) params.set("user_id", String(filter.user_id));
  if (filter.action) params.set("action", filter.action);
  if (filter.periode) params.set("periode", filter.periode);
  if (filter.limit) params.set("limit", String(filter.limit));
  const qs = params.toString();
  return api<{ entries: AuditLogEntry[]; count: number; limit: number }>(
    `/api/admin/audit-log${qs ? "?" + qs : ""}`,
  );
}

// ============================================================================
// Fase 4.3 — Products / Catalogus
// ============================================================================

export interface ProductRow {
  id: number;
  sku: string | null;
  ean: string | null;
  title: string | null;
  weight_g: number | null;
  dimensions_mm: { length: number | null; width: number | null; height: number | null };
  is_set: boolean | null;
  visible: boolean | null;
  landed_cost_eur: string | null;
  qty_actueel: number | null;
}

export interface ProductDetail extends ProductRow {
  purchase_eur: string | null;
  shipping_eur: string | null;
  qty_inkomend: number | null;
  sold_since_snapshot: number | null;
  monthly_sales: Array<{ month: string; units: number; revenue: string; margin: string }>;
}

export async function fetchProducts(q?: string, onlyVisible = false) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (onlyVisible) params.set("only_visible", "true");
  return api<ProductRow[]>("/api/products" + (params.toString() ? "?" + params : ""));
}

export async function fetchProduct(sku: string) {
  return api<ProductDetail>(`/api/products/${encodeURIComponent(sku)}`);
}

export async function updateProduct(sku: string, patch: Partial<{
  visible: boolean; weight_g: number | null;
  length_mm: number | null; width_mm: number | null; height_mm: number | null;
  is_set: boolean;
}>) {
  return api<ProductRow>(`/api/products/${encodeURIComponent(sku)}`, { method: "PUT", body: patch });
}

// ============================================================================
// Fase 4.5 — Marketplaces (per-kanaal)
// ============================================================================

export interface MarketplaceRow {
  sales_channel_name: string | null;
  sales_channel_meta_id: number | null;
  total_orders: number;
  total_items: number;
  total_revenue: string;
  total_margin: string;
  return_count: number;
  last_order_at: string | null;
}

export interface MarketplaceDetail {
  sales_channel_name: string;
  total_orders: number;
  total_items: number;
  total_revenue: string;
  total_margin: string;
  avg_vat_pct: string | null;
  margin_pct: string | null;
  monthly: Array<{ month: string; orders: number; revenue: string; margin: string }>;
  top_skus: Array<{ sku: string; title: string; units: number; revenue: string; margin: string }>;
}

export async function fetchMarketplaces() {
  return api<MarketplaceRow[]>("/api/marketplaces");
}

export async function fetchMarketplace(name: string) {
  return api<MarketplaceDetail>(`/api/marketplaces/${encodeURIComponent(name)}`);
}

// ============================================================================
// Fase 4.6 — Customers
// ============================================================================

export interface CustomerRow {
  email_hash: string;
  country: string | null;
  order_count: number;
  total_spent: string | null;
  total_margin: string | null;
  first_order_at: string | null;
  last_order_at: string | null;
  channels: string | null;
  is_blocked?: boolean;
  merged_into_hash?: string | null;
}

export interface CustomerDetail extends Omit<CustomerRow, "email_hash"> {
  email_hash: string;
  customer_country: string | null;
  orders: Array<{
    id: number;
    external_id: string | null;
    sales_channel_name: string | null;
    status: string | null;
    workflow_state: string | null;
    order_date: string | null;
    item_count: number;
    total_amount: string | null;
    total_margin: string | null;
  }>;
}

export async function fetchCustomers(q?: string, country?: string) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (country) params.set("country", country);
  return api<CustomerRow[]>("/api/customers" + (params.toString() ? "?" + params : ""));
}

export async function fetchCustomer(hash: string) {
  return api<CustomerDetail>(`/api/customers/${encodeURIComponent(hash)}`);
}

// ============================================================================
// Fase 4.7 — BTW-aangifte
// ============================================================================

export interface BtwAangifte {
  year: number;
  quarter: number;
  period_start: string;
  period_end: string;
  totals: { revenue_excl: string; vat_amount: string; revenue_incl: string };
  buckets: Array<{
    country: string;
    vat_pct: number | null;
    revenue_excl: string;
    vat_amount: string;
    revenue_incl: string;
    line_count: number;
  }>;
}

export async function fetchBtwAangifte(year: number, quarter: number) {
  return api<BtwAangifte>(`/api/finance/btw-aangifte?year=${year}&quarter=${quarter}`);
}

// ============================================================================
// Fase 4.9 — Reports
// ============================================================================

export interface ReportCatalogItem { key: string; title: string; description: string }

export async function fetchReportCatalog() {
  return api<ReportCatalogItem[]>("/api/reports");
}

export async function fetchReport<T = any>(key: string) {
  return api<T[]>(`/api/reports/${encodeURIComponent(key)}`);
}

// ============================================================================
// Fase 4.10 — Reconciliation / Integration health
// ============================================================================

export interface ReconcileHealth {
  job: string;
  last_started: string | null;
  last_finished: string | null;
  last_status: string | null;
  last_upserted: number | null;
  errors_24h: number;
  total_runs: number;
}

export interface ReconcileRun {
  id: number;
  job: string;
  started_at: string | null;
  finished_at: string | null;
  status: string | null;
  rows_in: number | null;
  rows_upserted: number | null;
  error_message: string | null;
  notes: string | null;
}

export interface ReconcileError {
  id: number;
  sync_run_id: number | null;
  tab_name: string | null;
  row_index: number | null;
  field_name: string | null;
  error_message: string;
  created_at: string | null;
}

export interface ReconcileGap {
  channel: string | null;
  total_orders: number;
  missing_workflow: number;
  missing_email: number;
  last_order_at: string | null;
}

export async function fetchReconcileHealth() { return api<ReconcileHealth[]>("/api/reconcile/health"); }
export async function fetchReconcileRuns(job?: string, limit = 50) {
  const p = new URLSearchParams();
  if (job) p.set("job", job);
  p.set("limit", String(limit));
  return api<ReconcileRun[]>("/api/reconcile/runs?" + p);
}
export async function fetchReconcileErrors(tab?: string, limit = 100) {
  const p = new URLSearchParams();
  if (tab) p.set("tab", tab);
  p.set("limit", String(limit));
  return api<ReconcileError[]>("/api/reconcile/errors?" + p);
}
export async function fetchReconcileGaps() { return api<ReconcileGap[]>("/api/reconcile/gaps"); }

// ============================================================================
// Fase 4.4 — Suppliers
// ============================================================================

export interface SupplierRow {
  id: number;
  name: string;
  code: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  country: string | null;
  payment_terms_days: number | null;
  currency: string;
  notes: string | null;
  is_active: boolean;
  external_id: string | null;
  created_at: string | null;
  orders_ytd: number;
  spend_ytd_eur: string | null;
  units_ytd: number;
  last_order_date: string | null;
  avg_lead_time_days: number | null;
}

export interface SupplierInput {
  name: string;
  code?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  country?: string | null;
  payment_terms_days?: number | null;
  currency?: string;
  notes?: string | null;
  is_active?: boolean;
  external_id?: string | null;
}

export async function fetchSuppliers(q?: string, includeInactive = false) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (includeInactive) params.set("include_inactive", "true");
  return api<SupplierRow[]>("/api/suppliers" + (params.toString() ? "?" + params : ""));
}

export async function fetchSupplier(id: number) {
  return api<SupplierRow>(`/api/suppliers/${id}`);
}

export async function createSupplier(body: SupplierInput) {
  return api<SupplierRow>("/api/suppliers", { method: "POST", body });
}

export async function updateSupplier(id: number, body: Partial<SupplierInput>) {
  return api<SupplierRow>(`/api/suppliers/${id}`, { method: "PUT", body });
}

export async function deleteSupplier(id: number) {
  return api<{ deactivated?: boolean; reason?: string } | null>(`/api/suppliers/${id}`, { method: "DELETE" });
}

// ============================================================================
// Fase 4.4 — Supplier orders (PO's)
// ============================================================================

export type SupplierOrderStatus = "draft" | "confirmed" | "partial" | "received" | "cancelled";

export interface SupplierOrderRow {
  id: number;
  supplier_id: number;
  supplier_name: string | null;
  order_number: string | null;
  order_date: string | null;
  expected_date: string | null;
  received_date: string | null;
  status: SupplierOrderStatus;
  currency: string;
  total_excl_vat_eur: string | null;
  shipping_eur: string | null;
  items_qty_ordered: number;
  items_qty_received: number;
  items_subtotal_eur: string;
  line_count: number;
  trailer_id: number | null;
  notes: string | null;
}

export interface SupplierOrderItem {
  id: number;
  sku: string;
  product_title: string | null;
  qty_ordered: number;
  qty_received: number;
  unit_price_eur: string;
  sort_order: number;
  notes: string | null;
}

export interface SupplierOrderDetail extends Omit<SupplierOrderRow, "items_qty_ordered" | "items_qty_received" | "line_count"> {
  items: SupplierOrderItem[];
}

export async function fetchSupplierOrders(filter: { status?: string; supplier_id?: number } = {}) {
  const params = new URLSearchParams();
  if (filter.status) params.set("status", filter.status);
  if (filter.supplier_id) params.set("supplier_id", String(filter.supplier_id));
  return api<SupplierOrderRow[]>("/api/supplier-orders" + (params.toString() ? "?" + params : ""));
}

export async function fetchSupplierOrder(id: number) {
  return api<SupplierOrderDetail>(`/api/supplier-orders/${id}`);
}

export async function createSupplierOrder(body: {
  supplier_id: number;
  order_number?: string;
  order_date?: string;
  expected_date?: string | null;
  status?: SupplierOrderStatus;
  currency?: string;
  total_excl_vat_eur?: number | null;
  shipping_eur?: number | null;
  notes?: string | null;
  trailer_id?: number | null;
}) {
  return api<{ id: number }>("/api/supplier-orders", { method: "POST", body });
}

export async function updateSupplierOrder(id: number, body: Partial<{
  status: SupplierOrderStatus;
  order_number: string | null;
  expected_date: string | null;
  received_date: string | null;
  total_excl_vat_eur: number | null;
  shipping_eur: number | null;
  notes: string | null;
  trailer_id: number | null;
}>) {
  return api(`/api/supplier-orders/${id}`, { method: "PUT", body });
}

export async function deleteSupplierOrder(id: number) {
  return api<null>(`/api/supplier-orders/${id}`, { method: "DELETE" });
}

export async function addSupplierOrderItem(orderId: number, body: {
  sku: string; product_title?: string | null;
  qty_ordered: number; unit_price_eur: number;
  sort_order?: number; notes?: string | null;
}) {
  return api<SupplierOrderItem>(`/api/supplier-orders/${orderId}/items`, { method: "POST", body });
}

export async function updateSupplierOrderItem(itemId: number, body: Partial<{
  qty_ordered: number; qty_received: number;
  unit_price_eur: number; sort_order: number; notes: string | null;
}>) {
  return api<SupplierOrderItem>(`/api/supplier-orders/items/${itemId}`, { method: "PUT", body });
}

export async function deleteSupplierOrderItem(itemId: number) {
  return api<null>(`/api/supplier-orders/items/${itemId}`, { method: "DELETE" });
}

// ============================================================================
// Fase 4.7 — Supplier invoices (inkomende facturen) + payments
// ============================================================================

export type InvoicePaymentStatus = "open" | "partial" | "paid" | "overdue";

export interface SupplierInvoiceRow {
  id: number;
  supplier_id: number;
  supplier_name: string | null;
  supplier_order_id: number | null;
  supplier_order_number: string | null;
  invoice_number: string;
  invoice_date: string;
  due_date: string | null;
  currency: string;
  subtotal_excl_vat: string;
  vat_amount: string;
  total_incl_vat: string;
  paid_eur: string;
  outstanding_eur: string;
  payment_status: InvoicePaymentStatus;
  payment_count: number;
  last_payment_date: string | null;
  notes: string | null;
  file_url: string | null;
}

export interface SupplierInvoicePayment {
  id: number;
  payment_date: string;
  amount_eur: string;
  method: string | null;
  reference: string | null;
  notes: string | null;
  created_at: string | null;
  created_by_name: string | null;
}

export interface SupplierInvoiceDetail extends Omit<SupplierInvoiceRow, "payment_count"> {
  payments: SupplierInvoicePayment[];
}

export interface SupplierInvoiceInput {
  supplier_id: number;
  supplier_order_id?: number | null;
  invoice_number: string;
  invoice_date: string;
  due_date?: string | null;
  currency?: string;
  subtotal_excl_vat: number;
  vat_amount?: number;
  total_incl_vat: number;
  notes?: string | null;
  file_url?: string | null;
}

export async function fetchSupplierInvoices(filter: {
  status?: InvoicePaymentStatus | "";
  supplier_id?: number;
  supplier_order_id?: number;
  q?: string;
} = {}) {
  const params = new URLSearchParams();
  if (filter.status) params.set("status", filter.status);
  if (filter.supplier_id) params.set("supplier_id", String(filter.supplier_id));
  if (filter.supplier_order_id) params.set("supplier_order_id", String(filter.supplier_order_id));
  if (filter.q) params.set("q", filter.q);
  return api<SupplierInvoiceRow[]>("/api/supplier-invoices" + (params.toString() ? "?" + params : ""));
}

export async function fetchSupplierInvoice(id: number) {
  return api<SupplierInvoiceDetail>(`/api/supplier-invoices/${id}`);
}

export async function createSupplierInvoice(body: SupplierInvoiceInput) {
  return api<{ id: number }>("/api/supplier-invoices", { method: "POST", body });
}

export async function updateSupplierInvoice(id: number, body: Partial<SupplierInvoiceInput>) {
  return api(`/api/supplier-invoices/${id}`, { method: "PUT", body });
}

export async function deleteSupplierInvoice(id: number) {
  return api<null>(`/api/supplier-invoices/${id}`, { method: "DELETE" });
}

export async function recordInvoicePayment(invoiceId: number, body: {
  payment_date?: string;
  amount_eur: number;
  method?: string | null;
  reference?: string | null;
  notes?: string | null;
}) {
  return api<SupplierInvoicePayment>(`/api/supplier-invoices/${invoiceId}/payments`, { method: "POST", body });
}

export async function deleteInvoicePayment(paymentId: number) {
  return api<null>(`/api/supplier-invoices/payments/${paymentId}`, { method: "DELETE" });
}

export interface InvoiceSummaryTotals {
  total_invoices: number;
  total_outstanding: string;
  total_overdue: string;
  total_due_30d: string;
}

export async function fetchInvoiceSummary() {
  return api<InvoiceSummaryTotals>("/api/supplier-invoices/summary/totals");
}

// ============================================================================
// Fase 4.4 — Supplier contracts (file uploads)
// ============================================================================

export interface SupplierContract {
  id: number;
  supplier_id: number;
  supplier_name: string | null;
  title: string;
  contract_number: string | null;
  valid_from: string | null;
  valid_until: string | null;
  file_path: string | null;
  file_name: string | null;
  file_size_bytes: number | null;
  file_mime_type: string | null;
  notes: string | null;
  is_active: boolean;
  version: number;
  parent_contract_id: number | null;
  created_at: string | null;
  created_by_name: string | null;
}

export interface SupplierContractInput {
  supplier_id: number;
  title: string;
  contract_number?: string | null;
  valid_from?: string | null;
  valid_until?: string | null;
  notes?: string | null;
  file_base64?: string;
  file_name?: string;
  file_mime_type?: string;
}

export async function fetchSupplierContracts(supplierId?: number) {
  const params = new URLSearchParams();
  if (supplierId) params.set("supplier_id", String(supplierId));
  return api<SupplierContract[]>("/api/supplier-contracts" + (params.toString() ? "?" + params : ""));
}

export async function createSupplierContract(body: SupplierContractInput) {
  return api<SupplierContract>("/api/supplier-contracts", { method: "POST", body });
}

export async function deleteSupplierContract(id: number) {
  return api<null>(`/api/supplier-contracts/${id}`, { method: "DELETE" });
}

export function supplierContractDownloadUrl(id: number): string {
  return `/api/supplier-contracts/${id}/download`;
}

// ============================================================================
// Fase 4.4 — B2B prices
// ============================================================================

export interface B2BPrice {
  id: number;
  sku: string;
  product_title: string | null;
  tier_name: string;
  price_eur: string;
  min_quantity: number;
  valid_from: string;
  valid_to: string | null;
  notes: string | null;
  created_at: string | null;
}

export async function fetchB2BPrices(filter: { sku?: string; tier_name?: string; active_only?: boolean } = {}) {
  const params = new URLSearchParams();
  if (filter.sku) params.set("sku", filter.sku);
  if (filter.tier_name) params.set("tier_name", filter.tier_name);
  if (filter.active_only === false) params.set("active_only", "false");
  return api<B2BPrice[]>("/api/b2b-prices" + (params.toString() ? "?" + params : ""));
}

export async function fetchB2BTiers() {
  return api<Array<{ tier_name: string; n: number }>>("/api/b2b-prices/tiers");
}

export async function createB2BPrice(body: {
  sku: string; tier_name: string; price_eur: number;
  min_quantity?: number; valid_from?: string; valid_to?: string | null; notes?: string | null;
}) {
  return api<B2BPrice>("/api/b2b-prices", { method: "POST", body });
}

export async function updateB2BPrice(id: number, body: Partial<{
  price_eur: number; min_quantity: number; valid_to: string | null; notes: string | null;
}>) {
  return api<B2BPrice>(`/api/b2b-prices/${id}`, { method: "PUT", body });
}

export async function deleteB2BPrice(id: number) {
  return api<null>(`/api/b2b-prices/${id}`, { method: "DELETE" });
}

// ============================================================================
// Fase 4.6 — Customer meta (block + merge)
// ============================================================================

export interface CustomerMeta {
  customer_email_hash: string;
  is_blocked: boolean;
  block_reason: string | null;
  blocked_at: string | null;
  merged_into_hash: string | null;
  merged_at: string | null;
  notes: string | null;
}

export async function fetchCustomerMeta(hash: string) {
  return api<CustomerMeta | null>(`/api/customer-meta/${encodeURIComponent(hash)}`);
}

export async function blockCustomer(hash: string, is_blocked: boolean, block_reason?: string | null) {
  return api<{ ok: true }>(`/api/customer-meta/${encodeURIComponent(hash)}/block`, {
    method: "PUT", body: { is_blocked, block_reason },
  });
}

export async function mergeCustomers(source_hash: string, target_hash: string) {
  return api<{ ok: true; source: string; target: string }>(`/api/customer-meta/merge`, {
    method: "POST", body: { source_hash, target_hash },
  });
}

// ============================================================================
// Fase 4.9 — Saved reports (custom rapportages bouwer)
// ============================================================================

export type AggregationFn = "sum" | "avg" | "count" | "min" | "max";
export type FilterOp = "eq" | "neq" | "gt" | "gte" | "lt" | "lte" | "like" | "in" | "is_null" | "is_not_null";

export interface ReportColumnDef {
  name: string;
  type: "text" | "number" | "date";
  aggregatable: boolean;
}

export interface ReportDatasetDef {
  source: string;
  columns: ReportColumnDef[];
}

export interface ReportConfig {
  dataset: string;
  columns: string[];
  aggregations?: Array<{ column: string; fn: AggregationFn; alias: string }>;
  filters: Array<{ column: string; op: FilterOp; value: unknown }>;
  group_by?: string[];
  order_by?: { column: string; dir: "asc" | "desc" } | null;
  limit_rows: number;
}

export interface SavedReport {
  id: number;
  name: string;
  description: string | null;
  dataset: string;
  is_shared: boolean;
  config: ReportConfig;
  created_at: string | null;
  created_by_name: string | null;
}

export async function fetchReportDatasets() {
  return api<Record<string, ReportDatasetDef>>("/api/saved-reports/datasets");
}

export async function previewReport(config: ReportConfig) {
  return api<{ sql: string; row_count: number; rows: any[] }>("/api/saved-reports/preview", { method: "POST", body: config });
}

export async function fetchSavedReports() {
  return api<SavedReport[]>("/api/saved-reports");
}

export async function fetchSavedReport(id: number) {
  return api<{ id: number; name: string; description: string | null; dataset: string; is_shared: boolean; config: ReportConfig; rows: any[] }>(`/api/saved-reports/${id}`);
}

export async function saveReport(body: {
  name: string; description?: string | null; is_shared?: boolean; config: ReportConfig;
}) {
  return api<{ id: number }>("/api/saved-reports", { method: "POST", body });
}

export async function updateSavedReport(id: number, body: {
  name: string; description?: string | null; is_shared?: boolean; config: ReportConfig;
}) {
  return api(`/api/saved-reports/${id}`, { method: "PUT", body });
}

export async function deleteSavedReport(id: number) {
  return api<null>(`/api/saved-reports/${id}`, { method: "DELETE" });
}

// ===========================================================================
// Amazon SP-API — orders, items, sync, stats
// ===========================================================================

export interface AmazonOrderRow {
  amazon_order_id: string;
  sp_account_id: number;
  marketplace_id: string;
  marketplace_code: string | null;
  purchase_date: string;
  last_update_date: string;
  order_status: string;
  fulfillment_channel: string | null;
  sales_channel: string | null;
  order_total_amount: string | null;
  order_total_currency: string | null;
  number_of_items_shipped: number | null;
  number_of_items_unshipped: number | null;
  is_business_order: boolean;
  is_prime: boolean;
  easy_ship_shipment_status: string | null;
}

export interface AmazonOrdersListResponse {
  total: number;
  limit: number;
  offset: number;
  rows: AmazonOrderRow[];
}

export interface AmazonOrderDetail {
  order: AmazonOrderRow & {
    earliest_ship_date: string | null;
    latest_ship_date: string | null;
    earliest_delivery_date: string | null;
    latest_delivery_date: string | null;
    is_premium_order: boolean;
    is_global_express: boolean;
    purchase_order_number: string | null;
    is_estimated_ship_date_set: boolean;
    has_regulated_items: boolean;
    ship_service_level: string | null;
    raw_payload: unknown;
    synced_at: string;
  };
  items: Array<{
    order_item_id: string;
    asin: string | null;
    seller_sku: string | null;
    title: string | null;
    quantity_ordered: number;
    quantity_shipped: number;
    item_price_amount: string | null;
    item_price_currency: string | null;
    shipping_price_amount: string | null;
    item_tax_amount: string | null;
    promotion_discount_amount: string | null;
    is_gift: boolean;
    condition_id: string | null;
    raw_payload: unknown;
  }>;
}

export interface AmazonSyncStateRow {
  sp_account_id: number;
  marketplace_id: string;
  marketplace_code: string | null;
  last_update_cursor: string | null;
  last_sync_at: string | null;
  last_sync_count: number;
  last_sync_error: string | null;
}

export interface AmazonStats {
  total_orders: number;
  total_items: number;
  oldest_purchase_date: string | null;
  newest_purchase_date: string | null;
  by_status: Array<{ status: string; count: number }>;
  by_marketplace: Array<{ marketplace_id: string; code: string | null; count: number }>;
}

export interface AmazonSyncResult {
  marketplaceId: string;
  ordersFetched: number;
  itemsFetched: number;
  newCursor: string | null;
  durationMs: number;
  error?: string;
}

export async function fetchAmazonOrders(filter: {
  account_id?: number;
  marketplace_id?: string;
  status?: string;
  fulfillment?: string;
  from?: string;
  to?: string;
  search?: string;
  limit?: number;
  offset?: number;
} = {}) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  }
  const qs = params.toString();
  return api<AmazonOrdersListResponse>(`/api/amazon/orders${qs ? "?" + qs : ""}`);
}

export async function fetchAmazonOrderDetail(orderId: string) {
  return api<AmazonOrderDetail>(`/api/amazon/orders/${encodeURIComponent(orderId)}`);
}

export async function fetchAmazonSyncState(accountId?: number) {
  const qs = accountId ? `?account_id=${accountId}` : "";
  return api<{ rows: AmazonSyncStateRow[] }>(`/api/amazon/sync-state${qs}`);
}

export async function fetchAmazonStats() {
  return api<AmazonStats>("/api/amazon/stats");
}

export async function triggerAmazonOrdersSync(
  accountId: number,
  opts: { since?: string; limit?: number; marketplaces?: string } = {},
) {
  const params = new URLSearchParams();
  if (opts.since) params.set("since", opts.since);
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.marketplaces) params.set("marketplaces", opts.marketplaces);
  const qs = params.toString();
  return api<{
    ok: boolean;
    account: { id: number; name: string };
    results: AmazonSyncResult[];
    totals: { marketplaces: number; orders: number; items: number; errors: number };
  }>(`/api/amazon/sync/orders/${accountId}${qs ? "?" + qs : ""}`, { method: "POST" });
}

// ===========================================================================
// Amazon Messaging — templates, settings, send, sent log
// ===========================================================================

export interface AmazonMessageTemplate {
  id: number;
  name: string;
  intent: string;
  lang: string;
  subject: string | null;
  body: string;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AmazonMessagingSetting {
  key: string;
  value: string;
  description: string | null;
  updated_at: string;
}

export interface AmazonMessageSent {
  id: number;
  amazon_order_id: string;
  sp_account_id: number;
  intent: string;
  template_id: number | null;
  lang: string;
  body: string;
  success: boolean;
  api_status: number | null;
  api_error: string | null;
  triggered_by: string;
  sent_by_id: number | null;
  sent_at: string;
}

export async function fetchAmazonMessageTemplates() {
  return api<{ rows: AmazonMessageTemplate[] }>("/api/amazon/messages/templates");
}

export async function createAmazonMessageTemplate(body: {
  name: string; intent?: string; lang: string;
  subject?: string | null; body: string;
  is_default?: boolean; is_active?: boolean;
}) {
  return api<{ id: number }>("/api/amazon/messages/templates", { method: "POST", body });
}

export async function updateAmazonMessageTemplate(id: number, body: Partial<{
  name: string; intent: string; lang: string;
  subject: string | null; body: string;
  is_default: boolean; is_active: boolean;
}>) {
  return api<{ ok: true }>(`/api/amazon/messages/templates/${id}`, { method: "PUT", body });
}

export async function deleteAmazonMessageTemplate(id: number) {
  return api<void>(`/api/amazon/messages/templates/${id}`, { method: "DELETE" });
}

export async function fetchAmazonMessagingSettings() {
  return api<{ rows: AmazonMessagingSetting[] }>("/api/amazon/messages/settings");
}

export async function updateAmazonMessagingSetting(key: string, value: string) {
  return api<{ ok: true }>(`/api/amazon/messages/settings/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: { value },
  });
}

export async function sendAmazonMessage(input: {
  order_id: string;
  intent?: string;
  template_id?: number;
  body?: string;
}) {
  return api<{ ok: boolean; message_id: number; preview: string; message?: string }>(
    "/api/amazon/messages/send",
    { method: "POST", body: input },
  );
}

export async function fetchAmazonMessagesSent(opts: { order_id?: string; limit?: number; offset?: number } = {}) {
  const params = new URLSearchParams();
  if (opts.order_id) params.set("order_id", opts.order_id);
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.offset) params.set("offset", String(opts.offset));
  const qs = params.toString();
  return api<{ total: number; limit: number; offset: number; rows: AmazonMessageSent[] }>(
    `/api/amazon/messages/sent${qs ? "?" + qs : ""}`,
  );
}

export async function fetchAmazonMessageActions(orderId: string) {
  return api<{ actions: string[] }>(`/api/amazon/messages/actions/${encodeURIComponent(orderId)}`);
}

// ===========================================================================
// Amazon Listings — assortiment + prijs-update
// ===========================================================================

export interface AmazonListing {
  id: number;
  sp_account_id: number;
  marketplace_id: string;
  marketplace_code: string | null;
  seller_sku: string;
  asin: string | null;
  item_name: string | null;
  listing_id: string | null;
  price: string | null;
  currency: string | null;
  quantity: number | null;
  status: string | null;
  fulfillment_channel: string | null;
  pending_quantity: number | null;
  open_date: string | null;
  synced_at: string;
}

export interface AmazonListingsListResponse {
  total: number;
  limit: number;
  offset: number;
  rows: AmazonListing[];
}

export interface AmazonListingsSyncResult {
  marketplaceId: string;
  listingsFetched: number;
  reportId: string | null;
  durationMs: number;
  error?: string;
}

export interface AmazonListingsSyncStateRow {
  sp_account_id: number;
  marketplace_id: string;
  marketplace_code: string | null;
  last_sync_at: string | null;
  last_sync_count: number;
  last_sync_error: string | null;
  last_report_id: string | null;
}

export interface AmazonPriceChangeRow {
  id: number;
  sp_account_id: number;
  marketplace_id: string;
  marketplace_code: string | null;
  seller_sku: string;
  old_price: string | null;
  new_price: string;
  currency: string;
  success: boolean;
  api_status: number | null;
  api_error: string | null;
  submission_id: string | null;
  changed_by_id: number | null;
  changed_at: string;
}

export async function fetchAmazonListings(filter: {
  account_id?: number;
  marketplace_id?: string;
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
} = {}) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  }
  const qs = params.toString();
  return api<AmazonListingsListResponse>(`/api/amazon/listings${qs ? "?" + qs : ""}`);
}

export async function fetchAmazonListingsSyncState() {
  return api<{ rows: AmazonListingsSyncStateRow[] }>("/api/amazon/listings/sync-state");
}

export async function triggerAmazonListingsSync(
  accountId: number,
  opts: { marketplaces?: string } = {},
) {
  const params = new URLSearchParams();
  if (opts.marketplaces) params.set("marketplaces", opts.marketplaces);
  const qs = params.toString();
  return api<{
    ok: boolean;
    account: { id: number; name: string };
    results: AmazonListingsSyncResult[];
    totals: { marketplaces: number; listings: number; errors: number };
  }>(`/api/amazon/sync/listings/${accountId}${qs ? "?" + qs : ""}`, { method: "POST" });
}

export async function updateAmazonListingPrice(sku: string, input: {
  sp_account_id: number;
  marketplace_id: string;
  price: number;
  currency: string;
}) {
  return api<{
    ok: boolean;
    submission_id: string | null;
    old_price: number | null;
    new_price: number;
    currency: string;
    error?: string;
  }>(`/api/amazon/listings/${encodeURIComponent(sku)}/price`, {
    method: "POST",
    body: input,
  });
}

export async function fetchAmazonPriceHistory(sku: string, marketplaceId?: string) {
  const qs = marketplaceId ? `?marketplace_id=${encodeURIComponent(marketplaceId)}` : "";
  return api<{ rows: AmazonPriceChangeRow[] }>(
    `/api/amazon/listings/${encodeURIComponent(sku)}/price-history${qs}`,
  );
}

export interface AmazonHandlingTimeChangeRow {
  id: number;
  sp_account_id: number;
  marketplace_id: string;
  marketplace_code: string | null;
  seller_sku: string;
  fulfillment_channel_code: string;
  old_lead_time: number | null;
  new_lead_time: number;
  success: boolean;
  api_status: number | null;
  api_error: string | null;
  submission_id: string | null;
  changed_by_id: number | null;
  changed_at: string;
}

export async function updateAmazonListingHandlingTime(sku: string, input: {
  sp_account_id: number;
  marketplace_id: string;
  lead_time_max_days: number;
  fulfillment_channel?: string;
}) {
  return api<{
    ok: boolean;
    submission_id: string | null;
    new_lead_time: number;
    fulfillment_channel: string;
    error?: string;
  }>(`/api/amazon/listings/${encodeURIComponent(sku)}/handling-time`, {
    method: "POST",
    body: input,
  });
}

export async function fetchAmazonHandlingTimeHistory(sku: string, marketplaceId?: string) {
  const qs = marketplaceId ? `?marketplace_id=${encodeURIComponent(marketplaceId)}` : "";
  return api<{ rows: AmazonHandlingTimeChangeRow[] }>(
    `/api/amazon/listings/${encodeURIComponent(sku)}/handling-time-history${qs}`,
  );
}

// === Warehouse routing ===

export interface AmazonWarehouse {
  id: number;
  code: string;
  name: string;
  default_lead_time_days: number;
  sort_order: number;
  is_active: boolean;
  notes: string | null;
  siu_warehouse_names: string[];
  created_at: string;
  updated_at: string;
}

export interface AmazonWarehouseStockRow {
  warehouse_id: number;
  warehouse_code: string;
  warehouse_name: string;
  warehouse_sort_order: number;
  warehouse_default_lead_time_days: number;
  quantity: number;
  lead_time_override_days: number | null;
  effective_lead_time_days: number;
  is_active: boolean;
  updated_at: string;
}

export interface AmazonRoutingDecisionRow {
  id: number;
  sp_account_id: number;
  marketplace_id: string;
  marketplace_code: string | null;
  seller_sku: string;
  resolved_warehouse_id: number | null;
  warehouse_code: string | null;
  warehouse_name: string | null;
  resolved_lead_time_days: number | null;
  resolved_quantity: number | null;
  reason: string | null;
  pushed_to_amazon: boolean;
  push_success: boolean | null;
  push_submission_id: string | null;
  push_error: string | null;
  triggered_by: string;
  resolved_by_id: number | null;
  resolved_at: string;
}

export async function fetchAmazonWarehouses() {
  return api<{ rows: AmazonWarehouse[] }>("/api/amazon/warehouses");
}

export async function createAmazonWarehouse(body: {
  code: string; name: string; default_lead_time_days: number;
  sort_order?: number; is_active?: boolean; notes?: string | null;
}) {
  return api<{ id: number }>("/api/amazon/warehouses", { method: "POST", body });
}

export async function updateAmazonWarehouse(id: number, body: Partial<{
  code: string; name: string; default_lead_time_days: number;
  sort_order: number; is_active: boolean; notes: string | null;
  siu_warehouse_names: string[];
}>) {
  return api<{ ok: true }>(`/api/amazon/warehouses/${id}`, { method: "PUT", body });
}

export async function fetchAmazonSiuWarehouses() {
  return api<{ warehouses: SiuWarehouseSummary[] }>(`/api/amazon/siu-warehouses`);
}

export async function deleteAmazonWarehouse(id: number) {
  return api<void>(`/api/amazon/warehouses/${id}`, { method: "DELETE" });
}

export async function fetchAmazonListingWarehouseStock(sku: string, opts: { sp_account_id: number; marketplace_id: string }) {
  const qs = `?sp_account_id=${opts.sp_account_id}&marketplace_id=${encodeURIComponent(opts.marketplace_id)}`;
  return api<{ rows: AmazonWarehouseStockRow[] }>(
    `/api/amazon/listings/${encodeURIComponent(sku)}/warehouse-stock${qs}`,
  );
}

export async function updateAmazonListingWarehouseStock(sku: string, body: {
  sp_account_id: number;
  marketplace_id: string;
  items: Array<{ warehouse_id: number; quantity: number; lead_time_override_days?: number | null }>;
}) {
  return api<{
    ok: boolean;
    count: number;
    auto?: {
      triggered: boolean;
      pushed: boolean;
      reason: string;
      lead_time_days: number | null;
      warehouse_name: string | null;
      submission_id: string | null;
      error: string | null;
    };
  }>(
    `/api/amazon/listings/${encodeURIComponent(sku)}/warehouse-stock`,
    { method: "PUT", body },
  );
}

export async function resolveAndSyncAmazonListing(sku: string, body: {
  sp_account_id: number;
  marketplace_id: string;
  push_to_amazon?: boolean;
}) {
  return api<{
    decision: {
      resolved: boolean;
      warehouse: AmazonWarehouse | null;
      lead_time_days: number | null;
      quantity: number;
      reason: string;
    };
    pushed: boolean;
    push_success: boolean | null;
    push_submission_id: string | null;
    push_error: string | null;
  }>(
    `/api/amazon/listings/${encodeURIComponent(sku)}/resolve-and-sync`,
    { method: "POST", body },
  );
}

export async function fetchAmazonRoutingHistory(sku: string, marketplaceId?: string) {
  const qs = marketplaceId ? `?marketplace_id=${encodeURIComponent(marketplaceId)}` : "";
  return api<{ rows: AmazonRoutingDecisionRow[] }>(
    `/api/amazon/listings/${encodeURIComponent(sku)}/routing-history${qs}`,
  );
}

export interface AmazonRoutingConfigResponse {
  auto_route_enabled: boolean;
  updated_at: string | null;
}

export async function fetchAmazonRoutingConfig(sku: string, opts: { sp_account_id: number; marketplace_id: string }) {
  const qs = `?sp_account_id=${opts.sp_account_id}&marketplace_id=${encodeURIComponent(opts.marketplace_id)}`;
  return api<AmazonRoutingConfigResponse>(`/api/amazon/listings/${encodeURIComponent(sku)}/routing-config${qs}`);
}

export async function updateAmazonRoutingConfig(sku: string, body: {
  sp_account_id: number;
  marketplace_id: string;
  auto_route_enabled: boolean;
}) {
  return api<{ ok: boolean; auto_route_enabled: boolean }>(
    `/api/amazon/listings/${encodeURIComponent(sku)}/routing-config`,
    { method: "PUT", body },
  );
}

// On-demand frontend PDP-check: scrape amazon.{tld}/dp/{ASIN} en parse
// delivery-time zoals klant hem ziet. Vergelijk met backend expected.
export interface AmazonFrontendCheckResponse {
  ok: true;
  sku: string;
  asin: string;
  marketplace_id: string;
  amazon_url: string;
  fetched_at: string;
  http_status: number;
  backend_last_pushed_at: string | null;
  raw: {
    deliveryTime: string | null;
    deliveryPrice: string | null;
    deliveryCutoff: string | null;
    deliveryType: string | null;
    inStockText: string | null;
  };
  parsed: {
    days_min: number | null;
    days_max: number | null;
    parsed_ok: boolean;
    parse_error: string | null;
  };
  comparison: {
    expected_lead_time_days: number | null;
    match: boolean | null;
    warning: string | null;
  };
}

export async function fetchAmazonFrontendCheck(sku: string, opts: { sp_account_id: number; marketplace_id: string }) {
  const qs = `?sp_account_id=${opts.sp_account_id}&marketplace_id=${encodeURIComponent(opts.marketplace_id)}`;
  return api<AmazonFrontendCheckResponse>(`/api/amazon/listings/${encodeURIComponent(sku)}/frontend-check${qs}`);
}

// ---------------------------------------------------------------------------
// Easy Ship templates + routing rules per (account, marketplace, warehouse)
// ---------------------------------------------------------------------------

export interface AmazonEasyshipTemplate {
  sp_account_id: number;
  marketplace_id: string;
  marketplace_code: string | null;
  label: "easyship" | "mfn_standard";
  template_value: string;
  notes: string | null;
  updated_at: string;
}

export interface AmazonRoutingRule {
  sp_account_id: number;
  marketplace_id: string;
  marketplace_code: string | null;
  warehouse_id: number;
  warehouse_code: string;
  warehouse_name: string;
  warehouse_sort_order: number;
  mode: "easyship" | "mfn";
  handling_time: number | null;
  is_active: boolean;
  updated_at: string;
}

export async function fetchAmazonEasyshipTemplates(spAccountId: number) {
  return api<{ rows: AmazonEasyshipTemplate[] }>(
    `/api/amazon/easyship/templates?sp_account_id=${spAccountId}`,
  );
}

export async function upsertAmazonEasyshipTemplate(body: {
  sp_account_id: number;
  marketplace_id: string;
  label: "easyship" | "mfn_standard";
  template_value: string;
  notes?: string | null;
}) {
  return api<{ ok: true }>(`/api/amazon/easyship/templates`, { method: "PUT", body });
}

export async function deleteAmazonEasyshipTemplate(opts: {
  sp_account_id: number;
  marketplace_id: string;
  label: "easyship" | "mfn_standard";
}) {
  const qs = `?sp_account_id=${opts.sp_account_id}&marketplace_id=${encodeURIComponent(opts.marketplace_id)}&label=${opts.label}`;
  return api<{ ok: true }>(`/api/amazon/easyship/templates${qs}`, { method: "DELETE" });
}

export async function fetchAmazonRoutingRules(spAccountId: number) {
  return api<{ rows: AmazonRoutingRule[] }>(
    `/api/amazon/easyship/rules?sp_account_id=${spAccountId}`,
  );
}

export async function upsertAmazonRoutingRule(body: {
  sp_account_id: number;
  marketplace_id: string;
  warehouse_id: number;
  mode: "easyship" | "mfn";
  handling_time: number | null;
  is_active?: boolean;
}) {
  return api<{ ok: true }>(`/api/amazon/easyship/rules`, { method: "PUT", body });
}

export async function deleteAmazonRoutingRule(opts: {
  sp_account_id: number;
  marketplace_id: string;
  warehouse_id: number;
}) {
  const qs = `?sp_account_id=${opts.sp_account_id}&marketplace_id=${encodeURIComponent(opts.marketplace_id)}&warehouse_id=${opts.warehouse_id}`;
  return api<{ ok: true }>(`/api/amazon/easyship/rules${qs}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// SIU → Amazon stock-sync trigger + run-historiek
// ---------------------------------------------------------------------------

export interface AmazonSiuSyncRun {
  id: number;
  sp_account_id: number;
  marketplace_id: string;
  marketplace_code: string | null;
  triggered_by: string;
  dry_run: boolean;
  started_at: string;
  finished_at: string | null;
  n_matched: number;
  n_stock_changed: number;
  n_auto_routed: number;
  n_pushed: number;
  n_skipped: number;
  n_failed: number;
  duration_ms: number | null;
  error: string | null;
}

export interface AmazonSiuSyncResult extends AmazonSiuSyncRun {
  run_id: number;
  details: Array<{
    seller_sku: string;
    per_warehouse: Array<{
      warehouse_code: string;
      old_quantity: number | null;
      new_quantity: number;
      changed: boolean;
    }>;
    auto_route?: {
      triggered: boolean;
      resolved: boolean;
      pushed: boolean;
      push_success: boolean | null;
      submission_id: string | null;
      reason: string;
      error: string | null;
    };
  }>;
}

export async function runAmazonSiuSync(body: {
  sp_account_id: number;
  marketplace_id: string;
  dry_run?: boolean;
  force_all?: boolean;
  filter_skus?: string[];
}) {
  return api<AmazonSiuSyncResult>(`/api/amazon/siu-sync/run`, { method: "POST", body });
}

export interface AmazonSiuSyncStatus {
  account_count: number;
  enabled_sku_count: number;
  last_sync_at: string | null;
  last_sync_error: string | null;
  last_sync_pushed: number;
  last_sync_failed: number;
  last_sync_marketplace: string | null;
}

export async function fetchAmazonSiuSyncStatus() {
  return api<AmazonSiuSyncStatus>(`/api/amazon/siu-sync/status`);
}

// ---------------------------------------------------------------------------
// Easy Ship exclusions (per-SKU "force MFN" — bv. koerier-producten)
// ---------------------------------------------------------------------------

export interface AmazonEasyshipExclusion {
  sp_account_id: number;
  marketplace_id: string;
  marketplace_code: string | null;
  seller_sku: string;
  reason: string | null;
  source: string;
  notes: string | null;
}

export interface AmazonEasyshipImport {
  id: number;
  marketplace_ids: string[];
  filename: string | null;
  n_total_rows: number;
  n_matched_skus: number;
  n_inserted: number;
  n_updated: number;
  n_unmatched: number;
  imported_at: string;
}

export interface AmazonEasyshipCsvImportResult {
  filename: string;
  n_total_rows: number;
  n_koerier: number;
  n_pakket: number;
  n_other: number;
  n_matched_in_listings: number;
  n_unmatched: number;
  unmatched_skus: string[];
  koerier_skus: string[];
}

export async function fetchAmazonEasyshipExclusions(opts: { sp_account_id: number; marketplace_id?: string }) {
  const qs = `?sp_account_id=${opts.sp_account_id}${opts.marketplace_id ? `&marketplace_id=${encodeURIComponent(opts.marketplace_id)}` : ""}`;
  return api<{ rows: AmazonEasyshipExclusion[] }>(`/api/amazon/easyship/exclusions${qs}`);
}

export async function addAmazonEasyshipExclusions(body: {
  sp_account_id: number;
  marketplace_ids: string[];
  seller_skus: string[];
  reason?: string;
}) {
  return api<{ n_inserted: number; n_updated: number; n_total: number }>(
    `/api/amazon/easyship/exclusions`, { method: "POST", body },
  );
}

export async function deleteAmazonEasyshipExclusion(opts: {
  sp_account_id: number;
  marketplace_id: string;
  seller_sku: string;
}) {
  const qs = `?sp_account_id=${opts.sp_account_id}&marketplace_id=${encodeURIComponent(opts.marketplace_id)}&seller_sku=${encodeURIComponent(opts.seller_sku)}`;
  return api<{ ok: true }>(`/api/amazon/easyship/exclusions${qs}`, { method: "DELETE" });
}

export async function importAmazonEasyshipExclusionsCsv(
  opts: { sp_account_id: number; marketplace_ids: string[]; file: File },
) {
  const fd = new FormData();
  fd.append("csv", opts.file);
  fd.append("sp_account_id", String(opts.sp_account_id));
  fd.append("marketplace_ids", JSON.stringify(opts.marketplace_ids));
  // Bypass api() helper because we send multipart — maar gebruik juiste key
  const token = getToken();
  const res = await fetch(`/api/amazon/easyship/exclusions/import`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: fd,
  });
  if (!res.ok) {
    let body: { error?: string } = {};
    try { body = await res.json(); } catch { /* */ }
    throw new ApiError(res.status, body, body.error || `HTTP ${res.status}`);
  }
  return res.json() as Promise<AmazonEasyshipCsvImportResult>;
}

export async function fetchAmazonEasyshipImports(spAccountId: number) {
  return api<{ rows: AmazonEasyshipImport[] }>(
    `/api/amazon/easyship/exclusions/imports?sp_account_id=${spAccountId}`,
  );
}

export async function fetchAmazonSiuSyncRuns(opts: { sp_account_id: number; marketplace_id?: string }) {
  const qs = `?sp_account_id=${opts.sp_account_id}${opts.marketplace_id ? `&marketplace_id=${encodeURIComponent(opts.marketplace_id)}` : ""}`;
  return api<{ rows: AmazonSiuSyncRun[] }>(`/api/amazon/siu-sync/runs${qs}`);
}

export async function fetchAmazonSiuSyncRunDetails(runId: number) {
  return api<{
    rows: Array<{
      seller_sku: string;
      warehouse_code: string;
      old_quantity: number | null;
      new_quantity: number;
      auto_routed: boolean;
      push_success: boolean | null;
      submission_id: string | null;
      reason: string | null;
    }>;
  }>(`/api/amazon/siu-sync/runs/${runId}/details`);
}

// ===========================================================================
// Mediamarkt (Mirakl) — accounts + test
// ===========================================================================

export interface MediamarktAccount {
  id: number;
  name: string;
  shop_id: string | null;
  base_url: string;
  marketplace: string;
  is_active: boolean;
  last_connect_at: string | null;
  last_error: string | null;
  created_at: string;
}

export interface MediamarktTestResult {
  ok: boolean;
  account: { id: number; name: string; marketplace: string };
  shop_info?: {
    shop_id: number | string | null;
    shop_name: string | null;
    premium_state: string | null;
    currency: string | null;
  };
  raw?: unknown;
  error?: string;
  message?: string;
}

export async function fetchMediamarktAccounts() {
  return api<{ accounts: MediamarktAccount[] }>("/api/mediamarkt/accounts");
}

export async function createMediamarktAccount(body: {
  name: string;
  marketplace: string;
  api_key: string;
  base_url?: string;
}) {
  return api<{ id: number }>("/api/mediamarkt/accounts", { method: "POST", body });
}

export async function updateMediamarktAccount(id: number, body: Partial<{
  name: string;
  marketplace: string;
  base_url: string;
  api_key: string;
  is_active: boolean;
}>) {
  return api<{ ok: boolean; updated: string[] }>(`/api/mediamarkt/accounts/${id}`, {
    method: "PUT",
    body,
  });
}

export async function deleteMediamarktAccount(id: number, hard = false) {
  return api<void>(`/api/mediamarkt/accounts/${id}${hard ? "?hard=true" : ""}`, {
    method: "DELETE",
  });
}

export async function testMediamarktAccount(id: number) {
  return api<MediamarktTestResult>(`/api/mediamarkt/accounts/${id}/test`, { method: "POST" });
}

// === Mediamarkt Listings ===

export interface MediamarktListing {
  id: number;
  account_id: number;
  offer_id: number | null;
  shop_sku: string;
  product_sku: string | null;
  product_title: string | null;
  price: string | null;
  discount_price: string | null;
  currency: string | null;
  quantity: number | null;
  state_code: string | null;
  active: boolean | null;
  leadtime_to_ship: number | null;
  last_updated_mirakl: string | null;
  synced_at: string;
}

export interface MediamarktOfferChangeRow {
  id: number;
  account_id: number;
  offer_id: number;
  shop_sku: string;
  old_price: string | null;
  new_price: string | null;
  old_quantity: number | null;
  new_quantity: number | null;
  old_leadtime: number | null;
  new_leadtime: number | null;
  success: boolean;
  api_status: number | null;
  api_error: string | null;
  changed_by_id: number | null;
  triggered_by: string;
  changed_at: string;
}

export interface MediamarktListingsSyncStateRow {
  account_id: number;
  last_sync_at: string | null;
  last_sync_count: number;
  last_sync_error: string | null;
}

export async function fetchMediamarktListings(filter: {
  account_id?: number;
  state_code?: string;
  active_only?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
} = {}) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  }
  const qs = params.toString();
  return api<{ total: number; limit: number; offset: number; rows: MediamarktListing[] }>(
    `/api/mediamarkt/listings${qs ? "?" + qs : ""}`,
  );
}

export async function fetchMediamarktListingsSyncState() {
  return api<{ rows: MediamarktListingsSyncStateRow[] }>("/api/mediamarkt/listings/sync-state");
}

export async function triggerMediamarktListingsSync(accountId: number) {
  return api<{
    ok: boolean;
    account_id: number;
    fetched: number;
    upserted: number;
    durationMs: number;
    error?: string;
  }>(`/api/mediamarkt/sync/listings/${accountId}`, { method: "POST" });
}

export async function updateMediamarktOffer(offerId: number, body: {
  account_id: number;
  price?: number;
  discount_price?: number | null;
  quantity?: number;
  leadtime_to_ship?: number;
}) {
  return api<{
    ok: boolean;
    pending?: boolean;
    error?: string;
    import_id: number | null;
    import_status: string | null;
    applied: {
      price: number | null;
      quantity: number | null;
      leadtime_to_ship: number | null;
      discount_price: number | null;
    };
  }>(`/api/mediamarkt/listings/${offerId}/update`, { method: "POST", body });
}

// === Bol Retailer API ===

export interface BolAccount {
  id: number;
  name: string;
  label: "nl" | "be";
  client_id: string;
  base_url: string;
  token_url: string;
  is_active: boolean;
  last_connect_at: string | null;
  last_error: string | null;
  created_at: string;
}

export interface BolTestResult {
  ok: boolean;
  account: { id: number; name: string; label: string };
  shop_info?: {
    shop_name: string | null;
    email: string | null;
    vat_number: string | null;
    kvk_number: string | null;
    registration_date: string | null;
  };
  raw?: unknown;
  error?: string;
  message?: string;
}

export async function fetchBolAccounts() {
  return api<{ accounts: BolAccount[] }>("/api/bol/accounts");
}

export async function createBolAccount(body: {
  name: string;
  label: "nl" | "be";
  client_id: string;
  client_secret: string;
  base_url?: string;
  token_url?: string;
}) {
  return api<{ id: number }>("/api/bol/accounts", { method: "POST", body });
}

export async function updateBolAccount(id: number, body: Partial<{
  name: string;
  client_id: string;
  client_secret: string;
  base_url: string;
  token_url: string;
  is_active: boolean;
}>) {
  return api<{ ok: boolean; updated: string[] }>(`/api/bol/accounts/${id}`, {
    method: "PUT",
    body,
  });
}

export async function deleteBolAccount(id: number, hard = false) {
  return api<void>(`/api/bol/accounts/${id}${hard ? "?hard=true" : ""}`, {
    method: "DELETE",
  });
}

export async function testBolAccount(id: number) {
  return api<BolTestResult>(`/api/bol/accounts/${id}/test`, { method: "POST" });
}

// === Bol Listings ===

export interface BolListing {
  id: number;
  account_id: number;
  offer_id: string;
  ean: string | null;
  reference_code: string | null;
  condition_name: string | null;
  price: string | null;
  stock_amount: number | null;
  fulfilment_method: string | null;
  fulfilment_delivery_code: string | null;
  on_hold_by_retailer: boolean | null;
  synced_at: string;
}

export interface BolOfferChangeRow {
  id: number;
  account_id: number;
  offer_id: string;
  reference_code: string | null;
  old_price: string | null;
  new_price: string | null;
  old_stock: number | null;
  new_stock: number | null;
  old_delivery_code: string | null;
  new_delivery_code: string | null;
  success: boolean;
  api_status: number | null;
  api_error: string | null;
  process_status_id: string | null;
  changed_by_id: number | null;
  triggered_by: string;
  changed_at: string;
}

export interface BolListingsSyncStateRow {
  account_id: number;
  last_sync_at: string | null;
  last_sync_count: number;
  last_sync_error: string | null;
}

export async function fetchBolListings(filter: {
  account_id?: number;
  search?: string;
  limit?: number;
  offset?: number;
} = {}) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  }
  const qs = params.toString();
  return api<{ total: number; limit: number; offset: number; rows: BolListing[] }>(
    `/api/bol/listings${qs ? "?" + qs : ""}`,
  );
}

export async function fetchBolListingsSyncState() {
  return api<{ rows: BolListingsSyncStateRow[] }>("/api/bol/listings/sync-state");
}

export async function triggerBolListingsSync(accountId: number) {
  return api<{
    ok: boolean;
    account_id: number;
    fetched: number;
    upserted: number;
    durationMs: number;
    export_id?: string;
    process_status_id?: string;
    error?: string;
  }>(`/api/bol/sync/listings/${accountId}`, { method: "POST" });
}

export async function updateBolOffer(offerId: string, body: {
  account_id: number;
  price?: number;
  stock_amount?: number;
  fulfilment_delivery_code?: string;
  on_hold_by_retailer?: boolean;
}) {
  return api<{
    ok: boolean;
    pending?: boolean;
    error?: string;
    process_status_id: string | null;
    process_status_ids?: { price?: string; stock?: string; offer?: string };
    applied: {
      price: number | null;
      stock_amount: number | null;
      fulfilment_delivery_code: string | null;
      on_hold_by_retailer: boolean | null;
    };
  }>(`/api/bol/listings/${encodeURIComponent(offerId)}/update`, {
    method: "POST",
    body,
  });
}

export async function fetchBolProcessStatus(processStatusId: string, accountId: number) {
  return api<{
    ok: boolean;
    pending: boolean;
    process_status_id: string | number;
    status: string;
    error_message?: string;
  }>(`/api/bol/process-status/${encodeURIComponent(processStatusId)}?account_id=${accountId}`);
}

export async function fetchBolOfferHistory(offerId: string) {
  return api<{ rows: BolOfferChangeRow[] }>(
    `/api/bol/listings/${encodeURIComponent(offerId)}/history`,
  );
}

export async function fetchMediamarktImportStatus(importId: number, accountId: number) {
  return api<{
    ok: boolean;
    pending: boolean;
    import_id: number;
    status: string;
    lines_read?: number;
    lines_in_success?: number;
    lines_in_error?: number;
    errors?: string[];
  }>(`/api/mediamarkt/imports/${importId}?account_id=${accountId}`);
}

// === Kaufland Seller API ===

export type KauflandStorefront = "de" | "at" | "cz" | "sk" | "pl" | "it" | "fr";
export const ALL_KAUFLAND_STOREFRONTS: KauflandStorefront[] = [
  "de", "at", "cz", "sk", "pl", "it", "fr",
];

export interface KauflandAccount {
  id: number;
  name: string;
  client_key_masked: string;
  base_url: string;
  storefronts: KauflandStorefront[];
  is_active: boolean;
  last_connect_at: string | null;
  last_error: string | null;
  created_at: string;
}

export interface KauflandStorefrontPingResult {
  storefront: KauflandStorefront;
  ok: boolean;
  http_status: number;
  unit_count?: number;
  error?: string;
}

export interface KauflandTestResult {
  ok: boolean;
  account?: { id: number; name: string };
  storefronts_available?: string[];
  storefronts_enabled?: string[];
  storefronts_pinged?: KauflandStorefrontPingResult[];
  total_units?: number;
  error?: string;
  message?: string;
}

export async function fetchKauflandAccounts() {
  return api<{ accounts: KauflandAccount[] }>("/api/kaufland/accounts");
}

export async function createKauflandAccount(body: {
  name: string;
  client_key: string;
  secret_key: string;
  storefronts?: string[];
  base_url?: string;
}) {
  return api<{ id: number }>("/api/kaufland/accounts", { method: "POST", body });
}

export async function updateKauflandAccount(id: number, body: Partial<{
  name: string;
  storefronts: string[];
  secret_key: string;
  base_url: string;
  is_active: boolean;
}>) {
  return api<{ ok: boolean; updated: string[] }>(`/api/kaufland/accounts/${id}`, {
    method: "PUT",
    body,
  });
}

export async function deleteKauflandAccount(id: number, hard = false) {
  return api<void>(`/api/kaufland/accounts/${id}${hard ? "?hard=true" : ""}`, {
    method: "DELETE",
  });
}

export async function testKauflandAccount(id: number) {
  return api<KauflandTestResult>(`/api/kaufland/accounts/${id}/test`, { method: "POST" });
}

// === Kaufland Listings ===

export interface KauflandListing {
  id: number;
  account_id: number;
  storefront: KauflandStorefront;
  id_unit: string;
  id_offer: string | null;
  id_product: string | null;
  ean: string | null;
  title: string | null;
  price: string | null;
  listing_price: string | null;
  currency: string | null;
  amount: number | null;
  delivery_time: number | null;
  handling_time: number | null;
  condition: string | null;
  listing_status: string | null;
  active: boolean | null;
  last_changed_kaufland: string | null;
  synced_at: string;
}

export interface KauflandOfferChangeRow {
  id: number;
  account_id: number;
  storefront: string;
  id_unit: string;
  id_offer: string | null;
  old_price: string | null;
  new_price: string | null;
  old_amount: number | null;
  new_amount: number | null;
  old_delivery: number | null;
  new_delivery: number | null;
  success: boolean;
  api_status: number | null;
  api_error: string | null;
  changed_by_id: number | null;
  triggered_by: string;
  changed_at: string;
}

export interface KauflandListingsSyncStateRow {
  account_id: number;
  storefront: string;
  last_sync_at: string | null;
  last_sync_count: number;
  last_sync_error: string | null;
}

export async function fetchKauflandListings(filter: {
  account_id?: number;
  storefront?: string;
  listing_status?: string;
  search?: string;
  limit?: number;
  offset?: number;
} = {}) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  }
  const qs = params.toString();
  return api<{ total: number; limit: number; offset: number; rows: KauflandListing[] }>(
    `/api/kaufland/listings${qs ? "?" + qs : ""}`,
  );
}

export async function fetchKauflandListingsSyncState() {
  return api<{ rows: KauflandListingsSyncStateRow[] }>("/api/kaufland/listings/sync-state");
}

export async function triggerKauflandListingsSync(accountId: number) {
  return api<{
    ok: boolean;
    account_id: number;
    storefronts: Array<{
      storefront: KauflandStorefront;
      fetched: number;
      upserted: number;
      error?: string;
    }>;
    durationMs: number;
  }>(`/api/kaufland/sync/listings/${accountId}`, { method: "POST" });
}

export async function updateKauflandOffer(idUnit: string, body: {
  account_id: number;
  storefront: KauflandStorefront;
  price?: number;
  listing_price?: number | null;
  amount?: number;
  handling_time?: number;
}) {
  return api<{
    ok: boolean;
    error?: string;
    api_status: number;
    applied: {
      price: number | null;
      amount: number | null;
      handling_time: number | null;
      listing_price: number | null;
    };
  }>(`/api/kaufland/listings/${encodeURIComponent(idUnit)}/update`, {
    method: "POST",
    body,
  });
}

export async function fetchKauflandOfferHistory(idUnit: string) {
  return api<{ rows: KauflandOfferChangeRow[] }>(
    `/api/kaufland/listings/${encodeURIComponent(idUnit)}/history`,
  );
}

// === Bol SIU stock-sync ===

export const BOL_DELIVERY_CODES = [
  "24uurs-23", "24uurs-22", "24uurs-21", "24uurs-20",
  "1-2d", "2-3d", "3-5d", "4-8d", "1-8d",
  "VVB", "MijnLeverbelofte",
] as const;
export type BolDeliveryCode = (typeof BOL_DELIVERY_CODES)[number];

/** Friendly labels voor dropdown — Bol's eigen UI-strings. */
export const BOL_DELIVERY_CODE_LABELS: Record<BolDeliveryCode, string> = {
  "24uurs-23": "Binnen 24 uur (besteld voor 23:00)",
  "24uurs-22": "Binnen 24 uur (besteld voor 22:00)",
  "24uurs-21": "Binnen 24 uur (besteld voor 21:00)",
  "24uurs-20": "Binnen 24 uur (besteld voor 20:00)",
  "1-2d": "1-2 dagen",
  "2-3d": "2-3 dagen",
  "3-5d": "3-5 dagen",
  "4-8d": "4-8 dagen",
  "1-8d": "1-8 dagen",
  "VVB": "VVB (Verzenden via Bol)",
  "MijnLeverbelofte": "Mijn Leverbelofte",
};

export interface BolStockConfig {
  siu_warehouse_names: string[];
  stock_buffer: number;
  max_stock: number | null;
  push_enabled: boolean;
  warehouse_delivery_codes: Record<string, BolDeliveryCode>;
  last_sync_at: string | null;
  last_sync_error: string | null;
}

export interface BolStockListing {
  id: number;
  offer_id: string;
  ean: string | null;
  reference_code: string | null;
  title: string | null;
  stock_amount: number | null;
  fulfilment_method: string | null;
  fulfilment_delivery_code: string | null;
  stock_managed_by_retailer: boolean | null;
  last_pushed_stock: number | null;
  last_pushed_at: string | null;
  siu_warehouse_override: string[] | null;
  product_sku: string | null;
  product_matched: boolean;
}

export interface BolStockRun {
  id: number;
  triggered_by: string;
  dry_run: boolean;
  started_at: string;
  finished_at: string | null;
  n_matched: number;
  n_pushed: number;
  n_skipped: number;
  n_failed: number;
  n_lvb_fallback: number;
  error: string | null;
}

export interface BolStockSyncResult {
  account_id: number;
  run_id: number;
  triggered_by: string;
  dry_run: boolean;
  n_matched: number;
  n_pushed: number;
  n_skipped: number;
  n_failed: number;
  n_lvb_fallback: number;
  duration_ms: number;
  details: Array<{
    offer_id: string; ean: string | null; sku: string | null;
    old_amount: number | null; new_amount: number | null;
    old_method: string | null; new_method: string | null;
    old_delivery_code: string | null; new_delivery_code: string | null;
    status: "pushed" | "skipped" | "failed" | "lvb_fallback";
    reason?: string; error?: string;
  }>;
  error?: string;
}

export function fetchBolStockConfig(accountId: number) {
  return api<{ ok: true; config: BolStockConfig }>(`/api/bol/accounts/${accountId}/stock-config`);
}

export function saveBolStockConfig(accountId: number, body: {
  siu_warehouse_names: string[]; stock_buffer: number; max_stock: number | null;
  push_enabled: boolean; warehouse_delivery_codes: Record<string, BolDeliveryCode>;
}) {
  return api<{ ok: true; config: BolStockConfig }>(`/api/bol/accounts/${accountId}/stock-config`, {
    method: "PUT", body,
  });
}

export function fetchBolStockListings(accountId: number, opts: {
  search?: string; limit?: number; offset?: number;
} = {}) {
  const q = new URLSearchParams();
  if (opts.search) q.set("search", opts.search);
  if (opts.limit) q.set("limit", String(opts.limit));
  if (opts.offset) q.set("offset", String(opts.offset));
  return api<{ ok: true; rows: BolStockListing[] }>(
    `/api/bol/accounts/${accountId}/stock-listings${q.toString() ? "?" + q.toString() : ""}`,
  );
}

export function saveBolListingOverride(listingId: number, override: string[] | null) {
  return api<{ ok: true }>(`/api/bol/stock-listings/${listingId}/override`, {
    method: "PATCH", body: { siu_warehouse_override: override },
  });
}

export function triggerBolStockSync(accountId: number, dryRun = true) {
  return api<BolStockSyncResult>(`/api/bol/accounts/${accountId}/stock-sync`, {
    method: "POST", body: { dry_run: dryRun },
  });
}

export function fetchBolStockRuns(accountId: number, limit = 20) {
  return api<{ ok: true; runs: BolStockRun[] }>(
    `/api/bol/accounts/${accountId}/stock-sync/runs?limit=${limit}`,
  );
}

export function fetchBolSiuWarehouses() {
  return api<{ ok: true; warehouses: SiuWarehouseSummary[] }>(`/api/bol/siu-warehouses`);
}

// === Bol Force-MijnLeverbelofte lijst ===

export interface BolForceMlbItem {
  id: number;
  sku: string | null;
  ean: string | null;
  reason: string | null;
  added_at: string;
}

export function fetchBolForceMlb(accountId: number) {
  return api<{ ok: true; items: BolForceMlbItem[] }>(`/api/bol/accounts/${accountId}/force-mlb`);
}

export function addBolForceMlb(accountId: number, body: {
  sku?: string | null; ean?: string | null; reason?: string;
}) {
  return api<{ ok: true }>(`/api/bol/accounts/${accountId}/force-mlb`, {
    method: "POST", body,
  });
}

export function removeBolForceMlb(accountId: number, itemId: number) {
  return api<{ ok: true }>(`/api/bol/accounts/${accountId}/force-mlb/${itemId}`, {
    method: "DELETE",
  });
}

export interface BolForceMlbBulkResult {
  total: number; added: number; already_exists: number; invalid: number; error: number;
}

export function bulkAddBolForceMlb(accountId: number, rows: Array<{
  sku?: string | null; ean?: string | null; reason?: string;
}>) {
  return api<{
    ok: true; counts: BolForceMlbBulkResult;
    results: Array<{ sku: string | null; ean: string | null; status: string; error?: string }>;
  }>(`/api/bol/accounts/${accountId}/force-mlb/bulk`, {
    method: "POST", body: { rows },
  });
}

// === Bol Prestatie-force levertijd lijst ===
// Vaste delivery_code per SKU/EAN — overschrijft ALLES (voorraad, magazijn-mapping,
// force-MLB). Use case: SKU's met vaste lever-belofte ongeacht stock-status.

export interface BolPrestatieForceItem {
  id: number;
  account_id: number;
  sku: string | null;
  ean: string | null;
  delivery_code: string;
  reason: string | null;
  added_at: string;
}

export interface BolPrestatieForceListResp {
  items: BolPrestatieForceItem[];
}

/** Lijst van delivery-codes die geforceerd kunnen worden via Prestatie-force.
 *  Volgorde van snelst → langzaamst. VVB + MijnLeverbelofte staan bovenaan
 *  (special) gevolgd door 24uurs varianten en standaard dag-ranges. */
export const BOL_DELIVERY_CODES_FOR_PRESTATIE = [
  "VVB",
  "MijnLeverbelofte",
  "24uurs-23",
  "24uurs-22",
  "24uurs-21",
  "24uurs-20",
  "24uurs-19",
  "24uurs-18",
  "24uurs-17",
  "24uurs-16",
  "24uurs-15",
  "24uurs-14",
  "24uurs-13",
  "24uurs-12",
  "1-2d",
  "2-3d",
  "3-5d",
  "4-8d",
  "1-8d",
] as const;

export type BolPrestatieDeliveryCode = (typeof BOL_DELIVERY_CODES_FOR_PRESTATIE)[number];

export function fetchBolPrestatieForce(accountId: number) {
  return api<{ ok: true } & BolPrestatieForceListResp>(
    `/api/bol/accounts/${accountId}/prestatie-force`,
  );
}

export function addBolPrestatieForce(
  accountId: number,
  body: {
    sku?: string | null;
    ean?: string | null;
    delivery_code: string;
    reason?: string;
  },
) {
  return api<{ ok: true }>(`/api/bol/accounts/${accountId}/prestatie-force`, {
    method: "POST",
    body,
  });
}

export function removeBolPrestatieForce(accountId: number, id: number) {
  return api<{ ok: true }>(
    `/api/bol/accounts/${accountId}/prestatie-force/${id}`,
    { method: "DELETE" },
  );
}

export interface BolPrestatieForceBulkResult {
  total: number; added: number; already_exists: number; invalid: number; error: number;
}

export function bulkAddBolPrestatieForce(
  accountId: number,
  rows: Array<{
    sku?: string | null;
    ean?: string | null;
    delivery_code: string;
    reason?: string;
  }>,
) {
  return api<{
    ok: true;
    counts: BolPrestatieForceBulkResult;
    results: Array<{ sku: string | null; ean: string | null; delivery_code: string; status: string; error?: string }>;
  }>(`/api/bol/accounts/${accountId}/prestatie-force/bulk`, {
    method: "POST",
    body: { rows },
  });
}

// === Kaufland SIU stock-sync ===

export interface KauflandStockConfig {
  siu_warehouse_names: string[];
  stock_buffer: number;
  max_stock: number | null;
  push_enabled: boolean;
  warehouse_leadtimes: Record<string, number>;
  last_sync_at: string | null;
  last_sync_error: string | null;
  updated_at: string;
}

export interface KauflandStockListing {
  id: number;
  storefront: string;
  id_unit: string;
  id_offer: string | null;
  ean: string | null;
  title: string | null;
  amount: number | null;
  last_pushed_stock: number | null;
  last_pushed_at: string | null;
  siu_warehouse_override: string[] | null;
  product_sku: string | null;
  product_matched: boolean;
  siu_unreserved_total: number | null;
}

export interface KauflandStockRun {
  id: number;
  triggered_by: string;
  triggered_by_id: number | null;
  dry_run: boolean;
  started_at: string;
  finished_at: string | null;
  n_matched: number;
  n_pushed: number;
  n_skipped: number;
  n_failed: number;
  error: string | null;
}

export interface SiuWarehouseSummary {
  name: string;
  n_products: number;
  total_stock: number;
}

export interface KauflandStockSyncResult {
  account_id: number;
  run_id: number;
  triggered_by: string;
  dry_run: boolean;
  n_matched: number;
  n_pushed: number;
  n_skipped: number;
  n_failed: number;
  duration_ms: number;
  details: Array<{
    id_unit: string; storefront: string; sku: string | null; ean: string | null;
    old_amount: number | null; new_amount: number;
    status: "pushed" | "skipped" | "failed"; reason?: string; error?: string;
  }>;
  error?: string;
}

export function fetchKauflandStockConfig(accountId: number) {
  return api<{ config: KauflandStockConfig }>(`/api/kaufland/accounts/${accountId}/stock-config`);
}

export function saveKauflandStockConfig(accountId: number, body: {
  siu_warehouse_names: string[]; stock_buffer: number; max_stock: number | null;
  push_enabled: boolean; warehouse_leadtimes: Record<string, number>;
}) {
  return api<{ ok: true; config: KauflandStockConfig }>(`/api/kaufland/accounts/${accountId}/stock-config`, {
    method: "PUT", body,
  });
}

export function fetchKauflandStockListings(accountId: number, opts: {
  storefront?: string; search?: string; limit?: number; offset?: number;
} = {}) {
  const q = new URLSearchParams();
  if (opts.storefront) q.set("storefront", opts.storefront);
  if (opts.search) q.set("search", opts.search);
  if (opts.limit) q.set("limit", String(opts.limit));
  if (opts.offset) q.set("offset", String(opts.offset));
  return api<{ total: number; listings: KauflandStockListing[] }>(
    `/api/kaufland/accounts/${accountId}/stock-listings${q.toString() ? "?" + q.toString() : ""}`,
  );
}

export function saveKauflandListingOverride(listingId: number, override: string[] | null) {
  return api<{ ok: true }>(`/api/kaufland/listings/${listingId}/stock-override`, {
    method: "PATCH", body: { siu_warehouse_override: override },
  });
}

export function triggerKauflandStockSync(accountId: number, dryRun = false) {
  return api<KauflandStockSyncResult>(
    `/api/kaufland/accounts/${accountId}/stock-sync${dryRun ? "?dryRun=1" : ""}`,
    { method: "POST" },
  );
}

export function fetchKauflandStockRuns(accountId: number, limit = 50) {
  return api<{ runs: KauflandStockRun[] }>(
    `/api/kaufland/accounts/${accountId}/stock-sync/runs?limit=${limit}`,
  );
}

export function fetchKauflandSiuWarehouses() {
  return api<{ warehouses: SiuWarehouseSummary[] }>(`/api/kaufland/siu-warehouses`);
}

// === Mediamarkt SIU stock-sync ===

export interface MediamarktStockConfig {
  siu_warehouse_names: string[];
  stock_buffer: number;
  max_stock: number | null;
  push_enabled: boolean;
  warehouse_leadtimes: Record<string, number>;
  last_sync_at: string | null;
  last_sync_error: string | null;
  updated_at: string;
}

export interface MediamarktStockRun {
  id: number;
  triggered_by: string;
  triggered_by_id: number | null;
  dry_run: boolean;
  started_at: string;
  finished_at: string | null;
  n_matched: number;
  n_pushed: number;
  n_skipped: number;
  n_failed: number;
  error: string | null;
}

export interface MediamarktStockSyncResult {
  account_id: number;
  run_id: number;
  triggered_by: string;
  dry_run: boolean;
  n_matched: number;
  n_pushed: number;
  n_skipped: number;
  n_failed: number;
  duration_ms: number;
  import_id?: number;
  details: Array<{
    offer_id: string; shop_sku: string; sku: string | null; ean: string | null;
    old_quantity: number | null; new_quantity: number;
    old_leadtime: number | null; new_leadtime: number | null;
    status: "pushed" | "skipped" | "failed"; reason?: string; error?: string;
  }>;
  error?: string;
}

export function fetchMediamarktStockConfig(accountId: number) {
  return api<{ config: MediamarktStockConfig }>(`/api/mediamarkt/accounts/${accountId}/stock-config`);
}

export function saveMediamarktStockConfig(accountId: number, body: {
  siu_warehouse_names: string[]; stock_buffer: number; max_stock: number | null;
  push_enabled: boolean; warehouse_leadtimes: Record<string, number>;
}) {
  return api<{ ok: true; config: MediamarktStockConfig }>(`/api/mediamarkt/accounts/${accountId}/stock-config`, {
    method: "PUT", body,
  });
}

export function triggerMediamarktStockSync(accountId: number, dryRun = false) {
  return api<MediamarktStockSyncResult>(
    `/api/mediamarkt/accounts/${accountId}/stock-sync${dryRun ? "?dryRun=1" : ""}`,
    { method: "POST" },
  );
}

export function fetchMediamarktStockRuns(accountId: number, limit = 50) {
  return api<{ runs: MediamarktStockRun[] }>(
    `/api/mediamarkt/accounts/${accountId}/stock-sync/runs?limit=${limit}`,
  );
}

export function fetchMediamarktSiuWarehouses() {
  return api<{ warehouses: SiuWarehouseSummary[] }>(`/api/mediamarkt/siu-warehouses`);
}

// === Shopify Admin API ===

export interface ShopifyAccount {
  id: number;
  name: string;
  label: string;
  shop_domain: string;
  client_id_masked: string;
  api_version: string;
  is_active: boolean;
  last_connect_at: string | null;
  last_error: string | null;
  created_at: string;
}

export interface ShopifyTestResult {
  ok: boolean;
  account?: { id: number; name: string; label: string };
  shop_info?: {
    name: string;
    email: string | null;
    myshopifyDomain: string;
    primaryDomain: { host: string; url: string };
    currencyCode: string;
    plan: { displayName: string } | null;
    ianaTimezone: string;
  };
  error?: string;
  message?: string;
}

export type ShopifySiuStockSource = "none" | "stock_opslag" | "stock_online" | "nod";

export interface ShopifyLocation {
  id: number;
  location_gid: string;
  legacy_id: number | null;
  name: string;
  is_active: boolean;
  is_primary: boolean;
  address_city: string | null;
  address_country: string | null;
  delivery_rule_type: "none" | "cutoff" | "leadtime";
  delivery_cutoff_time: string | null;     // "HH:MM"
  delivery_lead_days: number | null;
  delivery_label: string | null;            // metafield-key (= "denbosch", "nod")
  siu_stock_source: ShopifySiuStockSource; // legacy: kolom-niveau SIU-source
  siu_warehouse_name: string | null;        // legacy: single warehouse
  siu_warehouse_names: string[] | null;     // voorkeur: meerdere warehouses (SOM)
  warehouse_leadtimes: Record<string, number>; // { "VVB": 1, "NOD": 8 } — per-warehouse leadtime in werkdagen
}

export interface SiuWarehouse {
  name: string;
  managed_by_retailer: boolean;
  last_seen_at: string;
}

export async function fetchSiuWarehouses() {
  return api<{ rows: SiuWarehouse[] }>("/api/shopify/siu-warehouses");
}

// === Levertijdenswitch ===

export interface LevertijdMarketplaceCard {
  key: "mediamarkt" | "bol" | "amazon" | "kaufland" | "pro-cooking" | "pro-witgoed";
  name: string;
  initials: string;
  n_accounts: number;
  n_pending: number;
  status: "live" | "info_only";
}

export interface LevertijdMmRow {
  shop_sku: string;
  account_id: number;
  marketplace: string;       // bv "NL"
  product_title: string | null;
  leadtime_to_ship: number | null;
  pending_leadtime: number | null;
  pending_leadtime_at: string | null;
  pending_leadtime_error: string | null;
}

export interface LevertijdMmStorefront {
  code: string;
  flag: string;
  account_id: number | null;
  status: "active" | "not_connected";
}

export async function fetchLevertijdMarketplaces() {
  return api<{ marketplaces: LevertijdMarketplaceCard[] }>("/api/levertijden-switch/marketplaces");
}

// --- Unified per-SKU × marketplace matrix ---
export type UnifiedMarketplace = "amazon" | "bol" | "mediamarkt" | "kaufland" | "shopify_pc" | "shopify_pw";
export interface UnifiedLeadtimeRow {
  id: number;
  sku: string; ean: string | null;
  marketplace: UnifiedMarketplace;
  account_id: number | null; account_label: string | null;
  target_days: number | null;
  source: "assortment" | "listing_seed" | "manual" | "force";
  is_listed: boolean;
  live_days: number | null;
  pending_days: number | null;
  lifecycle_status: string | null;
  brand_name: string | null;
  updated_at: string;
}
export async function fetchUnifiedLeadtimes(opts: {
  only_listed?: boolean; marketplace?: UnifiedMarketplace; search?: string;
  lifecycle?: "all" | "active" | "phasing_out" | "discontinued";
} = {}) {
  const params = new URLSearchParams();
  if (opts.only_listed) params.set("only_listed", "true");
  if (opts.marketplace) params.set("marketplace", opts.marketplace);
  if (opts.search) params.set("search", opts.search);
  if (opts.lifecycle) params.set("lifecycle", opts.lifecycle);
  const qs = params.toString();
  return api<{ rows: UnifiedLeadtimeRow[] }>(
    `/api/levertijden-switch/unified${qs ? "?" + qs : ""}`,
  );
}
export async function patchUnifiedLeadtime(id: number, body: { target_days: number | null; notes?: string }) {
  return api<{ ok: boolean }>(`/api/levertijden-switch/unified/${id}`, {
    method: "PATCH", body,
  });
}
export async function onboardUnifiedLeadtimes() {
  return api<{
    ok: boolean;
    results: Array<{ marketplace: string; new_rows: number }>;
    amazon_routing_onboarded: number;
  }>(`/api/levertijden-switch/unified/onboard`, { method: "POST" });
}
export interface CoverageRow {
  sku: string; ean: string | null; lifecycle_status: string;
  on_amazon: boolean; on_bol: boolean;
  on_mediamarkt: boolean; on_kaufland: boolean; on_shopify: boolean;
}
export async function fetchAssortmentCoverage(opts: { only_gaps?: boolean; lifecycle?: "all" | "active" | "phasing_out" } = {}) {
  const params = new URLSearchParams();
  if (opts.only_gaps) params.set("only_gaps", "true");
  if (opts.lifecycle) params.set("lifecycle", opts.lifecycle);
  const qs = params.toString();
  return api<{ rows: CoverageRow[]; total: number }>(
    `/api/levertijden-switch/unified/coverage${qs ? "?" + qs : ""}`,
  );
}

export async function fetchLevertijdMmData(opts: { search?: string; only_pending?: boolean } = {}) {
  const params = new URLSearchParams();
  if (opts.search) params.set("search", opts.search);
  if (opts.only_pending) params.set("only_pending", "true");
  const qs = params.toString();
  return api<{ storefronts: LevertijdMmStorefront[]; rows: LevertijdMmRow[] }>(
    `/api/levertijden-switch/mediamarkt/data${qs ? "?" + qs : ""}`,
  );
}

export async function saveLevertijdMmPending(body: {
  account_id: number;
  shop_sku: string;
  leadtime_to_ship: number | null;
}) {
  return api<{ ok: boolean; cleared?: boolean }>(
    "/api/levertijden-switch/mediamarkt/save-pending",
    { method: "POST", body },
  );
}

export async function clearLevertijdMmPending(account_id: number, shop_sku: string) {
  return api<{ ok: boolean }>("/api/levertijden-switch/mediamarkt/clear-pending", {
    method: "POST",
    body: { account_id, shop_sku },
  });
}

export async function pushLevertijdMm(account_id: number) {
  return api<{
    ok: boolean;
    pending_status: "success" | "pending" | "errors";
    n_pending: number;
    n_pushed: number;
    import_id?: number;
    import_status?: string;
    lines_in_success?: number;
    lines_in_error?: number;
    errors?: string[];
    error?: string;
    message?: string;
  }>(`/api/levertijden-switch/mediamarkt/push?account_id=${account_id}`, { method: "POST" });
}

// === Bol ===

export interface LevertijdBolRow {
  offer_id: string;
  account_id: number;
  account_label: string;
  sku: string;
  ean: string | null;
  fulfilment_method: string | null;
  delivery_code: string | null;
  pending_delivery_code: string | null;
  pending_delivery_code_at: string | null;
  pending_delivery_code_error: string | null;
}

export interface LevertijdStorefront {
  code: string;
  flag: string;
  account_id?: number | null;
  marketplace_id?: string | null;
  storefront?: string;
  status: "active" | "not_connected";
}

export async function fetchLevertijdBolData(opts: { search?: string; only_pending?: boolean } = {}) {
  const params = new URLSearchParams();
  if (opts.search) params.set("search", opts.search);
  if (opts.only_pending) params.set("only_pending", "true");
  const qs = params.toString();
  return api<{ storefronts: LevertijdStorefront[]; rows: LevertijdBolRow[] }>(
    `/api/levertijden-switch/bol/data${qs ? "?" + qs : ""}`,
  );
}

export async function saveLevertijdBolPending(body: {
  account_id: number;
  offer_id: string;
  delivery_code: string | null;
}) {
  return api<{ ok: boolean; cleared?: boolean }>(
    "/api/levertijden-switch/bol/save-pending",
    { method: "POST", body },
  );
}

export async function clearLevertijdBolPending(account_id: number, offer_id: string) {
  return api<{ ok: boolean }>("/api/levertijden-switch/bol/clear-pending", {
    method: "POST",
    body: { account_id, offer_id },
  });
}

export async function pushLevertijdBol(account_id: number) {
  return api<{
    ok: boolean;
    n_pending: number;
    n_pushed: number;
    n_pending_async?: number;
    n_failed: number;
    n_skipped?: number;
    message?: string;
  }>(`/api/levertijden-switch/bol/push?account_id=${account_id}`, { method: "POST" });
}

// === Amazon ===

export interface LevertijdAmzRow {
  sp_account_id: number;
  marketplace_id: string;
  seller_sku: string;
  asin: string | null;
  lead_time_to_ship_max_days: number | null;
  pending_lead_time: number | null;
  pending_lead_time_at: string | null;
  pending_lead_time_error: string | null;
}

export async function fetchLevertijdAmzData(opts: { search?: string; only_pending?: boolean } = {}) {
  const params = new URLSearchParams();
  if (opts.search) params.set("search", opts.search);
  if (opts.only_pending) params.set("only_pending", "true");
  const qs = params.toString();
  return api<{ storefronts: LevertijdStorefront[]; rows: LevertijdAmzRow[] }>(
    `/api/levertijden-switch/amazon/data${qs ? "?" + qs : ""}`,
  );
}

export async function saveLevertijdAmzPending(body: {
  sp_account_id: number;
  marketplace_id: string;
  seller_sku: string;
  lead_time: number | null;
}) {
  return api<{ ok: boolean; cleared?: boolean }>(
    "/api/levertijden-switch/amazon/save-pending",
    { method: "POST", body },
  );
}

export async function clearLevertijdAmzPending(sp_account_id: number, marketplace_id: string, seller_sku: string) {
  return api<{ ok: boolean }>("/api/levertijden-switch/amazon/clear-pending", {
    method: "POST",
    body: { sp_account_id, marketplace_id, seller_sku },
  });
}

export async function pushLevertijdAmz(sp_account_id: number, marketplace_id: string) {
  return api<{ ok: boolean; n_pending: number; n_pushed: number; n_failed: number }>(
    `/api/levertijden-switch/amazon/push?sp_account_id=${sp_account_id}&marketplace_id=${encodeURIComponent(marketplace_id)}`,
    { method: "POST" },
  );
}

// === Kaufland ===

export interface LevertijdKflRow {
  account_id: number;
  storefront: string;
  id_unit: string;
  id_offer: string | null;
  ean: string | null;
  handling_time: number | null;
  pending_handling_time: number | null;
  pending_handling_time_at: string | null;
  pending_handling_time_error: string | null;
}

export async function fetchLevertijdKflData(opts: { search?: string; only_pending?: boolean } = {}) {
  const params = new URLSearchParams();
  if (opts.search) params.set("search", opts.search);
  if (opts.only_pending) params.set("only_pending", "true");
  const qs = params.toString();
  return api<{ account_id: number | null; storefronts: LevertijdStorefront[]; rows: LevertijdKflRow[] }>(
    `/api/levertijden-switch/kaufland/data${qs ? "?" + qs : ""}`,
  );
}

export async function saveLevertijdKflPending(body: {
  account_id: number;
  storefront: string;
  id_unit: string;
  handling_time: number | null;
}) {
  return api<{ ok: boolean; cleared?: boolean }>(
    "/api/levertijden-switch/kaufland/save-pending",
    { method: "POST", body },
  );
}

export async function clearLevertijdKflPending(account_id: number, storefront: string, id_unit: string) {
  return api<{ ok: boolean }>("/api/levertijden-switch/kaufland/clear-pending", {
    method: "POST",
    body: { account_id, storefront, id_unit },
  });
}

export async function pushLevertijdKfl(account_id: number) {
  return api<{ ok: boolean; n_pending: number; n_pushed: number; n_failed: number }>(
    `/api/levertijden-switch/kaufland/push?account_id=${account_id}`, { method: "POST" },
  );
}

export interface ShopifyListing {
  id: number;
  account_id: number;
  variant_gid: string;
  variant_legacy_id: number | null;
  product_gid: string;
  product_legacy_id: number | null;
  inventory_item_id: number | null;
  sku: string | null;
  barcode: string | null;
  product_title: string | null;
  product_handle: string | null;
  product_status: string | null;
  variant_title: string | null;
  price: string | null;
  compare_at_price: string | null;
  currency: string | null;
  inventory_total: number | null;
  inventory_primary: number | null;
  /** Map location_gid → quantity. Voor multi-location push (Pro Witgoed). */
  inventory_per_location: Record<string, number> | null;
  handling_time_days: number | null;
  last_updated_shopify: string | null;
  synced_at: string;
}

export interface ShopifyOfferChangeRow {
  id: number;
  account_id: number;
  variant_gid: string;
  sku: string | null;
  old_price: string | null;
  new_price: string | null;
  old_inventory: number | null;
  new_inventory: number | null;
  old_handling: number | null;
  new_handling: number | null;
  success: boolean;
  api_status: number | null;
  api_error: string | null;
  changed_by_id: number | null;
  triggered_by: string;
  changed_at: string;
}

export interface ShopifyListingsSyncStateRow {
  account_id: number;
  last_sync_at: string | null;
  last_sync_count: number;
  last_sync_error: string | null;
}

export async function fetchShopifyAccounts() {
  return api<{ accounts: ShopifyAccount[] }>("/api/shopify/accounts");
}

export async function createShopifyAccount(body: {
  name: string;
  label: string;
  shop_domain: string;
  client_id: string;
  client_secret: string;
  api_version?: string;
}) {
  return api<{ id: number }>("/api/shopify/accounts", { method: "POST", body });
}

export async function updateShopifyAccount(id: number, body: Partial<{
  name: string;
  shop_domain: string;
  client_id: string;
  client_secret: string;
  api_version: string;
  is_active: boolean;
}>) {
  return api<{ ok: boolean; updated: string[] }>(`/api/shopify/accounts/${id}`, {
    method: "PUT",
    body,
  });
}

export async function deleteShopifyAccount(id: number, hard = false) {
  return api<void>(`/api/shopify/accounts/${id}${hard ? "?hard=true" : ""}`, {
    method: "DELETE",
  });
}

export async function testShopifyAccount(id: number) {
  return api<ShopifyTestResult>(`/api/shopify/accounts/${id}/test`, { method: "POST" });
}

export interface DeployPwsResult {
  ok: boolean;
  theme_id: number;
  theme_name: string;
  assets: Array<{ key: string; ok: boolean; error?: string; updated_at?: string }>;
  message: string;
}
export async function deployPwsSnippets(accountId: number) {
  return api<DeployPwsResult>(`/api/shopify/theme/deploy-pws-snippets/${accountId}`, { method: "POST" });
}

// ---------------------------------------------------------------------------
// Pro Witgoed delivery-rules config
// ---------------------------------------------------------------------------

export interface PwsConfig {
  instock_label: string;
  instock_label_missed: string;
  instock_label_weekend: string;
  sub_instock: string;
  sub_instock_dpd: string;
  sub_backorder: string;
  soldout_label: string;
  updated_at: string;
  last_sync_at: string | null;
  last_sync_error: string | null;
}

export interface PwsLocationRow {
  location_gid: string;
  name: string;
  is_primary: boolean;
  delivery_rule_type: "none" | "cutoff" | "leadtime";
  delivery_cutoff_time: string | null;
  delivery_lead_days: number | null;
  delivery_label: string | null;
}

export interface PwsConfigResponse {
  config: PwsConfig;
  locations: PwsLocationRow[];
}

export interface PwsSaveResponse extends PwsConfigResponse {
  ok: true;
  saved: boolean;
  metafields_synced: boolean;
  sync_error: string | null;
}

export interface PwsConfigInput {
  instock_label: string;
  instock_label_missed: string;
  instock_label_weekend: string;
  sub_instock: string;
  sub_instock_dpd: string;
  sub_backorder: string;
  soldout_label: string;
}

export interface PwsLocationInput {
  location_gid: string;
  delivery_rule_type: "none" | "cutoff" | "leadtime";
  delivery_cutoff_time: string | null;
  delivery_lead_days: number | null;
  delivery_label: string | null;
}

export function fetchPwsConfig(accountId: number) {
  return api<PwsConfigResponse>(`/api/shopify/accounts/${accountId}/pws-config`);
}

export function savePwsConfig(
  accountId: number,
  body: { config: PwsConfigInput; locations: PwsLocationInput[]; push_metafields?: boolean },
) {
  return api<PwsSaveResponse>(`/api/shopify/accounts/${accountId}/pws-config`, {
    method: "PUT",
    body,
  });
}

export function resyncPwsConfig(accountId: number) {
  return api<{ ok: true; n_set: number }>(`/api/shopify/accounts/${accountId}/pws-config/resync`, { method: "POST" });
}

export async function triggerShopifyLocationsSync(accountId: number) {
  return api<{
    ok: boolean;
    fetched: number;
    upserted: number;
    primary_assigned: boolean;
    message?: string;
  }>(`/api/shopify/sync/locations/${accountId}`, { method: "POST" });
}

export async function fetchShopifyLocations(accountId: number) {
  return api<{ rows: ShopifyLocation[] }>(`/api/shopify/locations?account_id=${accountId}`);
}

export async function setShopifyPrimaryLocation(accountId: number, locationGid: string) {
  return api<{ ok: boolean }>(`/api/shopify/accounts/${accountId}/primary-location`, {
    method: "PUT",
    body: { location_gid: locationGid },
  });
}

export async function triggerShopifyProductsSync(accountId: number) {
  return api<{
    ok: boolean;
    account_id: number;
    products: number;
    variants_upserted: number;
    inventory_enriched: number;
    durationMs: number;
    error?: string;
  }>(`/api/shopify/sync/products/${accountId}`, { method: "POST" });
}

export async function fetchShopifyListings(filter: {
  account_id?: number;
  status?: string;
  search?: string;
  active_only?: boolean;
  limit?: number;
  offset?: number;
} = {}) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  }
  const qs = params.toString();
  return api<{ total: number; limit: number; offset: number; rows: ShopifyListing[] }>(
    `/api/shopify/listings${qs ? "?" + qs : ""}`,
  );
}

export async function fetchShopifyListingsSyncState() {
  return api<{ rows: ShopifyListingsSyncStateRow[] }>("/api/shopify/listings/sync-state");
}

export async function updateShopifyVariant(variantLegacyId: number, body: {
  account_id: number;
  price?: number;
  compare_at_price?: number | null;
  inventory?: number;
  inventory_per_location?: Record<string, number>;
  handling_time_days?: number | null;
}) {
  return api<{
    ok: boolean;
    error?: string;
    applied: {
      price: number | null;
      inventory: number | null;
      compare_at_price: number | null;
      inventory_per_location?: Record<string, number>;
      handling_time_days?: number | null;
    };
  }>(`/api/shopify/variants/${variantLegacyId}/update`, { method: "POST", body });
}

export async function updateShopifyLocationRule(
  accountId: number,
  locationGid: string,
  body: Partial<{
    delivery_rule_type: "none" | "cutoff" | "leadtime";
    delivery_cutoff_time: string | null;
    delivery_lead_days: number | null;
    delivery_label: string | null;
    siu_stock_source: ShopifySiuStockSource;
    siu_warehouse_name: string | null;
    siu_warehouse_names: string[] | null;
    warehouse_leadtimes: Record<string, number>;
  }>,
) {
  // GID bevat slashes → base64 encoden voor URL-safety
  const gidB64 = btoa(locationGid);
  return api<{ ok: boolean; updated: string[] }>(
    `/api/shopify/accounts/${accountId}/locations/${gidB64}`,
    { method: "PUT", body },
  );
}

// === Shopify SIU sync ===

export interface ShopifySiuSyncVariantResult {
  variant_legacy_id: number | null;
  variant_gid: string;
  sku: string | null;
  barcode: string | null;
  matched_by: "sku" | "ean" | null;
  per_location: Record<string, {
    source: string;
    old: number | null;
    new: number | null;
    pushed: boolean;
    error?: string;
  }>;
  push_ok: boolean;
  push_error?: string;
}

export interface ShopifySiuSyncResult {
  ok?: boolean;
  run_id: number;
  account_id: number;
  account_name: string;
  dry_run: boolean;
  n_variants: number;
  n_matched: number;
  n_pushed: number;
  n_skipped: number;
  n_failed: number;
  duration_ms: number;
  variants: ShopifySiuSyncVariantResult[];
  error?: string;
  message?: string;
}

export async function triggerShopifySiuSync(
  accountId: number,
  opts: { dry_run?: boolean; max_variants?: number } = {},
) {
  const qs = new URLSearchParams();
  if (opts.dry_run) qs.set("dry_run", "true");
  if (opts.max_variants) qs.set("max_variants", String(opts.max_variants));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return api<ShopifySiuSyncResult>(
    `/api/shopify/sync/siu-stock/${accountId}${suffix}`,
    { method: "POST" },
  );
}

export interface ShopifySiuSyncRunRow {
  id: number;
  account_id: number;
  triggered_by: string;
  triggered_by_id: number | null;
  dry_run: boolean;
  started_at: string;
  finished_at: string | null;
  n_variants: number;
  n_matched: number;
  n_pushed: number;
  n_skipped: number;
  n_failed: number;
  error: string | null;
}

export async function fetchShopifySiuSyncRuns(accountId: number) {
  return api<{ rows: ShopifySiuSyncRunRow[] }>(
    `/api/shopify/sync/siu-stock/runs?account_id=${accountId}`,
  );
}

export async function fetchShopifyVariantHistory(variantLegacyId: number) {
  return api<{ rows: ShopifyOfferChangeRow[] }>(
    `/api/shopify/variants/${variantLegacyId}/history`,
  );
}

export async function fetchMediamarktOfferHistory(offerId: number) {
  return api<{ rows: MediamarktOfferChangeRow[] }>(
    `/api/mediamarkt/listings/${offerId}/history`,
  );
}

/** PUT warehouse-stock returnt nu ook een auto-action sub-object. */
export interface WarehouseStockUpdateResponse {
  ok: boolean;
  count: number;
  auto: {
    triggered: boolean;
    pushed: boolean;
    reason: string;
    lead_time_days: number | null;
    warehouse_name: string | null;
    submission_id: string | null;
    error: string | null;
  };
}

/** Refresh 1 listing — haalt actuele prijs uit Amazon en update lokale DB. */
export async function refreshAmazonListing(sku: string, input: {
  sp_account_id: number;
  marketplace_id: string;
}) {
  return api<{
    ok: boolean;
    old_price: number | null;
    new_price: number | null;
    currency: string | null;
    changed: boolean;
    issues: unknown[];
    offers: unknown[];
  }>(`/api/amazon/listings/${encodeURIComponent(sku)}/refresh`, {
    method: "POST",
    body: input,
  });
}

// ---------------------------------------------------------------------------
// Sync-health monitoring — dashboard banner + detail page
// ---------------------------------------------------------------------------

export type HealthStatus = "healthy" | "warning" | "critical";

export interface MarketplaceHealth {
  marketplace: string;
  status: HealthStatus;
  critical: number;
  high: number;
  medium: number;
  low: number;
  total: number;
}

export interface DetectorRunInfo {
  key: string;
  last_run_at: string;
  last_duration_ms: number | null;
  last_events_opened: number;
  last_error: string | null;
  age_seconds: number;
}

export interface SyncHealthStatus {
  overall_status: HealthStatus;
  per_marketplace: MarketplaceHealth[];
  system_open_events: number;
  total_open_events: number;
  auto_fixes_last_hour: number;
  detectors: DetectorRunInfo[];
  generated_at: string;
}

export async function fetchSyncHealthStatus() {
  return api<SyncHealthStatus>("/api/sync-health/status");
}

export interface SyncHealthEvent {
  id: number;
  detector_key: string;
  marketplace: string | null;
  account_id: number | null;
  seller_sku: string | null;
  severity: "critical" | "high" | "medium" | "low";
  status: "open" | "acknowledged" | "resolved";
  title: string;
  description: string | null;
  metadata: unknown;
  first_seen_at: string;
  last_seen_at: string;
  detection_count: number;
  resolved_at: string | null;
  resolution_type: string | null;
}

export async function fetchSyncHealthEvents(filter: {
  marketplace?: string;
  detector?: string;
  severity?: string;
  status?: string;
  limit?: number;
} = {}) {
  const qs = new URLSearchParams();
  if (filter.marketplace) qs.set("marketplace", filter.marketplace);
  if (filter.detector) qs.set("detector", filter.detector);
  if (filter.severity) qs.set("severity", filter.severity);
  if (filter.status) qs.set("status", filter.status);
  if (filter.limit) qs.set("limit", String(filter.limit));
  return api<{ rows: SyncHealthEvent[] }>(`/api/sync-health/events?${qs}`);
}

export async function ackSyncHealthEvent(id: number) {
  return api<{ ok: boolean }>(`/api/sync-health/events/${id}/ack`, { method: "POST", body: {} });
}

export async function resolveSyncHealthEvent(id: number, notes?: string) {
  return api<{ ok: boolean }>(`/api/sync-health/events/${id}/resolve`, {
    method: "POST",
    body: { notes },
  });
}

export async function runSyncHealthDetector(detector: string) {
  return api<{
    n_scanned: number;
    n_events_opened: number;
    n_events_resolved: number;
    n_autofixes: number;
    error?: string;
  }>(`/api/sync-health/run/${encodeURIComponent(detector)}`, { method: "POST", body: {} });
}

export interface AgentRecentAction {
  id: number;
  fixer_key: string;
  status: "pending" | "success" | "failed";
  started_at: string;
  summary: string | null;
  detector_key: string | null;
  seller_sku: string | null;
  marketplace: string | null;
}

export interface AgentStatus {
  is_alive: boolean;
  heartbeat_age_seconds: number | null;
  currently_running: Array<{ detector_key: string; started_at: string }>;
  detector_runs_1h: number;
  autofixes_1h: number;
  autofixes_24h: number;
  events_resolved_24h: number;
  recent_actions: AgentRecentAction[];
  generated_at: string;
}

export async function fetchAgentStatus() {
  return api<AgentStatus>("/api/sync-health/agent-status");
}

export interface ShipThroughTodoRow {
  marketplace_id: string;
  marketplace_label: string;
  seller_sku: string;
  asin: string | null;
  resolved_mode: string | null;
  resolved_lead_time_days: number | null;
  expected_preference: "EASY_SHIP_PREFERRED" | "SELF_SHIP_ONLY";
  is_confirmed: boolean;
  status: "done" | "todo" | "mismatch";
  confirmed_at: string | null;
  confirmed_preference: string | null;
}

export async function fetchShipThroughTodo() {
  return api<{
    summary: { total: number; todo: number; done: number; mismatch: number };
    rows: ShipThroughTodoRow[];
  }>("/api/amazon/ship-through-todo");
}

export async function confirmShipThrough(input: {
  marketplace_id: string;
  seller_sku: string;
  preference: "EASY_SHIP_PREFERRED" | "SELF_SHIP_ONLY";
  notes?: string;
}) {
  return api<{ ok: boolean }>("/api/amazon/ship-through-status", { method: "POST", body: input });
}

export type EasyShipStatusCode = "active" | "pushed_waiting" | "wrong_template" | "no_template" | "unsupported" | "error";

export interface EasyShipStatusRow {
  sp_account_id: number;
  marketplace_id: string;
  seller_sku: string;
  status: EasyShipStatusCode;
  live_merchant_shipping_group: string | null;
  expected_template: string | null;
  matches_template: boolean;
  recent_easyship_orders_7d: number;
  last_easyship_order_at: string | null;
  live_lead_time_days: number | null;
  live_quantity: number | null;
  error: string | null;
  checked_at: string;
}

export async function fetchEasyShipStatus(skus: string[], opts?: { marketplace_id?: string; force?: boolean }) {
  const qs = new URLSearchParams();
  qs.set("skus", skus.join(","));
  if (opts?.marketplace_id) qs.set("marketplace_id", opts.marketplace_id);
  if (opts?.force) qs.set("force", "true");
  return api<{ rows: EasyShipStatusRow[] }>(`/api/amazon/easyship-status?${qs}`);
}

export async function verifyConfirmEasyShipNl(marketplaceId: string) {
  return api<{
    ok: boolean;
    checked: number;
    confirmed: number;
    mismatches: number;
    results: Array<{ sku: string; status: string; live_template: string | null; expected_template: string | null; matches: boolean }>;
  }>("/api/amazon/easyship-status/verify-confirm", {
    method: "POST",
    body: { marketplace_id: marketplaceId },
  });
}

export async function unconfirmShipThrough(input: { marketplace_id: string; seller_sku: string }) {
  return api<{ ok: boolean }>("/api/amazon/ship-through-status", { method: "DELETE", body: input });
}

export async function confirmShipThroughBulk(input: {
  preference: "EASY_SHIP_PREFERRED" | "SELF_SHIP_ONLY";
  skus: Array<{ marketplace_id: string; seller_sku: string }>;
}) {
  return api<{ ok: boolean; confirmed: number }>("/api/amazon/ship-through-status/bulk", {
    method: "POST",
    body: input,
  });
}

export interface BolOrphanSuggestion {
  event_id: number;
  ean: string | null;
  reference_code: string | null;
  marketplace: string | null;
  candidates: Array<{ id: number; sku: string; ean: string | null; title: string | null; reason: string }>;
}

export async function fetchBolOrphanSuggestions() {
  return api<{ rows: BolOrphanSuggestion[] }>("/api/sync-health/bol-orphan-suggestions");
}

export async function bolProductOverride(input: {
  ean?: string;
  reference_code?: string;
  account_label?: "nl" | "be";
  target_sku: string;
  reason?: string;
}) {
  return api<{
    ok: boolean;
    product: { id: number; sku: string };
    listings_overriden: Array<{ account_label: string; offer_id: string; ean: string | null; reference_code: string | null }>;
  }>("/api/sync-health/bol-product-override", { method: "POST", body: input });
}

export interface RoutingMatrixCell {
  mode: string;
  handling: number | null;
  label: string;
}
export interface RoutingMatrixMarketplace {
  key: string;
  label: string;
  group: string;
  account_id?: number;
  marketplace_id?: string;
}
export interface RoutingMatrixWarehouse {
  siu_name: string;
  label: string;
}
export interface RoutingMatrixData {
  warehouses: RoutingMatrixWarehouse[];
  marketplaces: RoutingMatrixMarketplace[];
  cells: Record<string, Record<string, RoutingMatrixCell>>;
  stats: Record<string, { auto_routed: number; last_sync: string | null }>;
  generated_at: string;
}

export async function fetchRoutingMatrix() {
  return api<RoutingMatrixData>("/api/levertijden-switch/routing-matrix");
}

export interface RolloutPreviewRow {
  marketplace_id: string;
  seller_sku: string;
  currently_enabled: boolean;
  vvb_qty: number;
  mda_qty: number;
  nod_qty: number;
  in_koerier_exclusion: boolean;
  chosen_warehouse: string | null;
  chosen_mode: string | null;
  chosen_handling: number | null;
  reason: string;
}

export async function fetchRolloutPreview(opts?: { marketplaces?: string[]; include_active?: boolean }) {
  const qs = new URLSearchParams();
  if (opts?.marketplaces) qs.set("marketplaces", opts.marketplaces.join(","));
  if (opts?.include_active) qs.set("include_active", "true");
  return api<{
    summary: Array<{ marketplace_id: string; total: number; easyship: number; mfn: number; koeriers: number }>;
    rows: RolloutPreviewRow[];
    generated_at: string;
  }>(`/api/sync-health/amazon-rollout-preview?${qs}`);
}

export async function executeBulkRollout(input: {
  marketplaces: string[];
  sku_tuples?: Array<{ marketplace_id: string; seller_sku: string }>;
  dry_run?: boolean;
  auto_push?: boolean;
  auto_verify_nl?: boolean;
}) {
  return api<{
    ok: boolean;
    dry_run?: boolean;
    enabled?: number;
    would_enable?: number;
    pushed?: number;
    verified_nl?: number;
    product_ids_synced?: number;
    skus_by_marketplace: Record<string, string[]>;
  }>("/api/sync-health/amazon-rollout-enable", { method: "POST", body: input });
}

// =========================================================================
// Staged Rollout Batches — preview → execute → rollback
// =========================================================================

export type RolloutBatchState =
  | "draft" | "executing" | "executed" | "partial_executed"
  | "failed" | "rolling_back" | "rolled_back";

export type RolloutItemState =
  | "pending" | "executed" | "failed" | "skipped" | "rolled_back";

export interface RolloutImpactPerMp {
  total: number;
  easyship: number;
  mfn: number;
  skipped: number;
}

export interface RolloutImpact {
  per_marketplace: Record<string, RolloutImpactPerMp>;
  per_skip_reason: Record<string, number>;
  total: number;
}

export interface RolloutPreviewItem {
  marketplace_id: string;
  seller_sku: string;
  planned_mode: "easyship" | "mfn" | "skip";
  skip_reason: string | null;
  stock_vvb: number;
  stock_mda: number;
  stock_nod: number;
  chosen_warehouse: string | null;
  chosen_handling: number | null;
  in_koerier_exclusion: boolean;
  currently_enabled: boolean;
}

export interface RolloutBatch {
  id: string;
  client_token: string;
  name: string | null;
  notes: string | null;
  marketplaces: string[];
  only_with_stock: boolean;
  exclude_koeriers: boolean;
  auto_push: boolean;
  auto_verify_nl: boolean;
  auto_verify_de: boolean;
  enable_easyship_be: boolean;
  state: RolloutBatchState;
  error_message: string | null;
  total_items: number;
  total_nl: number;
  total_de: number;
  total_be: number;
  total_easyship_nl: number;
  total_easyship_de: number;
  total_mfn: number;
  total_skipped: number;
  executed_count: number;
  pushed_count: number;
  verified_nl_count: number;
  verified_de_count: number;
  failed_count: number;
  rolled_back_count: number;
  created_by_id: number | null;
  created_by_name: string | null;
  created_at: string;
  executed_by_id: number | null;
  executed_by_name?: string | null;
  executed_at: string | null;
  rolled_back_by_id: number | null;
  rolled_back_at: string | null;
}

export interface RolloutBatchItem {
  id: string;
  batch_id: string;
  marketplace_id: string;
  seller_sku: string;
  planned_mode: "easyship" | "mfn" | "skip";
  skip_reason: string | null;
  stock_vvb: number | null;
  stock_mda: number | null;
  stock_nod: number | null;
  pre_auto_route_enabled: boolean | null;
  pre_resolved_mode: string | null;
  pre_ship_through_pref: string | null;
  item_state: RolloutItemState;
  push_success: boolean | null;
  verified: boolean;
  error_message: string | null;
  executed_at: string | null;
  rolled_back_at: string | null;
}

export interface RolloutBatchPreviewInput {
  client_token: string;
  marketplaces: string[];
  only_with_stock?: boolean;
  exclude_koeriers?: boolean;
  skus?: string[];
  sku_tuples?: Array<{ marketplace_id: string; seller_sku: string }>;
  auto_push?: boolean;
  auto_verify_nl?: boolean;
  auto_verify_de?: boolean;
  name?: string;
  notes?: string;
  dry_run?: boolean;
}

export async function previewRolloutBatch(input: RolloutBatchPreviewInput) {
  return api<{
    batch_id: string | null;
    state: RolloutBatchState | null;
    impact: RolloutImpact;
    items_preview: RolloutPreviewItem[];
    items_total: number;
    reused: boolean;
  }>("/api/sync-health/rollout-batches", { method: "POST", body: input });
}

export async function executeRolloutBatch(id: string) {
  return api<RolloutBatch>(`/api/sync-health/rollout-batches/${id}/execute`, { method: "POST" });
}

export async function reExecuteRolloutBatch(id: string) {
  return api<RolloutBatch>(`/api/sync-health/rollout-batches/${id}/re-execute`, { method: "POST" });
}

export async function rollbackRolloutBatch(id: string, reason?: string) {
  return api<RolloutBatch>(`/api/sync-health/rollout-batches/${id}/rollback`, {
    method: "POST",
    body: { reason },
  });
}

export async function listRolloutBatches(params?: {
  state?: string;
  marketplaces?: string[];
  limit?: number;
  offset?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.state) qs.set("state", params.state);
  if (params?.marketplaces?.length) qs.set("marketplaces", params.marketplaces.join(","));
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.offset) qs.set("offset", String(params.offset));
  const url = qs.toString()
    ? `/api/sync-health/rollout-batches?${qs}`
    : "/api/sync-health/rollout-batches";
  return api<{ batches: RolloutBatch[]; total: number }>(url);
}

export async function getRolloutBatchDetail(id: string) {
  return api<{ batch: RolloutBatch; items: RolloutBatchItem[] }>(
    `/api/sync-health/rollout-batches/${id}`,
  );
}

export async function forceResyncSku(input: { ean?: string; sku?: string; marketplaces?: string[] }) {
  return api<{
    ok: boolean;
    result: {
      ean: string | null;
      sku: string | null;
      product_id?: number;
      bol?: string;
      amazon?: { enqueued: number };
    };
  }>("/api/sync-health/force-resync", { method: "POST", body: input });
}

// =========================================================================
// Price-push — CSV-driven multi-marketplace prijs-push
// =========================================================================

export const PRICE_PUSH_MARKETPLACES = [
  "amazon_nl", "amazon_de", "amazon_be",
  "bol_nl", "bol_be",
  // Eén Kaufland-API, maar per storefront een eigen unit met een eigen prijs.
  "kaufland_de", "kaufland_at",
  "mm_nl", "mm_de", "mm_be",
] as const;
export type PricePushMarketplace = (typeof PRICE_PUSH_MARKETPLACES)[number];

export type PricePushBatchState =
  | "draft" | "executing" | "executed" | "partial_executed" | "failed";

export type PricePushItemStatus =
  | "pending" | "needs_review" | "approved" | "skipped"
  | "executed" | "failed" | "rolled_back";

export interface PricePushBatch {
  id: string;
  filename: string;
  uploaded_by_id: number | null;
  uploaded_by_name: string | null;
  state: PricePushBatchState;
  total_items: number;
  total_skipped: number;
  total_needs_review: number;
  executed_count: number;
  failed_count: number;
  notes: string | null;
  created_at: string;
  executed_at: string | null;
  executed_by_id: number | null;
  executed_by_name: string | null;
}

export interface PricePushBatchItem {
  id: string;
  batch_id: string;
  sku: string;
  ean: string | null;
  marketplace: PricePushMarketplace;
  current_price: number | null;
  target_price: number;
  delta_pct: number | null;
  source_margin: number | null;
  source_purchase: number | null;
  source_fees: number | null;
  status: PricePushItemStatus;
  error_message: string | null;
  pushed_at: string | null;
  pushed_by_id: number | null;
}

export interface PricePushParseError {
  row: number;
  col: string;
  msg: string;
}

export interface PricePushUploadResult {
  batch: PricePushBatch;
  warnings: string[];
  errors: PricePushParseError[];
  totals: {
    total: number;
    pending: number;
    needs_review: number;
    skipped: number;
  };
}

export interface PricePushBatchDetail {
  batch: PricePushBatch;
  items: PricePushBatchItem[];
}

export interface PricePushExecuteResult {
  batch_id: string;
  state: PricePushBatchState;
  executed: number;
  failed: number;
  skipped: number;
  errors: Array<{ sku: string; marketplace: PricePushMarketplace; error: string }>;
}

export async function listPriceBatches(params?: {
  state?: string;
  limit?: number;
  offset?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.state) qs.set("state", params.state);
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.offset) qs.set("offset", String(params.offset));
  const url = qs.toString() ? `/api/prijzen/batches?${qs}` : "/api/prijzen/batches";
  return api<{ batches: PricePushBatch[]; total: number }>(url);
}

export async function getPriceBatch(id: string) {
  return api<PricePushBatchDetail>(`/api/prijzen/batches/${id}`);
}

/** Upload een CSV file en bouw een preview-batch (state='draft'). Multipart. */
export async function uploadPriceCsv(opts: { file: File; notes?: string }): Promise<PricePushUploadResult> {
  const fd = new FormData();
  fd.append("csv", opts.file);
  if (opts.notes) fd.append("notes", opts.notes);
  const token = getToken();
  const res = await fetch(`/api/prijzen/batches/upload`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: fd,
  });
  if (!res.ok) {
    let body: { error?: string; errors?: PricePushParseError[]; warnings?: string[] } = {};
    try { body = await res.json(); } catch { /* */ }
    throw new ApiError(res.status, body, body.error || `HTTP ${res.status}`);
  }
  return res.json() as Promise<PricePushUploadResult>;
}

export async function executePriceBatch(id: string, opts?: { include_needs_review?: boolean }) {
  return api<PricePushExecuteResult>(`/api/prijzen/batches/${id}/execute`, {
    method: "POST",
    body: { include_needs_review: !!opts?.include_needs_review },
  });
}

export async function approvePriceItem(batchId: string, itemId: string) {
  return api<PricePushBatchItem>(`/api/prijzen/batches/${batchId}/items/${itemId}/approve`, {
    method: "POST",
  });
}

export async function skipPriceItem(batchId: string, itemId: string) {
  return api<PricePushBatchItem>(`/api/prijzen/batches/${batchId}/items/${itemId}/skip`, {
    method: "POST",
  });
}

// =========================================================================
// Price-engine — tool-native UI voor 15-kanaal advies + instellingen
// =========================================================================

export const PRICE_ENGINE_MARKETPLACES = [
  "bol_nl", "bol_be",
  "mm_nl", "mm_be", "mm_de",
  "kaufland",
  "amz_nl", "amz_be", "amz_de",
  "pc_nl", "pc_be", "pc_de",
  "pw_nl", "pw_be", "pw_de",
] as const;
export type PriceEngineMarketplace = (typeof PRICE_ENGINE_MARKETPLACES)[number];

export type PriceEngineStatus =
  | "ok"
  | "onder_vloer"
  | "geen_kosten"
  | "cat_niet_gevonden"
  | "override_actief"
  | "geen_listing";

export interface PriceEngineChannelInfo {
  advies: number | null;
  final_price: number | null;
  concurrent: number | null;
  override: number | null;
  live: number | null;
  live_synced_at: string | null;
  delta_pct: number | null;
  marge_eur: number | null;
  marge_pct: number | null;
  status: PriceEngineStatus | null;
}

export interface PriceEngineProductRow {
  sku: string;
  ean: string | null;
  category: string;
  packet_type: string | null;
  exw: number | null;
  channels: Record<PriceEngineMarketplace, PriceEngineChannelInfo>;
}

export interface PriceEngineProductInput {
  sku: string;
  ean: string | null;
  brand: string | null;
  category: string;
  packet_type: string | null;
  exw: number | null;
  transport: number | null;
  verz_bol_nl: number | null;
  verz_bol_be: number | null;
  verz_nl: number | null;
  verz_be: number | null;
  verz_de: number | null;
  updated_at: string;
}

export interface PriceEngineAdviceRow {
  sku: string;
  marketplace: PriceEngineMarketplace;
  pi: number;
  k1: number; k2: number; k3: number; k4: number;
  d1: number; d2: number; d3: number; d4: number;
  slim: number;
  pmin_vloer: number;
  drempel: number;
  advies: number;
  override_active: boolean;
  final_price: number;
  marge_eur: number;
  marge_pct: number;
  status: PriceEngineStatus;
}

export interface PriceEngineSkuDetail {
  input: PriceEngineProductInput;
  advice: PriceEngineAdviceRow[];
  overrides: Record<string, {
    concurrent: number | null;
    override: number | null;
    override_expires_at: string | null;
  }>;
  history: Array<{
    id: number;
    field: string;
    old_value: number | null;
    new_value: number | null;
    changed_at: string;
    changed_by_id: number | null;
  }>;
}

export interface PriceEngineSettings {
  margins: Array<{ category: string; marketplace: PriceEngineMarketplace; target_margin_pct: number; min_margin_pct: number; }>;
  floor: Array<{ category: string; min_margin_euro: number; }>;
  constants: Array<{ marketplace: PriceEngineMarketplace; vast: number; var_pct: number; }>;
  scope: { pro_witgoed: string[]; rounding_99: string[]; };
  global: {
    tolerantie: number;
    max_trek_omlaag: number;
    max_trek_omhoog: number;
    min_marge_bij_omlaag_trek: number;
    afronding_richting: "omhoog" | "omlaag";
  } | null;
  categories: Array<{ category: string; count: number; }>;
  marketplaces: PriceEngineMarketplace[];
}

export interface PriceEngineHistoryEntry {
  id: number;
  sku: string;
  field: string;
  old_value: number | null;
  new_value: number | null;
  changed_at: string;
  changed_by_id: number | null;
  changed_by_name: string | null;
}

export async function listPriceProducten(params?: {
  categorie?: string;
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
  /** SmartChips/InsightsStrip filters — additief, server-side. */
  filter_live_below?: boolean;
  filter_live_above?: boolean;
  filter_stale?: boolean;
  filter_recent?: boolean;
  filter_no_live?: boolean;
  filter_margin_below_pct?: number;
  /** Welke marketplaces meegenomen worden bij de filter-evaluatie. */
  visible_mps?: PriceEngineMarketplace[];
}) {
  const qs = new URLSearchParams();
  if (params?.categorie) qs.set("categorie", params.categorie);
  if (params?.status) qs.set("status", params.status);
  if (params?.search) qs.set("search", params.search);
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.offset) qs.set("offset", String(params.offset));
  if (params?.filter_live_below) qs.set("filter_live_below", "1");
  if (params?.filter_live_above) qs.set("filter_live_above", "1");
  if (params?.filter_stale) qs.set("filter_stale", "1");
  if (params?.filter_recent) qs.set("filter_recent", "1");
  if (params?.filter_no_live) qs.set("filter_no_live", "1");
  if (params?.filter_margin_below_pct !== undefined && params.filter_margin_below_pct !== null) {
    qs.set("filter_margin_below_pct", String(params.filter_margin_below_pct));
  }
  if (params?.visible_mps && params.visible_mps.length > 0) {
    qs.set("visible_mps", params.visible_mps.join(","));
  }
  const url = qs.toString() ? `/api/prijzen/producten?${qs}` : "/api/prijzen/producten";
  return api<{ items: PriceEngineProductRow[]; total: number }>(url);
}

export interface PriceInsights {
  total_skus: number;
  live_below_advies: number;
  live_above_advies: number;
  onder_vloer: number;
  stale_data: number;
  recent_changed: number;
  no_listings: number;
}

/** Aggregated counts voor de InsightsStrip + SmartChips. Backend rekent
 *  alle tegels in één query — UI hoeft niet client-side te aggregeren. */
export async function fetchPriceInsights(params?: {
  marketplaces?: PriceEngineMarketplace[];
  categorie?: string;
}) {
  const qs = new URLSearchParams();
  if (params?.marketplaces && params.marketplaces.length > 0) {
    qs.set("marketplaces", params.marketplaces.join(","));
  }
  if (params?.categorie) qs.set("categorie", params.categorie);
  const url = qs.toString() ? `/api/prijzen/insights?${qs}` : "/api/prijzen/insights";
  return api<PriceInsights>(url);
}

export async function getPriceProduct(sku: string) {
  return api<PriceEngineSkuDetail>(`/api/prijzen/producten/${encodeURIComponent(sku)}`);
}

export async function updatePriceInputs(sku: string, body: Partial<{
  exw: number | null;
  transport: number | null;
  verz_bol_nl: number | null;
  verz_bol_be: number | null;
  verz_nl: number | null;
  verz_be: number | null;
  verz_de: number | null;
}>) {
  return api<{ ok: boolean; sku: string; recomputed: boolean }>(
    `/api/prijzen/producten/${encodeURIComponent(sku)}/inputs`,
    { method: "POST", body },
  );
}

export async function updatePriceOverride(sku: string, body: {
  marketplace: PriceEngineMarketplace;
  override_price: number | null;
  expires_at?: string | null;
}) {
  return api<{ ok: boolean }>(
    `/api/prijzen/producten/${encodeURIComponent(sku)}/override`,
    { method: "POST", body },
  );
}

export async function updatePriceConcurrent(sku: string, body: {
  marketplace: PriceEngineMarketplace;
  concurrent_price: number | null;
}) {
  return api<{ ok: boolean }>(
    `/api/prijzen/producten/${encodeURIComponent(sku)}/concurrent`,
    { method: "POST", body },
  );
}

export async function pushPriceDirect(sku: string, body: {
  marketplace: PriceEngineMarketplace;
  override_price?: number | null;
}) {
  return api<{
    ok: boolean;
    pushed_price?: number;
    marketplace?: string;
    error?: string | null;
  }>(
    `/api/prijzen/producten/${encodeURIComponent(sku)}/push-direct`,
    { method: "POST", body },
  );
}

/** Fetch live price van marketplace + update cache. Returnt nieuwe live + synced_at. */
export async function refreshLivePrice(sku: string, marketplace: PriceEngineMarketplace) {
  return api<{
    sku: string;
    marketplace: PriceEngineMarketplace;
    live: number | null;
    synced_at: string | null;
  }>(`/api/prijzen/refresh-live`, {
    method: "POST",
    body: { sku, marketplace },
  });
}

/**
 * Bulk refresh — streamt Server-Sent Events vanuit de backend en roept
 * callbacks aan per event. Backend stuurt 3 event-types:
 *   - meta: { total, skipped }   — direct na pre-filter
 *   - row:  { sku, marketplace, live, synced_at, error? } — per voltooide refresh
 *   - done: { ok, failed }       — als alles klaar is
 *
 * Auth: leest hetzelfde token als de gewone api() helper (localStorage) en
 * zet de Authorization-header handmatig — fetch streaming kan niet via api().
 */
export async function refreshLivePricesBulkStream(
  skus: string[],
  marketplaces: PriceEngineMarketplace[],
  onMeta: (m: { total: number; skipped: number }) => void,
  onRow: (r: {
    sku: string;
    marketplace: PriceEngineMarketplace;
    live: number | null;
    synced_at: string | null;
    error?: string;
  }) => void,
  onDone: (d: { ok: number; failed: number }) => void,
): Promise<void> {
  const token = getToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch("/api/prijzen/refresh-bulk", {
    method: "POST",
    headers,
    body: JSON.stringify({ skus, marketplaces }),
  });

  if (res.status === 401) {
    setToken(null);
    window.location.href = "/login";
    throw new ApiError(401, null, "Sessie verlopen — log opnieuw in.");
  }

  if (!res.ok) {
    // Niet-streamende fout (bv. 400 vóór SSE-headers) → body is JSON.
    let msg = res.statusText || `HTTP ${res.status}`;
    try {
      const txt = await res.text();
      try {
        const obj = JSON.parse(txt);
        if (typeof obj?.error === "string") msg = obj.error;
        else if (typeof obj?.detail === "string") msg = obj.detail;
      } catch {
        if (txt) msg = txt;
      }
    } catch { /* negeer */ }
    throw new ApiError(res.status, null, msg);
  }

  if (!res.body) throw new ApiError(0, null, "Geen response-stream beschikbaar");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const handleFrame = (frame: string): void => {
    // Een SSE-frame bestaat uit regels "event: <name>" + "data: <json>".
    // Parse beide; negeer comments (regels die met ":" beginnen).
    let eventName = "message";
    let dataStr = "";
    for (const line of frame.split("\n")) {
      if (line.startsWith("event: ")) eventName = line.slice(7).trim();
      else if (line.startsWith("data: ")) dataStr += (dataStr ? "\n" : "") + line.slice(6);
    }
    if (!dataStr) return;
    let payload: unknown;
    try { payload = JSON.parse(dataStr); } catch { return; }
    if (eventName === "meta") {
      onMeta(payload as { total: number; skipped: number });
    } else if (eventName === "row") {
      onRow(payload as {
        sku: string;
        marketplace: PriceEngineMarketplace;
        live: number | null;
        synced_at: string | null;
        error?: string;
      });
    } else if (eventName === "done") {
      onDone(payload as { ok: number; failed: number });
    } else if (eventName === "error") {
      const err = payload as { error?: string };
      throw new ApiError(0, null, err?.error || "Bulk-refresh stream error");
    }
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (value) buffer += decoder.decode(value, { stream: true });
    // Frames zijn gescheiden door dubbele newline. Process alle complete frames.
    let sepIdx: number;
    while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, sepIdx);
      buffer = buffer.slice(sepIdx + 2);
      if (frame.trim()) handleFrame(frame);
    }
    if (done) break;
  }
}

export async function fetchPriceSettings() {
  return api<PriceEngineSettings>(`/api/prijzen/instellingen`);
}

export async function updatePriceMargin(body: {
  category: string;
  marketplace: PriceEngineMarketplace;
  target_margin_pct: number;
  min_margin_pct?: number;
}) {
  return api<{ ok: boolean }>(`/api/prijzen/instellingen/margins`, { method: "POST", body });
}

export async function updatePriceFloor(body: { category: string; min_margin_euro: number }) {
  return api<{ ok: boolean }>(`/api/prijzen/instellingen/floor`, { method: "POST", body });
}

export async function togglePriceScope(body: {
  scope_type: "pro_witgoed" | "rounding_99";
  category: string;
  enabled: boolean;
}) {
  return api<{ ok: boolean }>(`/api/prijzen/instellingen/scope`, { method: "POST", body });
}

export async function updatePriceConstants(body: {
  marketplace: PriceEngineMarketplace;
  vast: number;
  var_pct: number;
}) {
  return api<{ ok: boolean }>(`/api/prijzen/instellingen/constants`, { method: "POST", body });
}

export async function updatePriceGlobal(body: Partial<{
  tolerantie: number;
  max_trek_omlaag: number;
  max_trek_omhoog: number;
  min_marge_bij_omlaag_trek: number;
  afronding_richting: "omhoog" | "omlaag";
}>) {
  return api<{ ok: boolean }>(`/api/prijzen/instellingen/global`, { method: "POST", body });
}

export async function recomputePriceAdvice(body?: { skus?: string[] }) {
  return api<{ ok: boolean; computed: number; duration_ms?: number }>(
    `/api/prijzen/recompute`,
    { method: "POST", body: body || {} },
  );
}

export async function fetchPriceHistory(params?: { sku?: string; limit?: number; offset?: number }) {
  const qs = new URLSearchParams();
  if (params?.sku) qs.set("sku", params.sku);
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.offset) qs.set("offset", String(params.offset));
  const url = qs.toString() ? `/api/prijzen/historie?${qs}` : `/api/prijzen/historie`;
  return api<{ entries: PriceEngineHistoryEntry[] }>(url);
}

// Selection-based push (UI bouwt push-batches uit price_advice ipv CSV).
// Server moet route /api/prijzen/batches/from-selection ondersteunen (optional;
// fallback: client gebruikt /api/prijzen/batches/upload met legacy CSV-flow).
export interface PricePushSelection {
  mode: "all_drift" | "category" | "marketplace" | "sku";
  category?: string;
  marketplace?: PriceEngineMarketplace;
  skus?: string[];
  min_delta_pct?: number;
  notes?: string;
}

export async function buildPushBatchFromSelection(sel: PricePushSelection) {
  return api<PricePushUploadResult>(`/api/prijzen/batches/from-selection`, {
    method: "POST",
    body: sel,
  });
}

// ============================================================================
// Assortment master (Fase 3 — Merkatura curated assortment)
// ============================================================================

export type AssortmentLifecycle =
  | "active"
  | "phasing_out"
  | "discontinued"
  | "inactive"
  | "prospect";

export interface AssortmentBrandRef {
  id: number;
  name: string | null;
}

export interface AssortmentCategoryRef {
  id: number;
  name: string | null;
}

export interface AssortmentItem {
  id: number;
  sku: string;
  ean: string | null;
  siu_product_id: number | null;
  title: string | null;
  brand: AssortmentBrandRef | null;
  category: AssortmentCategoryRef | null;
  supplier: string | null;
  tags: string[];
  lifecycle_status: AssortmentLifecycle;
  stock_opslag: number | null;
  stock_online: number | null;
  source: string;
  added_at: string;
  updated_at: string;
}

export interface AssortmentSiuOnly {
  siu_product_id: number;
  sku: string;
  ean: string | null;
  title: string | null;
  stock_opslag: number | null;
  stock_online: number | null;
}

export interface AssortmentListResponse {
  items: AssortmentItem[];
  total: number;
  limit: number;
  offset: number;
  siu_only: AssortmentSiuOnly[];
}

export interface AssortmentBrand {
  id: number;
  name: string;
  slug: string;
  item_count: number;
}

export interface AssortmentCategory {
  id: number;
  parent_id: number | null;
  name: string;
  slug: string;
  item_count: number;
}

export interface AssortmentChangeLogEntry {
  id: number;
  field: string;
  old_value: string | null;
  new_value: string | null;
  changed_at: string;
  changed_by: string | null;
  reason: string | null;
}

export interface AssortmentListParams {
  lifecycle?: AssortmentLifecycle | null;
  brand_id?: number | null;
  category_id?: number | null;
  search?: string;
  limit?: number;
  offset?: number;
  include_siu_only?: boolean;
}

export async function listAssortmentItems(params: AssortmentListParams = {}) {
  const qs = new URLSearchParams();
  if (params.lifecycle) qs.set("lifecycle", params.lifecycle);
  if (params.brand_id) qs.set("brand_id", String(params.brand_id));
  if (params.category_id) qs.set("category_id", String(params.category_id));
  if (params.search) qs.set("search", params.search);
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.offset) qs.set("offset", String(params.offset));
  if (params.include_siu_only) qs.set("include_siu_only", "true");
  const s = qs.toString();
  return api<AssortmentListResponse>(`/api/assortment/list${s ? "?" + s : ""}`);
}

export async function siuSearchProducts(q: string) {
  const qs = new URLSearchParams({ q });
  return api<{ products: AssortmentSiuOnly[] }>(`/api/assortment/siu-search?${qs}`);
}

export interface AssortmentCreateBody {
  sku: string;
  ean?: string | null;
  siu_product_id?: number | null;
  brand_id?: number | null;
  category_id?: number | null;
  supplier?: string | null;
  tags?: string[];
  lifecycle_status?: AssortmentLifecycle;
}

export async function createAssortmentItem(body: AssortmentCreateBody) {
  return api<{ id: number }>("/api/assortment/items", { method: "POST", body });
}

export interface AssortmentPatchBody {
  ean?: string | null;
  brand_id?: number | null;
  category_id?: number | null;
  supplier?: string | null;
  tags?: string[];
  lifecycle_status?: AssortmentLifecycle;
  reason?: string;
}

export async function updateAssortmentItem(id: number, patch: AssortmentPatchBody) {
  return api<{ ok: boolean; changed: number }>(`/api/assortment/items/${id}`, {
    method: "PATCH",
    body: patch,
  });
}

export async function deleteAssortmentItem(id: number, reason?: string) {
  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : "";
  return api<{ ok: boolean; already_discontinued?: boolean }>(
    `/api/assortment/items/${id}${qs}`,
    { method: "DELETE" },
  );
}

export async function listAssortmentBrands() {
  return api<{ brands: AssortmentBrand[] }>("/api/assortment/brands");
}

export async function createAssortmentBrand(name: string) {
  return api<{ id: number; name: string; slug: string }>("/api/assortment/brands", {
    method: "POST",
    body: { name },
  });
}

export async function listAssortmentCategories(parent_id?: number | null) {
  const qs = new URLSearchParams();
  if (parent_id === null) qs.set("parent_id", "null");
  else if (parent_id !== undefined) qs.set("parent_id", String(parent_id));
  const s = qs.toString();
  return api<{ categories: AssortmentCategory[] }>(
    `/api/assortment/categories${s ? "?" + s : ""}`,
  );
}

export async function createAssortmentCategory(name: string, parent_id?: number | null) {
  return api<{ id: number; name: string; slug: string; parent_id: number | null }>(
    "/api/assortment/categories",
    { method: "POST", body: { name, parent_id: parent_id ?? null } },
  );
}

export async function getAssortmentChangeLog(item_id: number) {
  return api<{ log: AssortmentChangeLogEntry[] }>(
    `/api/assortment/change-log/${item_id}`,
  );
}

// =========================================================================
// Marketplace-drift (Fase D) — live vs expected mismatches per marketplace
// =========================================================================

export type DriftMarketplace = "bol" | "amazon" | "mediamarkt" | "kaufland";

export type DriftSource =
  | "prestatie_force"
  | "force_mlb"
  | "pending"
  | "warehouse_default"
  | "external_change";

export type DriftAction =
  | "re_pushed"
  | "accepted_external"
  | "manual_resolved"
  | "skipped";

export interface MarketplaceDriftEvent {
  id: number;
  marketplace: DriftMarketplace;
  account_id: number;
  sku: string;
  ean: string | null;
  live_code: string | null;
  expected_code: string | null;
  source: DriftSource;
  detected_at: string;
  resolved_at: string | null;
  action_taken: DriftAction | null;
  push_process_id: string | null;
  notes: string | null;
}

export interface MarketplaceDriftSummary {
  per_marketplace: Record<DriftMarketplace, number>;
  per_account: Array<{ marketplace: DriftMarketplace; account_id: number; open: number }>;
  total_open: number;
  generated_at: string;
}

/** Lijst drift-events met optionele filters. Default open=true (alleen
 *  openstaande events). limit max 500 server-side. */
export async function fetchMarketplaceDriftEvents(filter: {
  marketplace?: DriftMarketplace;
  account_id?: number;
  sku?: string;
  open?: boolean;
  limit?: number;
} = {}) {
  const qs = new URLSearchParams();
  if (filter.marketplace) qs.set("marketplace", filter.marketplace);
  if (filter.account_id !== undefined) qs.set("account_id", String(filter.account_id));
  if (filter.sku) qs.set("sku", filter.sku);
  if (filter.open !== undefined) qs.set("open", String(filter.open));
  if (filter.limit !== undefined) qs.set("limit", String(filter.limit));
  const s = qs.toString();
  return api<{ rows: MarketplaceDriftEvent[] }>(
    `/api/marketplace-drift/events${s ? "?" + s : ""}`,
  );
}

/** Open-counts per marketplace + per (marketplace, account). */
export async function fetchMarketplaceDriftSummary() {
  return api<MarketplaceDriftSummary>("/api/marketplace-drift/summary");
}

/** Resolve een drift-event handmatig. action_taken default "manual_resolved";
 *  voor "ik accepteer de live-waarde" → "accepted_external". */
export async function resolveMarketplaceDriftEvent(
  id: number,
  input: { action_taken?: DriftAction; notes?: string } = {},
) {
  return api<{ ok: boolean; id: number; action_taken: DriftAction }>(
    `/api/marketplace-drift/events/${id}/resolve`,
    { method: "POST", body: input },
  );
}

/** Trigger adapter.triggerRePush voor dit event + sluit het af met
 *  action_taken='re_pushed'. Returnt push_process_id zoals adapter die teruggaf. */
export async function forceRePushMarketplaceDriftEvent(id: number) {
  return api<{
    ok: boolean;
    id: number;
    marketplace: DriftMarketplace;
    account_id: number;
    sku: string;
    push_process_id: string | null;
  }>(`/api/marketplace-drift/events/${id}/force-repush`, { method: "POST", body: {} });
}

// ===========================================================================
// WMS v2 (TC27 scan module) — API helpers
// ===========================================================================

import { getActiveSessionToken, getActiveUser } from "../modules/wms-mobile/session";
import { runMutation } from "../modules/wms-mobile/offline-queue";

/** WMS v2 flow-request met X-Active-Session header (FIX-1: JWT ipv unsafe id).
 *  Backend gebruikt JWT-payload user_id voor mutation-attribution. */
async function apiWmsV2<T>(path: string, init: Omit<RequestInit, "body"> & { body?: object } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const sessionToken = getActiveSessionToken();
  if (sessionToken) headers.set("X-Active-Session", sessionToken);
  return api<T>(path, { ...init, headers });
}

/** Mutation via offline-queue: bij network-fail wordt de call in IndexedDB gequeued
 *  en later automatisch gestuurd. Return-shape blijft ~consistent (queued=true bij offline). */
async function apiWmsMutation<T>(
  kind: "putaway" | "pick" | "move" | "inbound" | "receive" | "other",
  method: string,
  path: string,
  body: unknown,
  serialNumber?: string | null,
): Promise<T & { queued?: boolean }> {
  const activeUser = getActiveUser();
  const r = await runMutation<T>({
    kind, url: path, method, body,
    activeUserId: activeUser?.id ?? null,
    serialNumber: serialNumber ?? null,
  });
  if (!r.ok) throw new ApiError(500, { error: r.error }, r.error ?? "Onbekende fout");
  if (r.queued) {
    // Server heeft het (nog) niet gezien; return synthetic body met queued-flag.
    return { queued: true } as T & { queued: boolean };
  }
  return (r.response ?? {}) as T & { queued?: boolean };
}

// --- Locations ---

export interface WmsLocation {
  code: string;
  type: "bulk" | "pick" | "stelling" | "pool" | "receiving";
  rij: string;
  status: "vrij" | "bezet" | "geblokkeerd";
  capaciteit_pallets: number | null;
  volgorde: number;
  is_receiving: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export async function listWmsLocations(opts: { rij?: string; status?: string } = {}) {
  const qs = new URLSearchParams();
  if (opts.rij) qs.set("rij", opts.rij);
  if (opts.status) qs.set("status", opts.status);
  const suf = qs.toString() ? `?${qs}` : "";
  return apiWmsV2<{ rows: WmsLocation[] }>(`/api/wms/v2/locations${suf}`);
}

export async function createWmsLocation(body: {
  code: string; type: string; rij: string; status?: string;
  capaciteit_pallets?: number | null; volgorde?: number; notes?: string | null;
}) {
  return apiWmsV2<{ ok: boolean; code: string }>(`/api/wms/v2/locations`, { method: "POST", body });
}

export async function bulkSeedWmsLocations(locations: Array<{
  code: string; type: string; rij: string; volgorde?: number; capaciteit_pallets?: number | null;
}>) {
  return apiWmsV2<{ ok: boolean; inserted: number; skipped: number }>(
    `/api/wms/v2/locations/bulk-seed`, { method: "POST", body: { locations } }
  );
}

// --- Stock ---

export interface WmsBayStock {
  bay_code: string;
  aantal: number;
  putaway_at: string | null;
  laatste_mutatie_ts: string;
  rij: string;
  volgorde: number;
  is_receiving: boolean;
}

export async function getWmsStockForSku(sku: string) {
  return apiWmsV2<{ sku: string; total: number; bays: WmsBayStock[] }>(
    `/api/wms/v2/stock/sku/${encodeURIComponent(sku)}`
  );
}

export async function getWmsStockForBay(bayCode: string) {
  return apiWmsV2<{
    bay_code: string; total_units: number;
    items: Array<{ sku: string; aantal: number; putaway_at: string | null; laatste_mutatie_ts: string; product_name: string | null; ean: string | null; is_mda: boolean | null }>;
  }>(`/api/wms/v2/stock/bay/${encodeURIComponent(bayCode)}`);
}

// --- Flows ---

export async function wmsInbound(body: { inbound_bay: string; sku: string; qty: number; pakbon_ref?: string | null }) {
  return apiWmsMutation<{ ok: boolean; mutation_id: number }>("inbound", "POST", `/api/wms/v2/flow/inbound`, body);
}

export async function wmsPutaway(body: {
  inbound_bay: string; target_bay: string; sku: string; qty: number; pakbon_ref?: string | null;
}) {
  return apiWmsMutation<{ ok: boolean; correlation_id: string; move_out_id: number; move_in_id: number; remaining_on_inbound: number }>(
    "putaway", "POST", `/api/wms/v2/flow/putaway`, body,
  );
}

export async function wmsMove(body: { from_bay: string; to_bay: string; sku: string; qty: number; notes?: string | null }) {
  return apiWmsMutation<{ ok: boolean; correlation_id: string; move_out_id: number; move_in_id: number }>(
    "move", "POST", `/api/wms/v2/flow/move`, body,
  );
}

export async function wmsPick(body: { bay_code: string; sku: string; qty: number; serial_number?: string | null; pakbon_ref?: string | null; no_serial?: boolean }) {
  return apiWmsMutation<{ ok: boolean; mutation_id: number; remaining: number }>(
    "pick", "POST", `/api/wms/v2/flow/pick`, body, body.serial_number,
  );
}

/**
 * Camera-OCR: stuur een base64-encoded foto van een product-label naar de
 * server. Response bevat het geëxtraheerde serienummer (of null).
 * Kosten ~$0.01 per call via Claude Vision.
 */
export async function wmsOcrSerial(body: { image_base64: string; mime_type: string }) {
  return api<{ ok: boolean; serial: string | null; duration_ms: number; raw: string; error?: string }>(
    `/api/wms/v2/serial/ocr`, { method: "POST", body },
  );
}

// --- Pincode ---

export async function setWmsPincode(pincode: string) {
  return api<{ ok: boolean }>(`/api/wms/v2/pincode/set`, { method: "POST", body: { pincode } });
}

export async function setWmsPincodeForUser(user_id: number, pincode: string) {
  return api<{ ok: boolean; user_id: number }>(`/api/wms/v2/pincode/set-for-user`, { method: "POST", body: { user_id, pincode } });
}

export async function verifyWmsPincode(pincode: string, user_id: number) {
  return api<{ ok: boolean; user_id: number; name: string; role: string; active_session_token: string; expires_at: string }>(
    `/api/wms/v2/pincode/verify`, { method: "POST", body: { pincode, user_id } }
  );
}

export async function listWmsPincodeUsers() {
  return api<{ users: Array<{ id: number; name: string; role: string }> }>(`/api/wms/v2/pincode/users`);
}

// --- Serial resolver ---

export interface SerialResolution {
  sku: string;
  serial_required: boolean;
  source: "override" | "categorie" | "is_mda" | "default";
  detail: string | null;
}

export async function resolveWmsSerials(skus: string[]) {
  return apiWmsV2<{ results: SerialResolution[] }>(`/api/wms/v2/serial/resolve`, { method: "POST", body: { skus } });
}

// --- Lookup EAN → SKU (reuse existing v1 endpoint) ---

export async function wmsLookupEan(ean: string) {
  return apiWmsV2<{
    ean: string; sku: string | null; product_name: string | null;
    is_known: boolean; is_mda: boolean; current_stock: number;
    source?: string | null; qty_per_carton?: number | null;
  }>(`/api/wms/scan/lookup-ean`, { method: "POST", body: { ean } });
}

// --- Pakbon (WMS v2) ---

export interface WmsPakbonRow {
  id: number; delivery_ref: string; container_ref: string | null;
  status: string; inbound_bay_code: string | null;
  uploaded_at: string; started_at: string | null;
  total_expected: number; total_received: number; lines_count: number;
}

export async function listOpenPakbonnen() {
  return apiWmsV2<{ rows: WmsPakbonRow[] }>(`/api/wms/v2/pakbon/open`);
}

export interface WmsPakbonDetail {
  delivery_id: number; delivery_ref: string; inbound_bay_code: string; status: string;
  lines: Array<{ id: number; regel_nr: number; sku: string; ean: string | null; expected_qty: number; received_qty: number }>;
}

export async function openPakbon(id: number) {
  return apiWmsV2<WmsPakbonDetail>(`/api/wms/v2/pakbon/${id}/open`, { method: "POST" });
}

export async function receiveScan(id: number, scan: string, qty = 1) {
  return apiWmsMutation<{
    ok: boolean; matched_line_id: number | null; received_qty_after: number; expected_qty: number;
    over_receipt: boolean; inbound_bay_code: string; mutation_id: number;
  }>("receive", "POST", `/api/wms/v2/pakbon/${id}/receive`, { scan, qty });
}

export async function unreceiveScan(id: number, sku: string, qty = 1) {
  return apiWmsV2<{
    ok: boolean; matched_line_id: number | null; received_qty_after: number; expected_qty: number;
    inbound_bay_code: string; mutation_id: number;
  }>(`/api/wms/v2/pakbon/${id}/unreceive`, { method: "POST", body: { sku, qty } });
}

export interface InbLeftoverItem {
  sku: string;
  qty_on_inb: number;
  home_bay: string | null;
  place_mode: "auto" | "requires_bay_scan";
  blk_home_bays: string[];
  product_name: string | null;
}
export async function fetchInbLeftover(id: number) {
  return apiWmsV2<{ leftover: InbLeftoverItem[]; total_qty: number }>(
    `/api/wms/v2/pakbon/${id}/inb-leftover`,
  );
}
export async function placeInbToHome(id: number, sku: string, qty: number, target_bay?: string | null) {
  return apiWmsV2<{ ok: boolean; from_bay: string; to_bay: string; qty: number }>(
    `/api/wms/v2/pakbon/${id}/place-to-home`,
    { method: "POST", body: { sku, qty, target_bay: target_bay ?? null } },
  );
}

// --- Manuele outbound (buiten picklijst om) ---
export interface ManualOutLookup {
  sku: string; ean: string | null; product_name: string | null;
  serial_required: boolean;
  bay_code: string | null; stock_on_bay: number; stock_total: number;
  /** 'nod' = voorraad komt uit nod_movements (NOD-scan-pakbon-flow), geen fysieke bay.
   *  Bij null of 'wms': normale fysieke bay-flow. */
  source?: "nod" | "wms";
  /** Alleen bij source='nod': delivery_ref van meest recente inbound (voor UI-context). */
  nod_location?: string | null;
}
export async function manualOutboundLookup(scan: string): Promise<ManualOutLookup> {
  return apiWmsV2<ManualOutLookup>(`/api/wms/v2/manual-outbound/lookup`, {
    method: "POST", body: { scan },
  });
}
export async function manualOutboundBook(body: {
  sku: string; qty: number; serials?: string[]; bay_code?: string; notes?: string;
}): Promise<{ ok: boolean; sku: string; qty: number; bay_code: string; serials_used: string[]; mutation_ids: number[] }> {
  return apiWmsV2(`/api/wms/v2/manual-outbound/book`, { method: "POST", body });
}

export interface PakbonLookupResult {
  sku: string;
  product_name: string | null;
  ean: string | null;
  line_id: number | null;
  expected_qty: number | null;
  received_qty: number | null;
  is_mda: boolean;
  qty_per_carton: number | null;
  place_mode: "auto" | "requires_bay_scan";
  auto_target_bay: string | null;
  auto_target_zone: {
    type: string | null;
    capaciteit_pallets: number | null;
    distinct_skus_present: number;
    capacity_status: "ok" | "bijna_vol" | "vol";
  } | null;
  blk_home_bays: string[];
}

export async function pakbonLookup(id: number, scan: string) {
  return apiWmsV2<PakbonLookupResult>(`/api/wms/v2/pakbon/${id}/lookup`, { method: "POST", body: { scan } });
}

export async function pakbonPlace(id: number, scan: string, qty: number, targetBay: string | null) {
  return apiWmsMutation<{
    ok: boolean; matched_line_id: number | null; received_qty_after: number; expected_qty: number;
    over_receipt: boolean; target_bay: string; place_mode: "auto" | "manual_scan"; mutation_id: number;
  }>("putaway", "POST", `/api/wms/v2/pakbon/${id}/place`, { scan, qty, target_bay: targetBay });
}

export interface WmsPakbonLine {
  id: number; line_number: number; sku: string; ean: string | null;
  expected_qty: number; received_qty: number;
  product_name: string | null; is_mda: boolean;
}

export async function fetchPakbonLines(id: number) {
  return apiWmsV2<{
    header: { id: number; delivery_ref: string; status: string; inbound_bay_code: string | null };
    lines: WmsPakbonLine[];
  }>(`/api/wms/v2/pakbon/${id}/lines`);
}

export async function closePakbonAction(id: number, force = false) {
  return apiWmsV2<{
    ok: boolean; delivery_id: number; status: string;
    inbound_bay_leeg: boolean; inbound_bay_leftover_qty: number;
    discrepanties: Array<{ sku: string; expected: number; received: number; diff: number }>;
  }>(`/api/wms/v2/pakbon/${id}/close`, { method: "POST", body: { force } });
}

// --- Picklijst ---

export interface WmsPicklijstRegel {
  id: number; regel_nr: number; sku: string; ean: string | null;
  aantal_gevraagd: number; aantal_gepickt: number;
  bay_hint: string | null; picked_by_user_id: number | null;
  picked_at: string | null; completed_at: string | null;
}

export async function createPicklijst(body: {
  source_ref?: string | null; notes?: string | null;
  regels: Array<{ sku: string; ean?: string | null; aantal_gevraagd: number; bay_hint?: string | null }>;
}) {
  return apiWmsV2<{ ok: boolean; picklijst_id: number }>(`/api/wms/v2/picklijst`, { method: "POST", body });
}

export async function listOpenPicklijsten() {
  return apiWmsV2<{ rows: Array<{ id: number; source_ref: string | null; status: string; created_at: string; regels_open: number; regels_total: number }> }>(
    `/api/wms/v2/picklijst/open`
  );
}

export async function getPicklijst(id: number) {
  return apiWmsV2<{
    picklijst: { id: number; source_ref: string | null; status: string; created_at: string; notes: string | null };
    regels: WmsPicklijstRegel[];
  }>(`/api/wms/v2/picklijst/${id}`);
}

export async function claimPickRegel(id: number) {
  return apiWmsV2<{ ok: boolean; claimed_by: number }>(`/api/wms/v2/picklijst/regel/${id}/claim`, { method: "POST" });
}

export async function incrementPickRegel(id: number, qty = 1) {
  return apiWmsV2<{ ok: boolean; aantal_gepickt: number; aantal_gevraagd: number }>(
    `/api/wms/v2/picklijst/regel/${id}/increment`, { method: "POST", body: { qty } }
  );
}

export interface ScanLabelRegel {
  id: number; picklijst_id: number; regel_nr: number; sku: string; ean: string | null;
  aantal_gevraagd: number; aantal_gepickt: number; bay_hint: string | null;
  is_mda: boolean; completed_at: string | null; label_scanned_at: string | null;
}
export interface ScanLabelResult {
  order: { order_id: number; store_id: string | null; sales_channel: string | null } | null;
  carrier_track: string;
  regels: ScanLabelRegel[];
}
export async function scanPicklijstLabel(carrierTrack: string) {
  return apiWmsV2<ScanLabelResult>(`/api/wms/v2/picklijst/scan-label`, {
    method: "POST", body: { carrier_track: carrierTrack },
  });
}
export async function confirmPicklijstLabel(regelId: number, carrierTrack: string, serialNumber: string | null = null) {
  return apiWmsV2<{ ok: boolean; label_scanned_at: string }>(
    `/api/wms/v2/picklijst/regel/${regelId}/confirm-label`,
    { method: "POST", body: { carrier_track: carrierTrack, serial_number: serialNumber } },
  );
}

// --- Scan-Lab: READ-ONLY scan-lookup (geen mutations, geen picklijst-updates) ---
export interface ScanLookupCarrierDetect {
  carrier: string;
  confidence: "high" | "medium" | "low";
  prefix: string;
  expected_lengths?: number[];
}
export interface ScanLookupPicklijstResult {
  regel_id: number; picklijst_id: number; sku: string; ean: string | null;
  aantal_gevraagd: number; aantal_gepickt: number; bay_hint: string | null;
  order_id: number | null; order_no: string | null; sales_channel: string | null;
  picked_at: string | null; completed_at: string | null;
}
export interface ScanLookupLabelScanResult {
  scan_id: number; ts: string; carrier_track: string;
  order_id: number | null; order_no: string | null; sales_channel: string | null;
  regel_id: number | null; outcome: string; notes: string | null;
}
export interface ScanLookupOrderRawResult {
  order_id: number; order_no: string | null; sales_channel: string | null;
  order_date: string; status: string;
  parcel_track: string | null; transporter_text: string | null; carrier: string | null;
  is_exact_parcel_match: boolean;
}
export interface ScanLookupOrderStoreIdResult {
  order_id: number; order_no: string | null; sales_channel: string | null;
  order_date: string; status: string;
}
export interface ScanLookupCandidateResult {
  candidate: string;
  gs1_stripped: boolean;
  strategies: {
    picklijst_regel: { found: boolean; count: number; results: ScanLookupPicklijstResult[] };
    label_scans_history: { found: boolean; count: number; results: ScanLookupLabelScanResult[] };
    orders_raw: { found: boolean; count: number; results: ScanLookupOrderRawResult[] };
    orders_store_id: { found: boolean; count: number; results: ScanLookupOrderStoreIdResult[] };
  };
}
export interface ScanLookupResponse {
  raw_scan: string;
  trimmed: string;
  length: number;
  gs1_stripped_variant: string | null;
  mktl_order_no: string | null;
  dpd_consignment_extracted: string | null;
  carrier_detect_primary: ScanLookupCarrierDetect;
  carrier_detect_stripped: ScanLookupCarrierDetect | null;
  candidates_tried: string[];
  results: ScanLookupCandidateResult[];
  recommended_order_no: string | null;
}
export async function scanLookup(raw: string): Promise<ScanLookupResponse> {
  return apiWmsV2<ScanLookupResponse>(`/api/wms/v2/scan-lookup`, {
    method: "POST", body: { raw },
  });
}

// --- WMS recent activity (Home 'Ga verder waar je was') ---
export interface WmsRecentPakbon {
  id: number; delivery_ref: string; status: string; started_at: string | null;
  lines_count: number; total_received: number; total_expected: number;
}
export interface WmsRecentPicklijst {
  id: number; source_ref: string | null; status: string; created_at: string;
  regels_open: number; regels_total: number;
}
export interface WmsRecentLabelScan {
  carrier_track: string; ts: string; outcome: string;
  order_no: string | null; sales_channel: string | null;
}
export interface WmsRecentActivity {
  pakbonnen: WmsRecentPakbon[];
  picklijsten: WmsRecentPicklijst[];
  label_scans: WmsRecentLabelScan[];
}
export async function fetchWmsRecentActivity(): Promise<WmsRecentActivity> {
  return apiWmsV2<WmsRecentActivity>(`/api/wms/v2/recent-activity`);
}

// --- wms_afwijkingen (canonical override-logger) ---
export type DeviationEventType =
  | "putaway_bay_override"
  | "pakbon_force_close"
  | "pick_skip"
  | "label_no_serial"
  | "move_manual_bay"
  | "stelling_vol_override"
  | "sku_mismatch_accept"
  | "counting_correction"
  | "manual_taak_completed";

export interface DeviationLog {
  event_type: DeviationEventType;
  sku?: string | null;
  target_bay?: string | null;
  actual_bay?: string | null;
  mutation_id?: number | null;
  correlation_id?: string | null;
  reason_code?: string | null;
  reason?: string | null;
  before_state?: unknown;
  after_state?: unknown;
  notes?: string | null;
}
export async function logWmsDeviation(body: DeviationLog) {
  return apiWmsV2<{ ok: boolean; id: number }>(
    `/api/wms/v2/afwijkingen`,
    { method: "POST", body },
  );
}

export interface BayCapacity {
  code: string; type: string | null; bay_status: string | null;
  capaciteit_pallets: number | null;
  distinct_skus_present: number; totaal_stuks_present: number;
  capacity_status: "ok" | "bijna_vol" | "vol";
}
export async function fetchBayCapacity(code: string) {
  return apiWmsV2<BayCapacity>(`/api/wms/v2/locations/${encodeURIComponent(code)}/capacity`);
}

export interface SerialCategoryRow {
  slug: string;
  serial_required: boolean;
  sku_count: number;
  overrides_true: number;
  overrides_false: number;
}
export async function fetchSerialConfig() {
  return apiWmsV2<{ categories: SerialCategoryRow[] }>(`/api/wms/v2/serial-config`);
}
export async function updateSerialConfig(categories: string[]) {
  return apiWmsV2<{ ok: boolean; serial_required_categories: string[] }>(
    `/api/wms/v2/serial-config`,
    { method: "PUT", body: { categories } }
  );
}

export async function bulkPickSku(picklijstId: number, sku: string, qty: number, regelIds?: number[]) {
  return apiWmsV2<{
    ok: boolean; sku: string; requested_qty: number; distributed_qty: number; remaining_qty: number;
    picked: Array<{ regel_id: number; picked_now: number; completed: boolean }>;
  }>(`/api/wms/v2/picklijst/${picklijstId}/bulk-pick-sku`, {
    method: "POST", body: { sku, qty, regel_ids: regelIds },
  });
}

// --- Manual-add COS-picklijst + SIU-corrections ---
export interface CosManualSearchItem {
  order_item_id: number; sku: string | null; ean: string | null;
  qty_ordered: number; current_warehouse: string | null;
  already_on_open_list_regel_id: number | null;
}
export interface CosManualSearchOrder {
  order_id: number; store_id: string | null; sales_channel: string | null;
  order_date: string; carrier_track: string | null;
  items: CosManualSearchItem[];
}
export async function searchOrdersForManualAdd(q: string) {
  const p = new URLSearchParams({ q });
  return apiWmsV2<{ orders: CosManualSearchOrder[] }>(`/api/wms/v2/picklijst/manual-add/search?${p}`);
}
export async function manualAddToCosPicklijst(body: {
  order_id: number;
  items: Array<{ order_item_id: number; sku: string; ean: string | null;
    qty: number; carrier_track: string | null; original_warehouse: string; reason: string }>;
}) {
  return apiWmsV2<{
    ok: boolean; picklijst_id: number;
    added: Array<{ order_item_id: number; regel_id: number }>;
    skipped: Array<{ order_item_id: number; reason: string; existing_regel_id: number | null }>;
  }>(`/api/wms/v2/picklijst/manual-add`, { method: "POST", body });
}

export interface SiuCorrectionRow {
  id: number; source_type: string; status: string; sku: string; qty: number;
  from_warehouse: string; to_warehouse: string; reason: string | null;
  created_at: string; done_at: string | null; cancelled_at: string | null;
  order_id: number | null; order_no: string | null;
  created_by_name: string | null; done_by_name: string | null; cancelled_by_name: string | null;
  added_by_name: string | null; age_hours: number; is_overdue: boolean;
}
export async function listSiuCorrections(status: "open" | "done" | "cancelled" | "all" = "open") {
  const p = new URLSearchParams({ status });
  return apiWmsV2<{
    rows: SiuCorrectionRow[];
    summary: { open: number; overdue_24h: number; done_last_7d: number };
  }>(`/api/wms/v2/siu-corrections?${p}`);
}
export async function markSiuCorrectionDone(id: number, notes: string | null = null) {
  return apiWmsV2<{ ok: boolean }>(`/api/wms/v2/siu-corrections/${id}/done`, {
    method: "POST", body: { notes },
  });
}
export async function cancelSiuCorrection(id: number, reason: string) {
  return apiWmsV2<{ ok: boolean }>(`/api/wms/v2/siu-corrections/${id}/cancel`, {
    method: "POST", body: { reason },
  });
}

// --- WMS v2 NOD/COS overview ---
export interface WmsNodCosRow {
  sku: string;
  ean: string | null;
  product_name: string | null;
  is_mda: boolean;
  nod_qty: number;
  cos_expected: number;
  cos_physical: number;
  cos_in_receipt: number;
  in_flight: number;
  oldest_drift_ts: string | null;
  drift_hours: number | null;
}

export async function fetchWmsNodCosOverview(opts: { q?: string; hideMatched?: boolean } = {}) {
  const qs = new URLSearchParams();
  if (opts.q) qs.set("q", opts.q);
  if (opts.hideMatched) qs.set("hide_matched", "true");
  const suf = qs.toString() ? `?${qs}` : "";
  return apiWmsV2<{ rows: WmsNodCosRow[] }>(`/api/wms/v2/stock/nod-cos-overview${suf}`);
}

// --- WMS v2 Counting (handmatige tellingen) ---
export type WmsCountingSoort = "standaard" | "refurbished";
export type WmsGradatie = "A" | "B" | "C" | "D";
export interface WmsCountingRow {
  id: number; name: string; status: string; soort: WmsCountingSoort; created_at: string; closed_at: string | null;
  created_by_name: string | null; items: number; total_count: number;
}
export interface WmsCountingItem {
  ean: string; sku: string | null; product_name: string | null;
  /** '' bij een standaard telling; A/B/C/D bij refurbished. */
  gradatie: string;
  count: number; first_scan_at: string; last_scan_at: string;
}

export async function createCounting(name: string, notes?: string | null, soort: WmsCountingSoort = "standaard") {
  return apiWmsV2<{ ok: boolean; counting_id: number }>(`/api/wms/v2/counting`, { method: "POST", body: { name, notes: notes ?? null, soort } });
}
export async function listCountings() {
  return apiWmsV2<{ rows: WmsCountingRow[] }>(`/api/wms/v2/counting`);
}
export async function getCounting(id: number) {
  return apiWmsV2<{
    counting: { id: number; name: string; status: string; soort: WmsCountingSoort; created_at: string; closed_at: string | null; notes: string | null };
    items: WmsCountingItem[];
  }>(`/api/wms/v2/counting/${id}`);
}
export async function scanCounting(id: number, ean: string, qty = 1, gradatie?: WmsGradatie) {
  return apiWmsV2<{ ok: boolean; ean: string; sku: string | null; gradatie: string; count: number }>(
    `/api/wms/v2/counting/${id}/scan`, { method: "POST", body: { ean, qty, ...(gradatie ? { gradatie } : {}) } }
  );
}
export async function closeCounting(id: number) {
  return apiWmsV2<{ ok: boolean }>(`/api/wms/v2/counting/${id}/close`, { method: "POST" });
}
export async function updateCounting(id: number, patch: { name?: string; notes?: string | null }) {
  return apiWmsV2<{ ok: boolean; changed: number }>(`/api/wms/v2/counting/${id}`, { method: "PATCH", body: patch });
}
export async function deleteCounting(id: number) {
  return apiWmsV2<{ ok: boolean }>(`/api/wms/v2/counting/${id}`, { method: "DELETE" });
}
export function countingCsvUrl(id: number): string {
  return `/api/wms/v2/counting/${id}/csv`;
}

// --- WMS nulmeting / herinrichting ---
// Voorraad van de oude geblokkeerde locatiecodes naar de nieuwe plattegrond.
// Boekt als move, zodat historie en voorraadwaarde intact blijven; zie
// server/src/routes/wmsNulmeting.ts voor de onderbouwing.
export interface NulmetingStatus {
  generated_at: string;
  te_verplaatsen: { bays: number; skus: number; stuks: number };
  al_op_nieuwe_indeling: { bays: number; skus: number; stuks: number };
  voortgang_pct: number;
  nulmeting_boekingen: number;
  laatste_boeking: string | null;
}
export interface NulmetingBay {
  bay_code: string; type: string; skus: number; stuks: number;
}
export interface NulmetingBron {
  bay_code: string; aantal: number; type: string; geblokkeerd: boolean;
}
export interface NulmetingLookup {
  sku: string;
  ean: string | null;
  product_naam: string | null;
  bronnen: NulmetingBron[];
  te_verplaatsen: number;
  suggestie_doel: string | null;
}
export interface NulmetingVerplaatsResultaat {
  ok: boolean;
  correlation_id: string;
  sku: string;
  van_bay: string;
  naar_bay: string;
  verplaatst: number;
  rest_op_bron: number;
  afwijking_gelogd: boolean;
}

export async function nulmetingStatus() {
  return apiWmsV2<NulmetingStatus>(`/api/wms/nulmeting/status`);
}
export async function nulmetingBays(limiet = 50) {
  return apiWmsV2<{ bays: NulmetingBay[] }>(`/api/wms/nulmeting/bays?limiet=${limiet}`);
}
export async function nulmetingLookup(scan: string) {
  return apiWmsV2<NulmetingLookup>(`/api/wms/nulmeting/lookup`, { method: "POST", body: { scan } });
}
export interface NulmetingBayRegel {
  sku: string; aantal: number; product_naam: string | null; ean: string | null;
}
export interface NulmetingBayInhoud {
  bay: { code: string; type: string; status: string; capaciteit_pallets: number | null };
  regels: NulmetingBayRegel[];
  stuks_totaal: number;
}
export interface NulmetingTelResultaat {
  ok: boolean;
  bay_code: string;
  sku: string;
  product_naam: string | null;
  geteld: number;
  was_geboekt_op_vak: number;
  verplaatst_van_oud: Array<{ van_bay: string; aantal: number }>;
  /** Positief = gevonden voorraad, negatief = minder geteld dan geboekt. */
  correctie: number;
  nog_op_oude_codes: number;
}
export interface NulmetingRestant {
  regels: Array<{ bay_code: string; sku: string; aantal: number; product_naam: string | null }>;
  stuks_totaal: number;
}

export async function nulmetingBayInhoud(code: string) {
  return apiWmsV2<NulmetingBayInhoud>(`/api/wms/nulmeting/bay/${encodeURIComponent(code)}`);
}
/** Locatie-eerst tellen: het aantal is het TOTAAL van deze SKU op dit vak. */
export async function nulmetingTel(body: {
  bay_code: string; scan: string; aantal: number; notes?: string;
}) {
  return apiWmsV2<NulmetingTelResultaat>(`/api/wms/nulmeting/tel`, { method: "POST", body });
}
export async function nulmetingRestant() {
  return apiWmsV2<NulmetingRestant>(`/api/wms/nulmeting/restant`);
}
export async function nulmetingAfboeken(body: {
  bay_code: string; sku: string; aantal: number; reden: string;
}) {
  return apiWmsV2<{ ok: boolean; bay_code: string; sku: string; afgeboekt: number }>(
    `/api/wms/nulmeting/afboeken`, { method: "POST", body });
}

export async function nulmetingVerplaats(body: {
  sku: string; van_bay: string; naar_bay: string; aantal: number;
  geteld?: number; notes?: string;
}) {
  return apiWmsV2<NulmetingVerplaatsResultaat>(`/api/wms/nulmeting/verplaats`, { method: "POST", body });
}

// --- WMS locatie-scan: koppel bay ↔ SKU als home-location ---
export interface SetHomeLocationResult {
  ok: boolean;
  sku: string;
  product_name: string | null;
  bay_code: string;
  ean: string;
  was_new: boolean;
  total_home_locations: number;
}
export async function setSkuHomeLocation(bay_code: string, ean: string) {
  return apiWmsV2<SetHomeLocationResult>(
    `/api/wms/v2/location/set-home`,
    { method: "POST", body: { bay_code, ean } }
  );
}

// --- WMS movement serial edit (correctie op v1 bewegingen) ---
export async function updateMovementSerial(movementId: number, serial_number: string | null) {
  return api<{ ok: boolean; id: number; serial_number: string | null }>(
    `/api/wms/movements/${movementId}/serial`,
    { method: "PATCH", body: { serial_number } }
  );
}

// --- WMS v2 putaway suggestion ---
export interface PutawaySuggestion {
  bay_code: string;
  reason: string;
  aantal: number;
  type: string | null;
  priority: number;
}
export async function fetchPutawaySuggestions(opts: { sku?: string; ean?: string }) {
  const qs = new URLSearchParams();
  if (opts.sku) qs.set("sku", opts.sku);
  if (opts.ean) qs.set("ean", opts.ean);
  return apiWmsV2<{ sku: string | null; suggestions: PutawaySuggestion[] }>(
    `/api/wms/v2/putaway/suggest?${qs}`
  );
}

// --- Serial numbers per order (IMPL-4) ---
// De order-detail endpoint (/api/orders/:id) levert al `serial_numbers: string[]`
// per OrderItem. Deze helper is een compacte alternatieve view — bijv. voor CS-inbox
// die alleen SN's nodig heeft zonder financiële velden op te vragen.
export async function fetchOrderSerialNumbers(orderId: number) {
  const order = await fetchOrder(orderId);
  return order.items.map((it) => ({
    item_id: it.id,
    sku: it.product_sku,
    product_title: it.product_title,
    quantity_ordered: it.quantity_ordered,
    serial_numbers: it.serial_numbers,
  }));
}

// ---------------------------------------------------------------------------
// Bol Advertising — leesendpoints van de marketing-module (fase 1: monitoren).
//
// Let op: alle numerieke velden komen als STRING binnen. Postgres numeric wordt
// door Prisma een Decimal en bigint krijgt via de toJSON-patch in server.ts een
// string-representatie. Gebruik Number() in de component, net als bij
// MarketingRow.amount_eur hierboven.
// ---------------------------------------------------------------------------

export interface BolAdsTotals {
  impressions: string; clicks: string; cost: string; sales: string;
  conversions: string; acos: string | null; roas: string | null;
  ctr: string | null; cpc: string | null; conversion_rate: string | null;
  spending_campaigns: string;
}
export interface BolAdsDailyPoint {
  report_date: string; cost: string; sales: string;
  clicks: string; impressions: string; acos: string | null;
}
export interface BolAdsPortfolioRow { state: string; n: number; daily_budget: string }
export interface BolAdsAlert {
  campaign_id: string; name: string; state: string;
  daily_budget_eur: string | null; constraints: string[]; since: string | null;
}
export interface BolAdsSummary {
  period: { from: string; to: string };
  totals: BolAdsTotals;
  daily: BolAdsDailyPoint[];
  portfolio: BolAdsPortfolioRow[];
  alerts: BolAdsAlert[];
}

export interface BolAdsCampaignRow {
  campaign_id: string; name: string; state: string; campaign_type: string | null;
  daily_budget_eur: string | null; acos_target_percentage: string | null;
  constraints: string[];
  impressions: string; clicks: string; cost: string; sales: string;
  conversions: string; acos: string | null; roas: string | null;
  budget_benutting: string | null;
}

export interface BolAdsProductRow {
  ean: string; sku: string | null; titel: string | null;
  impressions: string; clicks: string; cost: string; sales: string;
  conversions: string; acos: string | null; roas: string | null;
  paren: number | null; lift_search: string | null; lift_browse: string | null; slechter: number | null;
}

export interface BolAdsTermRow {
  search_term: string; campagnes: number;
  impressions: string; clicks: string; cost: string; sales: string;
  conversions: string; acos: string | null; roas: string | null;
  gem_winnend_bod: string | null; gem_impressie_aandeel: string | null;
  zoekvolume: number | null;
  /** Kostensplitsing naar herkomst: automatische targeting versus een keyword. */
  cost_auto: string; cost_keyword: string;
  heeft_leverend_keyword: boolean;
  heeft_keyword_ergens: boolean;
  /** Term kostte geld in een ad-group waar hij tegelijk is uitgesloten. */
  spend_ondanks_uitsluiting: boolean;
}

export interface BolAdsKeywordRow {
  keyword_id: string; keyword_text: string; match_type: string; state: string;
  bid_eur: string; constraints: string[];
  ad_group_id: string; ad_group: string | null;
  campagne: string; campagne_state: string; campaign_type: string | null;
  /** Totaal aantal rijen vóór de LIMIT, zodat de UI afkapping kan melden. */
  totaal_voor_limiet: number;
  impressions: string; clicks: string; cost: string; sales: string;
  acos: string | null; roas: string | null;
  gem_winnend_bod: string | null;
  /** Jouw bod gedeeld door het gemiddeld winnende bod. */
  bod_ratio: string | null;
}

function bolAdsQuery(p: { from?: string; to?: string; limit?: number } = {}) {
  const q = new URLSearchParams();
  if (p.from) q.set("from", p.from);
  if (p.to) q.set("to", p.to);
  if (p.limit) q.set("limit", String(p.limit));
  return q.toString();
}

export async function fetchBolAdsSummary(p: { from?: string; to?: string } = {}) {
  return api<BolAdsSummary>(`/api/marketing/bol-ads/summary?${bolAdsQuery(p)}`);
}
export async function fetchBolAdsCampaigns(p: { from?: string; to?: string; limit?: number } = {}) {
  return api<{ period: { from: string; to: string }; campaigns: BolAdsCampaignRow[] }>(
    `/api/marketing/bol-ads/campaigns?${bolAdsQuery(p)}`,
  );
}
export async function fetchBolAdsProducts(p: { from?: string; to?: string; limit?: number } = {}) {
  return api<{ period: { from: string; to: string }; products: BolAdsProductRow[] }>(
    `/api/marketing/bol-ads/products?${bolAdsQuery(p)}`,
  );
}
export async function fetchBolAdsKeywordList(p: { from?: string; to?: string; limit?: number } = {}) {
  return api<{ period: { from: string; to: string }; keywords: BolAdsKeywordRow[] }>(
    `/api/marketing/bol-ads/keywords?${bolAdsQuery(p)}`,
  );
}
export async function fetchBolAdsSearchTerms(p: { from?: string; to?: string; limit?: number } = {}) {
  return api<{ period: { from: string; to: string }; terms: BolAdsTermRow[] }>(
    `/api/marketing/bol-ads/search-terms?${bolAdsQuery(p)}`,
  );
}

// --- Bol Advertising fase 2: bijsturen -------------------------------------
// Preview en apply lopen door exact dezelfde serverlogica; het enige verschil
// is de dryRun-vlag. Wat de preview toont is dus letterlijk wat er verstuurd
// wordt, geen aparte berekening die uit de pas kan lopen.

export interface BolAdsPushConfig {
  push_enabled: boolean;
  allow_state_changes: boolean;
  /** Aparte schakelaar voor biedingen: honderden entiteiten tegen tientallen campagnes. */
  allow_bid_changes: boolean;
  max_delta_pct: number;
  max_daily_budget_eur: number;
  max_total_daily_budget_eur: number;
  max_changes_per_run: number;
  max_acos_target_pct: number;
  max_bid_eur: number;
  max_bid_ratio: number;
  max_bid_changes_per_run: number;
}

export type BolAdsChangeStatus =
  | "dry_run" | "needs_review" | "blocked" | "skipped" | "applied" | "failed" | "unknown";

export interface BolAdsChangeResult {
  campaignId: string;
  campaignName: string | null;
  field: "daily_budget_eur" | "state" | "acos_target_percentage";
  oldValue: string | null;
  newValue: string | null;
  deltaPct: number | null;
  status: BolAdsChangeStatus;
  blockReason: string | null;
  requestBody: Record<string, unknown> | null;
  violations: unknown | null;
}

export interface BolAdsChangeLogRow {
  id: string; campaign_id: string; campaign_name: string | null;
  field: string; old_value: string | null; new_value: string | null;
  delta_pct: string | null; status: BolAdsChangeStatus; block_reason: string | null;
  violations: unknown | null; triggered_by: string;
  created_at: string; applied_at: string | null; door: string | null;
  /**
   * Gevuld bij bied-wijzigingen, NULL bij campagne-wijzigingen — daar is
   * campaign_id de sleutel. Zonder deze drie zijn keyword-rijen onderling niet
   * te onderscheiden: dezelfde tekst komt binnen één campagne over meerdere
   * ad-groups voor.
   */
  entity_type: "KEYWORD" | "TARGET_PRODUCT" | "TARGET_CATEGORY" | null;
  entity_id: string | null;
  ad_group_id: string | null;
}

export interface BolAdsChangeInput {
  campaignId: string;
  dailyBudgetEur?: number;
  state?: "ENABLED" | "PAUSED";
  /** Alleen op AUTO-campagnes; de server weigert hem op MANUAL. */
  acosTargetPercentage?: number;
}

export async function previewBolAdsChange(body: BolAdsChangeInput) {
  return api<{ config: BolAdsPushConfig; results: BolAdsChangeResult[] }>(
    "/api/marketing/bol-ads/campaigns/preview", { method: "POST", body },
  );
}
export async function applyBolAdsChange(body: BolAdsChangeInput) {
  return api<{ config: BolAdsPushConfig; results: BolAdsChangeResult[] }>(
    "/api/marketing/bol-ads/campaigns/apply", { method: "POST", body },
  );
}
export async function fetchBolAdsPushConfig() {
  return api<BolAdsPushConfig>("/api/marketing/bol-ads/push-config");
}
export async function updateBolAdsPushConfig(body: {
  push_enabled?: boolean;
  allow_state_changes?: boolean;
  allow_bid_changes?: boolean;
}) {
  return api<BolAdsPushConfig>("/api/marketing/bol-ads/push-config", { method: "PUT", body });
}
export async function fetchBolAdsChanges(opts: { limit?: number; campaignId?: string } = {}) {
  const q = new URLSearchParams();
  if (opts.limit) q.set("limit", String(opts.limit));
  if (opts.campaignId) q.set("campaignId", opts.campaignId);
  return api<{ changes: BolAdsChangeLogRow[] }>(`/api/marketing/bol-ads/changes?${q}`);
}
